import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Edit, Trash2, Save, X, MapPin, Clock, Users, DollarSign, UserCheck, LogIn, LogOut, CheckCircle, Key, AlertCircle, Download, ExternalLink, Eye, RefreshCw, Image, Upload, FileUp, Settings, ChevronDown, ShieldAlert, Star, Activity, Lock, EyeOff } from 'lucide-react';

import SizeManager from './components/SizeManager';

// Configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Authentication Helper
const auth = {
  getToken: () => localStorage.getItem('authToken'),
  setToken: (token) => localStorage.setItem('authToken', token),
  removeToken: () => localStorage.removeItem('authToken'),
  isAuthenticated: () => !!localStorage.getItem('authToken'),
  getHeaders: () => {
    const token = auth.getToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }
};

// API Helper Functions with JWT expiration handling
const api = {
  // Callback for handling unauthorized (401) responses
  onUnauthorized: null,

  // Helper to check response and handle 401
  handleResponse: async (response) => {
    if (response.status === 401) {
      // JWT token expired or invalid
      if (api.onUnauthorized) {
        api.onUnauthorized();
      }
      const error = new Error('Session expired. Please log in again.');
      error.status = 401;
      throw error;
    }
    if (!response.ok) {
      // Try to get error details from response body
      const errorData = await response.json().catch(() => null);
      const error = new Error(errorData?.detail || `HTTP error! status: ${response.status}`);
      error.status = response.status;
      error.detail = errorData?.detail;
      throw error;
    }
    return response.json();
  },

  // Like get(), but also returns the X-Total-Count header. The users list is
  // server-paginated (#324), and a page is meaningless without knowing how many
  // rows the filter actually matched. The response body stays a bare array so
  // existing callers are untouched — an envelope would have broken every client
  // at once.
  getWithTotal: async (endpoint) => {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: auth.getHeaders()
    });
    const data = await api.handleResponse(response);
    const header = response.headers.get('X-Total-Count');
    return { data, total: header === null ? data.length : Number(header) };
  },

  get: async (endpoint) => {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: auth.getHeaders()
    });
    return api.handleResponse(response);
  },
  post: async (endpoint, data) => {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: auth.getHeaders(),
      body: JSON.stringify(data),
    });
    return api.handleResponse(response);
  },
  put: async (endpoint, data) => {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'PUT',
      headers: auth.getHeaders(),
      body: JSON.stringify(data),
    });
    return api.handleResponse(response);
  },
  delete: async (endpoint) => {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'DELETE',
      headers: auth.getHeaders()
    });
    return api.handleResponse(response);
  },
  login: async (email, password) => {
    const response = await fetch(`${API_BASE_URL}/users/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      const error = new Error(`HTTP error! status: ${response.status}`);
      error.status = response.status;
      error.detail = errorData?.detail || 'Login failed';
      throw error;
    }
    return response.json();
  }
};

// Utility Functions
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// Components
const LoadingSpinner = () => (
  <div className="loading-container">
    <div className="spinner"></div>
  </div>
);

const ErrorMessage = ({ message, onDismiss }) => (
  <div className="error-message">
    <div className="message-content">
      <p>{message}</p>
      {onDismiss && (
        <button onClick={onDismiss} className="close-btn">
          <X size={16} />
        </button>
      )}
    </div>
  </div>
);

const SuccessMessage = ({ message, onDismiss }) => (
  <div className="success-message">
    <div className="message-content">
      <p>{message}</p>
      {onDismiss && (
        <button onClick={onDismiss} className="close-btn">
          <X size={16} />
        </button>
      )}
    </div>
  </div>
);

// #246 — coordinate bias for every Google Places call, in one place.
//
// Sending no coordinates is not the neutral choice it looks like: Google biases
// an unbiased request by the IP of whoever made it, and we proxy these calls, so
// "no bias" silently means "biased to our backend in New Jersey". For a chain
// ("la Madeleine, Austin, TX") name + city does not identify the branch, and
// /suggest-address and /fetch-hours act on a single match without showing the
// alternatives — so the wrong branch's address or hours can land on the record.
//
// Spread into the request body. Yields {} when the location has no coordinates
// yet, which reproduces the previous behaviour rather than guessing.
const locationBias = (loc) => (
  loc && loc.latitude != null && loc.longitude != null
    ? { latitude: loc.latitude, longitude: loc.longitude }
    : {}
);

// Autocomplete Select Component
// #345: how an OPERATOR list labels a restaurant that isn't public. Screens that
// load restaurants with include_pending=true (the § 2.5 operator read) get
// private and pending rows too, and must never mix them in unmarked — the whole
// point of the operator view is knowing which are meant to be public. Same
// three states, same wording, as the Private Restaurants section (#282).
// Returns '' for a public restaurant.
const restaurantVisibilityTag = (restaurant) => {
  if (!restaurant || restaurant.visibility === 'public') return '';
  if (restaurant.review_state === 'pending') return 'pending review';
  if (restaurant.review_state === 'rejected') return 'made private';
  return 'private';
};

const withVisibilityTag = (restaurant) => {
  const tag = restaurantVisibilityTag(restaurant);
  return tag ? `${restaurant.name} (${tag})` : restaurant.name;
};

const AutocompleteSelect = ({
  items,
  selectedId,
  onSelect,
  placeholder,
  label,
  getDisplayText = (item) => item.name,
  getSecondaryText = (item) => item.description || '',
  required = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const wrapperRef = React.useRef(null);

  // Get selected item
  const selectedItem = items.find(item => item.id === selectedId);

  // Filter items based on search term
  const filteredItems = searchTerm.trim().length >= 1
    ? findSimilarItems(searchTerm, items)
    : items;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset highlighted index when filtered items change
  useEffect(() => {
    setHighlightedIndex(0);
  }, [searchTerm]);

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
  };

  const handleSelectItem = (item) => {
    onSelect(item.id);
    setSearchTerm('');
    setIsOpen(false);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    setSearchTerm('');
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < filteredItems.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredItems[highlightedIndex]) {
          handleSelectItem(filteredItems[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        break;
      default:
        break;
    }
  };

  const handleClear = () => {
    onSelect('');
    setSearchTerm('');
    setIsOpen(false);
  };

  return (
    <div className="autocomplete-wrapper" ref={wrapperRef}>
      <label>{label} {required && <span className="required">*</span>}</label>
      <div className="autocomplete-input-wrapper">
        <input
          type="text"
          value={isOpen ? searchTerm : (selectedItem ? getDisplayText(selectedItem) : '')}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={selectedItem ? getDisplayText(selectedItem) : placeholder}
          className="autocomplete-input"
          autoComplete="off"
        />
        {selectedId && (
          <button
            type="button"
            onClick={handleClear}
            className="autocomplete-clear"
            title="Clear selection"
          >
            ✕
          </button>
        )}
      </div>
      {isOpen && (
        <div className="autocomplete-dropdown">
          {filteredItems.length > 0 ? (
            <ul className="autocomplete-list">
              {filteredItems.slice(0, 50).map((item, index) => (
                <li
                  key={item.id}
                  onClick={() => handleSelectItem(item)}
                  className={`autocomplete-item ${index === highlightedIndex ? 'highlighted' : ''} ${item.id === selectedId ? 'selected' : ''}`}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <div className="autocomplete-item-primary">{getDisplayText(item)}</div>
                  {getSecondaryText(item) && (
                    <div className="autocomplete-item-secondary">{getSecondaryText(item)}</div>
                  )}
                </li>
              ))}
              {filteredItems.length > 50 && (
                <li className="autocomplete-more">
                  ... and {filteredItems.length - 50} more. Keep typing to narrow results.
                </li>
              )}
            </ul>
          ) : (
            <div className="autocomplete-empty">No matches found</div>
          )}
        </div>
      )}
    </div>
  );
};

// Google Maps Autocomplete Component
// biasLatitude/biasLongitude rank suggestions around the location being edited
// rather than around our server (#246). An admin edits from a desk, so the
// card's own coordinates are a better anchor than the browser's — and this way
// the admin panel needs no geolocation permission at all.
//
// Passed as two primitives rather than the whole location object on purpose:
// the search below is debounced inside a useCallback, and the location object
// changes identity on every keystroke (typing edits `address`). Depending on
// the object would rebuild the debounce each keystroke, so it would never fire;
// omitting it from the deps would silently capture stale coordinates. The
// coordinates themselves only change when a place is actually picked.
const GoogleMapsAutocomplete = ({ value, onChange, locationIndex, onPlaceSelect, biasLatitude, biasLongitude }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionToken, setSessionToken] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const wrapperRef = React.useRef(null);

  // Generate session token on mount
  useEffect(() => {
    setSessionToken(generateUUID());
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounce helper
  const debounce = useCallback((func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  }, []);

  // Debounced search function
  const searchPlaces = useCallback(
    debounce(async (input) => {
      if (!input || input.length < 3) {
        setSuggestions([]);
        setIsOpen(false);
        return;
      }

      setIsLoading(true);
      try {
        const response = await api.post('/api/google-maps/autocomplete', {
          input,
          session_token: sessionToken,
          ...locationBias({ latitude: biasLatitude, longitude: biasLongitude })
        });
        setSuggestions(response.predictions || []);
        setIsOpen(true);
        setHighlightedIndex(0);
      } catch (error) {
        console.error('Autocomplete error:', error);
        setSuggestions([]);
        setIsOpen(false);
      } finally {
        setIsLoading(false);
      }
    }, 300),
    [sessionToken, biasLatitude, biasLongitude]
  );

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    onChange(e); // Update the actual input value
    searchPlaces(newValue);
  };

  const handleSelect = async (suggestion) => {
    try {
      setIsLoading(true);
      const details = await api.post('/api/google-maps/place-details', {
        place_id: suggestion.place_id,
        session_token: sessionToken
      });

      // Batch update all address fields
      onPlaceSelect(locationIndex, {
        address: details.address || '',
        city: details.city || '',
        state: details.state || '',
        zip_code: details.zip_code || '',
        country: details.country || 'United States',
        latitude: details.latitude,
        longitude: details.longitude
      });

      // Generate new session token for next search
      setSessionToken(generateUUID());
      setSuggestions([]);
      setIsOpen(false);
    } catch (error) {
      console.error('Place details error:', error);
      alert('Failed to get place details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (!isOpen || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (suggestions[highlightedIndex]) {
          handleSelect(suggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSuggestions([]);
        break;
      default:
        break;
    }
  };

  return (
    <div className="autocomplete-wrapper" ref={wrapperRef}>
      <div className="autocomplete-input-wrapper">
        <input
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder="Start typing an address..."
          className="autocomplete-input"
          autoComplete="off"
          required
        />
        {isLoading && (
          <div style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}>
            <div className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }}></div>
          </div>
        )}
      </div>
      {isOpen && suggestions.length > 0 && (
        <div className="autocomplete-dropdown">
          <ul className="autocomplete-list">
            {suggestions.map((suggestion, index) => (
              <li
                key={suggestion.place_id}
                onClick={() => handleSelect(suggestion)}
                className={`autocomplete-item ${index === highlightedIndex ? 'highlighted' : ''}`}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                <div className="autocomplete-item-primary">
                  {suggestion.structured_formatting?.main_text || suggestion.description}
                </div>
                {suggestion.structured_formatting?.secondary_text && (
                  <div className="autocomplete-item-secondary">
                    {suggestion.structured_formatting.secondary_text}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// Duplicate Detection Component
const DuplicateWarning = ({ items, fieldName = 'name' }) => {
  if (!items || items.length === 0) return null;

  return (
    <div className="duplicate-warning">
      <div className="warning-header">⚠️ Similar {fieldName}s found ({items.length}):</div>
      <ul className="duplicate-list">
        {items.slice(0, 5).map((item, index) => (
          <li key={index}>
            <strong>{item.name}</strong>
            {item.description && <span className="duplicate-description"> - {item.description}</span>}
            {item.city && <span className="duplicate-location"> ({item.city}{item.state ? `, ${item.state}` : ''})</span>}
          </li>
        ))}
        {items.length > 5 && <li className="more-items">... and {items.length - 5} more</li>}
      </ul>
    </div>
  );
};

// Utility function to find similar items by name
const findSimilarItems = (searchName, items, excludeId = null) => {
  if (!searchName || searchName.trim().length < 2) return [];

  const normalizedSearch = searchName.toLowerCase().trim();

  return items.filter(item => {
    if (excludeId && item.id === excludeId) return false;
    const normalizedName = item.name.toLowerCase().trim();

    // Exact match
    if (normalizedName === normalizedSearch) return true;

    // Contains match
    if (normalizedName.includes(normalizedSearch) || normalizedSearch.includes(normalizedName)) return true;

    // Word-by-word match
    const searchWords = normalizedSearch.split(/\s+/);
    const nameWords = normalizedName.split(/\s+/);

    return searchWords.some(searchWord =>
      nameWords.some(nameWord =>
        nameWord.includes(searchWord) || searchWord.includes(nameWord)
      )
    );
  });
};

// Cuisines Management
const Cuisines = () => {
  const [cuisines, setCuisines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingCuisine, setEditingCuisine] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [similarCuisines, setSimilarCuisines] = useState([]);

  useEffect(() => {
    fetchCuisines();
  }, []);

  // Check for similar cuisines when name changes
  useEffect(() => {
    if (formData.name && formData.name.trim().length >= 2) {
      const similar = findSimilarItems(formData.name, cuisines, editingCuisine?.id);
      setSimilarCuisines(similar);
    } else {
      setSimilarCuisines([]);
    }
  }, [formData.name, cuisines, editingCuisine]);

  const fetchCuisines = async () => {
    try {
      setLoading(true);
      const data = await api.get('/cuisines/');
      // Sort cuisines alphabetically by name
      const sortedData = data.sort((a, b) => a.name.localeCompare(b.name));
      setCuisines(sortedData);
    } catch (err) {
      setError('Failed to fetch cuisines');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingCuisine) {
        await api.put(`/cuisines/${editingCuisine.id}`, formData);
        setSuccess('Cuisine updated successfully');
      } else {
        await api.post('/cuisines/', formData);
        setSuccess('Cuisine created successfully');
      }
      setFormData({ name: '', description: '' });
      setEditingCuisine(null);
      setShowForm(false);
      fetchCuisines();
    } catch (err) {
      setError('Failed to save cuisine');
    }
  };

  const handleEdit = (cuisine) => {
    setEditingCuisine(cuisine);
    setFormData({ name: cuisine.name, description: cuisine.description || '' });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    try {
      // First check if cuisine is in use
      const [restaurants, foods] = await Promise.all([
        api.get(`/cuisines/${id}/restaurants`),
        api.get(`/cuisines/${id}/foods`)
      ]);

      if (restaurants.length > 0 || foods.length > 0) {
        // Build detailed error message
        let message = `Cannot delete this cuisine because it is being used by:\n\n`;

        if (restaurants.length > 0) {
          message += `Restaurants (${restaurants.length}):\n`;
          restaurants.slice(0, 5).forEach(r => {
            message += `  • ${r.name}\n`;
          });
          if (restaurants.length > 5) {
            message += `  ... and ${restaurants.length - 5} more\n`;
          }
          message += '\n';
        }

        if (foods.length > 0) {
          message += `Foods (${foods.length}):\n`;
          foods.slice(0, 5).forEach(f => {
            message += `  • ${f.name}\n`;
          });
          if (foods.length > 5) {
            message += `  ... and ${foods.length - 5} more\n`;
          }
        }

        message += '\nRemove this cuisine from all items first, then try deleting again.';

        alert(message);
        return;
      }

      // Cuisine is not in use - proceed with delete confirmation
      if (window.confirm('Are you sure you want to delete this cuisine?')) {
        await api.delete(`/cuisines/${id}`);
        setSuccess('Cuisine deleted successfully');
        fetchCuisines();
      }
    } catch (err) {
      setError(err.message || 'Failed to delete cuisine');
    }
  };

  return (
    <div className="section">
      <div className="section-header">
        <h2>Cuisines</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="btn btn-primary"
        >
          <Plus size={16} />
          Add Cuisine
        </button>
      </div>

      {error && <ErrorMessage message={error} onDismiss={() => setError('')} />}
      {success && <SuccessMessage message={success} onDismiss={() => setSuccess('')} />}

      {showForm && (
        <div className="form-container">
          <h3>{editingCuisine ? 'Edit Cuisine' : 'Add New Cuisine'}</h3>
          <div className="form">
            <div className="form-group">
              <label>Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
              <DuplicateWarning items={similarCuisines} fieldName="cuisine" />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows="3"
              />
            </div>
            <div className="form-actions">
              <button onClick={handleSubmit} className="btn btn-success">
                <Save size={16} />
                Save
              </button>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditingCuisine(null);
                  setFormData({ name: '', description: '' });
                }}
                className="btn btn-secondary"
              >
                <X size={16} />
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {cuisines.map((cuisine) => (
                <tr key={cuisine.id}>
                  <td className="font-medium">{cuisine.name}</td>
                  <td>{cuisine.description || 'No description'}</td>
                  <td className="actions">
                    <button
                      onClick={() => handleEdit(cuisine)}
                      className="btn-icon btn-edit"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(cuisine.id)}
                      className="btn-icon btn-delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Restaurant Categories Management
const RestaurantCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [similarCategories, setSimilarCategories] = useState([]);

  useEffect(() => {
    fetchCategories();
  }, []);

  // Check for similar categories when name changes
  useEffect(() => {
    if (formData.name && formData.name.trim().length >= 2) {
      const similar = findSimilarItems(formData.name, categories, editingCategory?.id);
      setSimilarCategories(similar);
    } else {
      setSimilarCategories([]);
    }
  }, [formData.name, categories, editingCategory]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const data = await api.get('/restaurants/categories/');
      // Filter out CLOSED category - now using is_closed field on locations
      const filteredData = data.filter(cat => cat.name.toUpperCase() !== 'CLOSED');
      // Sort categories alphabetically by name
      const sortedData = filteredData.sort((a, b) => a.name.localeCompare(b.name));
      setCategories(sortedData);
    } catch (err) {
      setError('Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingCategory) {
        await api.put(`/restaurants/categories/${editingCategory.id}`, formData);
        setSuccess('Category updated successfully');
      } else {
        await api.post('/restaurants/categories/', formData);
        setSuccess('Category created successfully');
      }
      setFormData({ name: '', description: '' });
      setEditingCategory(null);
      setShowForm(false);
      fetchCategories();
    } catch (err) {
      setError('Failed to save category');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({ name: category.name, description: category.description || '' });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        await api.delete(`/restaurants/categories/${id}`);
        setSuccess('Category deleted successfully');
        fetchCategories();
      } catch (err) {
        setError('Failed to delete category');
      }
    }
  };

  return (
    <div className="section">
      <div className="section-header">
        <h2>Restaurant Categories</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="btn btn-primary"
        >
          <Plus size={16} />
          Add Category
        </button>
      </div>

      {error && <ErrorMessage message={error} onDismiss={() => setError('')} />}
      {success && <SuccessMessage message={success} onDismiss={() => setSuccess('')} />}

      {showForm && (
        <div className="form-container">
          <h3>{editingCategory ? 'Edit Category' : 'Add New Category'}</h3>
          <div className="form">
            <div className="form-group">
              <label>Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
              <DuplicateWarning items={similarCategories} fieldName="restaurant category" />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows="3"
              />
            </div>
            <div className="form-actions">
              <button onClick={handleSubmit} className="btn btn-success">
                <Save size={16} />
                Save
              </button>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditingCategory(null);
                  setFormData({ name: '', description: '' });
                }}
                className="btn btn-secondary"
              >
                <X size={16} />
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((category) => (
                <tr key={category.id}>
                  <td className="font-medium">{category.name}</td>
                  <td>{category.description || 'No description'}</td>
                  <td className="actions">
                    <button
                      onClick={() => handleEdit(category)}
                      className="btn-icon btn-edit"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(category.id)}
                      className="btn-icon btn-delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Restaurants Management
const Restaurants = () => {
  const [restaurants, setRestaurants] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cuisines, setCuisines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingRestaurant, setEditingRestaurant] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [expandedRestaurant, setExpandedRestaurant] = useState(null);
  const [restaurantMenuItems, setRestaurantMenuItems] = useState({});
  const [restaurantImages, setRestaurantImages] = useState({});
  const [loadingMenu, setLoadingMenu] = useState(false);
  const [loadingImages, setLoadingImages] = useState(false);
  const [similarRestaurants, setSimilarRestaurants] = useState([]);
  const [loadingHours, setLoadingHours] = useState({});
  // Per-location address suggestion state (#45): which locations are mid-lookup,
  // and the pending Google suggestion awaiting admin Accept/Edit/Dismiss.
  const [loadingSuggest, setLoadingSuggest] = useState({});
  const [addressSuggestions, setAddressSuggestions] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    website_url: '',
    menu_url: '',
    flickr_album_url: '',
    category_ids: [],
    cuisine_ids: [],
    locations: [{ name: '', address: '', unit: '', city: '', state: '', zip_code: '', country: 'United States', phone: '', is_main_location: true }]
  });

  useEffect(() => {
    fetchRestaurants();
    fetchCategories();
    fetchCuisines();
  }, []);

  // Check for similar restaurants when name changes
  useEffect(() => {
    if (formData.name && formData.name.trim().length >= 2) {
      const similar = findSimilarItems(formData.name, restaurants, editingRestaurant?.id);
      setSimilarRestaurants(similar);
    } else {
      setSimilarRestaurants([]);
    }
  }, [formData.name, restaurants, editingRestaurant]);

  // ESC key handler for modal
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && showForm) {
        resetForm();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [showForm]);

  const fetchRestaurants = async () => {
    try {
      setLoading(true);
      const data = await api.get('/restaurants/?limit=10000');
      setRestaurants(data);
    } catch (err) {
      setError('Failed to fetch restaurants');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const data = await api.get('/restaurants/categories/');
      // Filter out CLOSED category - now using is_closed field on locations
      const filteredData = data.filter(cat => cat.name.toUpperCase() !== 'CLOSED');
      // Sort categories alphabetically by name
      const sortedData = filteredData.sort((a, b) => a.name.localeCompare(b.name));
      setCategories(sortedData);
    } catch (err) {
      console.error('Failed to fetch categories');
    }
  };

  const fetchCuisines = async () => {
    try {
      const data = await api.get('/cuisines/');
      // Sort cuisines alphabetically by name
      const sortedData = data.sort((a, b) => a.name.localeCompare(b.name));
      setCuisines(sortedData);
    } catch (err) {
      console.error('Failed to fetch cuisines');
    }
  };

  const fetchRestaurantMenu = async (restaurantId) => {
    try {
      setLoadingMenu(true);
      const data = await api.get(`/foods/menu/restaurant/${restaurantId}?include_pending=true`);
      setRestaurantMenuItems(prev => ({
        ...prev,
        [restaurantId]: data
      }));
    } catch (err) {
      console.error('Failed to fetch restaurant menu');
      setError('Failed to load menu items');
    } finally {
      setLoadingMenu(false);
    }
  };

  const fetchRestaurantImages = async (restaurantId) => {
    try {
      setLoadingImages(true);
      const data = await api.get(`/restaurants/${restaurantId}/images`);
      setRestaurantImages(prev => ({
        ...prev,
        [restaurantId]: data.images
      }));
    } catch (err) {
      console.error('Failed to fetch restaurant images');
      // Don't show error to user - images are optional
    } finally {
      setLoadingImages(false);
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingRestaurant) {
        // Update main restaurant info
        await api.put(`/restaurants/${editingRestaurant.id}`, {
          name: formData.name,
          description: formData.description,
          website_url: formData.website_url,
          menu_url: formData.menu_url,
          flickr_album_url: formData.flickr_album_url,
          category_ids: formData.category_ids,
          cuisine_ids: formData.cuisine_ids
        });

        // Handle location updates
        const existingLocationIds = editingRestaurant.locations.map(loc => loc.id);

        for (let i = 0; i < formData.locations.length; i++) {
          const location = formData.locations[i];

          if (location.id && existingLocationIds.includes(location.id)) {
            // Update existing location
            await api.put(`/restaurants/locations/${location.id}`, {
              name: location.name,
              address: location.address,
              unit: location.unit,
              city: location.city,
              state: location.state,
              zip_code: location.zip_code,
              country: location.country || 'United States',
              phone: location.phone,
              latitude: location.latitude,
              longitude: location.longitude,
              is_main_location: location.is_main_location,
              is_closed: location.is_closed || false,
              closed_date: location.closed_date || null,
              closure_reason: location.closure_reason || null,
              expected_opening_date: location.expected_opening_date || null,
              monday_hours: location.monday_hours || null,
              tuesday_hours: location.tuesday_hours || null,
              wednesday_hours: location.wednesday_hours || null,
              thursday_hours: location.thursday_hours || null,
              friday_hours: location.friday_hours || null,
              saturday_hours: location.saturday_hours || null,
              sunday_hours: location.sunday_hours || null
            });
          } else {
            // Create new location
            await api.post(`/restaurants/${editingRestaurant.id}/locations/`, {
              name: location.name,
              address: location.address,
              unit: location.unit,
              city: location.city,
              state: location.state,
              zip_code: location.zip_code,
              country: location.country || 'United States',
              phone: location.phone,
              latitude: location.latitude,
              longitude: location.longitude,
              is_main_location: location.is_main_location,
              is_closed: location.is_closed || false,
              closed_date: location.closed_date || null,
              closure_reason: location.closure_reason || null,
              expected_opening_date: location.expected_opening_date || null,
              monday_hours: location.monday_hours || null,
              tuesday_hours: location.tuesday_hours || null,
              wednesday_hours: location.wednesday_hours || null,
              thursday_hours: location.thursday_hours || null,
              friday_hours: location.friday_hours || null,
              saturday_hours: location.saturday_hours || null,
              sunday_hours: location.sunday_hours || null
            });
          }
        }

        // Delete removed locations
        const currentLocationIds = formData.locations.filter(loc => loc.id).map(loc => loc.id);
        const locationsToDelete = existingLocationIds.filter(id => !currentLocationIds.includes(id));

        for (const locationId of locationsToDelete) {
          await api.delete(`/restaurants/locations/${locationId}`);
        }

        setSuccess('Restaurant updated successfully');
      } else {
        await api.post('/restaurants/', formData);
        setSuccess('Restaurant created successfully');
      }
      resetForm();
      fetchRestaurants();
    } catch (err) {
      console.error('Failed to save restaurant:', err);
      // Extract detailed error message
      let errorMessage = 'Failed to save restaurant';
      if (err.detail) {
        errorMessage = typeof err.detail === 'string' ? err.detail : JSON.stringify(err.detail);
      } else if (err.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      website_url: '',
      menu_url: '',
      flickr_album_url: '',
      category_ids: [],
      cuisine_ids: [],
      locations: [{
        name: '',
        address: '',
        unit: '',
        city: '',
        state: '',
        zip_code: '',
        country: 'United States',
        phone: '',
        is_main_location: true,
        monday_hours: '',
        tuesday_hours: '',
        wednesday_hours: '',
        thursday_hours: '',
        friday_hours: '',
        saturday_hours: '',
        sunday_hours: ''
      }]
    });
    setEditingRestaurant(null);
    setShowForm(false);
  };

  const handleEdit = (restaurant) => {
    setEditingRestaurant(restaurant);
    let locations = restaurant.locations.length > 0 ? restaurant.locations :
      [{ address: '', unit: '', city: '', state: '', zip_code: '', phone: '', is_main_location: true }];
    
    // Ensure at least one location is marked as main
    const hasMainLocation = locations.some(loc => loc.is_main_location);
    if (!hasMainLocation && locations.length > 0) {
      locations[0].is_main_location = true;
    }
    
    setFormData({
      name: restaurant.name,
      description: restaurant.description || '',
      website_url: restaurant.website_url || '',
      menu_url: restaurant.menu_url || '',
      flickr_album_url: restaurant.flickr_album_url || '',
      category_ids: restaurant.categories.map(c => c.id),
      cuisine_ids: restaurant.cuisines ? restaurant.cuisines.map(c => c.id) : [],
      locations: locations
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this restaurant?')) {
      try {
        await api.delete(`/restaurants/${id}`);
        setSuccess('Restaurant deleted successfully');
        fetchRestaurants();
      } catch (err) {
        setError('Failed to delete restaurant');
      }
    }
  };

  const updateLocation = (index, field, value) => {
    const newLocations = [...formData.locations];
    newLocations[index] = { ...newLocations[index], [field]: value };

    // If setting main location, unset all others
    if (field === 'is_main_location' && value === true) {
      newLocations.forEach((location, i) => {
        if (i !== index) {
          location.is_main_location = false;
        }
      });
    }

    setFormData({ ...formData, locations: newLocations });
  };

  // Batch update multiple location fields at once to avoid state inconsistency
  const updateLocationFields = (index, updates) => {
    const newLocations = [...formData.locations];
    newLocations[index] = { ...newLocations[index], ...updates };
    setFormData({ ...formData, locations: newLocations });
  };

  const addLocation = () => {
    const newLocation = {
      name: '',
      address: '',
      unit: '',
      city: '',
      state: '',
      zip_code: '',
      country: 'United States',
      phone: '',
      is_main_location: false,
      monday_hours: '',
      tuesday_hours: '',
      wednesday_hours: '',
      thursday_hours: '',
      friday_hours: '',
      saturday_hours: '',
      sunday_hours: ''
    };
    setFormData({ ...formData, locations: [...formData.locations, newLocation] });
  };

  const removeLocation = (index) => {
    if (formData.locations.length <= 1) {
      alert('Restaurant must have at least one location');
      return;
    }

    const newLocations = formData.locations.filter((_, i) => i !== index);

    // If we removed the main location, make the first remaining location main
    if (formData.locations[index].is_main_location && newLocations.length > 0) {
      newLocations[0].is_main_location = true;
    }

    setFormData({ ...formData, locations: newLocations });
  };

  const fetchHoursFromGoogle = async (locationIndex) => {
    const location = formData.locations[locationIndex];

    if (!formData.name) {
      setError('Restaurant name is required to fetch hours from Google');
      return;
    }

    try {
      setLoadingHours(prev => ({ ...prev, [locationIndex]: true }));

      // Use the new dedicated endpoint that handles search + hours in one call
      const response = await api.post('/api/google-maps/fetch-hours', {
        business_name: formData.name,
        address: location.address,
        city: location.city,
        state: location.state,
        ...locationBias(location)
      });

      if (!response.found) {
        setError(response.error || 'Business not found in Google Maps');
        setLoadingHours(prev => ({ ...prev, [locationIndex]: false }));
        return;
      }

      if (!response.opening_hours) {
        setError(response.error || 'Opening hours not available for this business');
        setLoadingHours(prev => ({ ...prev, [locationIndex]: false }));
        return;
      }

      // Update all hours fields
      const hoursUpdates = {
        monday_hours: response.opening_hours.monday_hours || '',
        tuesday_hours: response.opening_hours.tuesday_hours || '',
        wednesday_hours: response.opening_hours.wednesday_hours || '',
        thursday_hours: response.opening_hours.thursday_hours || '',
        friday_hours: response.opening_hours.friday_hours || '',
        saturday_hours: response.opening_hours.saturday_hours || '',
        sunday_hours: response.opening_hours.sunday_hours || ''
      };

      updateLocationFields(locationIndex, hoursUpdates);
      setSuccess(`Operating hours loaded from Google Maps! (${response.place_name})`);

    } catch (err) {
      console.error('Failed to fetch hours from Google:', err);
      setError(err.detail || 'Failed to fetch hours from Google Maps. Please try again.');
    } finally {
      setLoadingHours(prev => ({ ...prev, [locationIndex]: false }));
    }
  };

  // #45 — suggest a street address from Google using the restaurant name + city.
  // Mirrors fetchHoursFromGoogle, but the result is staged for admin review (the
  // inline card) rather than applied directly — Google can return a wrong/relocated
  // match (cf. Cafe Luna). Apply happens in applyAddressSuggestion.
  const suggestAddressFromGoogle = async (locationIndex) => {
    const location = formData.locations[locationIndex];

    if (!formData.name) {
      setError('Restaurant name is required to suggest an address from Google');
      return;
    }
    if (!location.city) {
      setError('City is required to suggest an address from Google');
      return;
    }

    try {
      setLoadingSuggest(prev => ({ ...prev, [locationIndex]: true }));

      const response = await api.post('/api/google-maps/suggest-address', {
        business_name: formData.name,
        city: location.city,
        state: location.state,
        ...locationBias(location)
      });

      if (!response.found) {
        setError(response.error || 'Business not found in Google Maps');
        return;
      }

      // Stage for review; if Google matched but returned no street-level address,
      // response.error explains it and `address` will be blank.
      setAddressSuggestions(prev => ({
        ...prev,
        [locationIndex]: { ...response, editing: false, editedAddress: response.address || '' }
      }));
      if (response.error) {
        setError(response.error);
      }
    } catch (err) {
      console.error('Failed to suggest address from Google:', err);
      setError(err.detail || 'Failed to suggest address from Google Maps. Please try again.');
    } finally {
      setLoadingSuggest(prev => ({ ...prev, [locationIndex]: false }));
    }
  };

  const applyAddressSuggestion = (locationIndex) => {
    const s = addressSuggestions[locationIndex];
    if (!s) return;
    const current = formData.locations[locationIndex];
    updateLocationFields(locationIndex, {
      address: (s.editing ? s.editedAddress : s.address) || '',
      city: s.city || current.city || '',
      state: s.state || current.state || '',
      zip_code: s.zip_code || current.zip_code || '',
      country: s.country || current.country || 'United States',
      latitude: s.latitude,
      longitude: s.longitude
    });
    dismissAddressSuggestion(locationIndex);
    setSuccess(`Address applied from Google Maps${s.formatted_address ? ` (${s.formatted_address})` : ''}`);
  };

  const dismissAddressSuggestion = (locationIndex) => {
    setAddressSuggestions(prev => {
      const next = { ...prev };
      delete next[locationIndex];
      return next;
    });
  };

  const toggleRestaurantDetails = async (restaurantId) => {
    if (expandedRestaurant === restaurantId) {
      setExpandedRestaurant(null);
    } else {
      setExpandedRestaurant(restaurantId);
      // Fetch menu items and images if not already loaded
      if (!restaurantMenuItems[restaurantId]) {
        await fetchRestaurantMenu(restaurantId);
      }
      if (!restaurantImages[restaurantId]) {
        await fetchRestaurantImages(restaurantId);
      }
    }
  };

  return (
    <div className="section">
      <div className="section-header">
        <h2>Restaurants</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="btn btn-primary"
        >
          <Plus size={16} />
          Add Restaurant
        </button>
      </div>

      {error && <ErrorMessage message={error} onDismiss={() => setError('')} />}
      {success && <SuccessMessage message={success} onDismiss={() => setSuccess('')} />}

      {showForm && (
        <div className="modal-overlay" onClick={(e) => {
          if (e.target.className === 'modal-overlay') {
            resetForm();
          }
        }}>
          <div className="modal-content">
            <div className="form-container">
              <h3>{editingRestaurant ? 'Edit Restaurant' : 'Add New Restaurant'}</h3>
          <div className="form">
            <div className="form-grid">
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
                <DuplicateWarning items={similarRestaurants} fieldName="restaurant" />
              </div>
              <div className="form-group">
                <label>Categories</label>
                <select
                  multiple
                  value={formData.category_ids}
                  onChange={(e) => setFormData({ ...formData, category_ids: Array.from(e.target.selectedOptions, option => parseInt(option.value)) })}
                >
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>{category.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Cuisines</label>
                <select
                  multiple
                  value={formData.cuisine_ids}
                  onChange={(e) => setFormData({ ...formData, cuisine_ids: Array.from(e.target.selectedOptions, option => parseInt(option.value)) })}
                >
                  {cuisines.map(cuisine => (
                    <option key={cuisine.id} value={cuisine.id}>{cuisine.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows="3"
              />
              <small className="text-muted">
                Supports links and <strong>bold</strong> — paste a URL, or use <code>[label](https://…)</code> and <code>**bold**</code>.
              </small>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>Website URL</label>
                <input
                  type="url"
                  value={formData.website_url}
                  onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                  placeholder="https://example.com"
                />
              </div>
              <div className="form-group">
                <label>Menu URL</label>
                <input
                  type="url"
                  value={formData.menu_url}
                  onChange={(e) => setFormData({ ...formData, menu_url: e.target.value })}
                  placeholder="https://example.com/menu"
                />
              </div>
              <div className="form-group">
                <label>Flickr Album URL</label>
                <input
                  type="url"
                  value={formData.flickr_album_url}
                  onChange={(e) => setFormData({ ...formData, flickr_album_url: e.target.value })}
                  placeholder="https://flickr.com/photos/user/albums/..."
                />
              </div>
            </div>
            
            <div className="location-section">
              <div className="location-header">
                <h4>Locations ({formData.locations.length})</h4>
                <button
                  type="button"
                  onClick={addLocation}
                  className="btn btn-outline btn-small"
                >
                  <Plus size={14} />
                  Add Location
                </button>
              </div>
              
              {formData.locations.map((location, index) => (
                <div key={index} className="location-form">
                  <div className="location-form-header">
                    <div className="location-title">
                      <span>Location {index + 1}</span>
                      {location.is_main_location && (
                        <span className="main-location-badge">Main Location</span>
                      )}
                    </div>
                    {formData.locations.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeLocation(index)}
                        className="btn-icon btn-delete btn-small"
                        title="Remove location"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                  
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Location Name (Optional)</label>
                      <input
                        type="text"
                        value={location.name || ''}
                        onChange={(e) => updateLocation(index, 'name', e.target.value)}
                        placeholder="e.g., Downtown, Airport, Union Square"
                      />
                    </div>
                    <div className="form-group">
                      <label>Address</label>
                      <GoogleMapsAutocomplete
                        value={location.address}
                        onChange={(e) => updateLocation(index, 'address', e.target.value)}
                        locationIndex={index}
                        onPlaceSelect={(idx, updates) => updateLocationFields(idx, updates)}
                        biasLatitude={location.latitude}
                        biasLongitude={location.longitude}
                      />
                      {/* #45 — one-click address lookup from name + city, for when
                          the admin doesn't want to type into the autocomplete. */}
                      <button
                        type="button"
                        onClick={() => suggestAddressFromGoogle(index)}
                        className="btn btn-secondary btn-small"
                        style={{ fontSize: '13px', padding: '6px 12px', marginTop: '8px' }}
                        disabled={loadingSuggest[index] || !formData.name || !location.city}
                        title={!location.city ? 'Add a city first' : 'Look up the street address from Google using the name + city'}
                      >
                        {loadingSuggest[index] ? (
                          <>
                            <RefreshCw size={14} style={{ marginRight: '6px', animation: 'spin 1s linear infinite' }} />
                            Searching...
                          </>
                        ) : (
                          <>
                            <MapPin size={14} style={{ marginRight: '6px' }} />
                            Suggest address from Google
                          </>
                        )}
                      </button>
                      {addressSuggestions[index] && (
                        <div style={{ marginTop: '10px', padding: '12px', border: '1px solid #d0d7de', borderRadius: '8px', background: '#f6f8fa' }}>
                          <div style={{ fontSize: '12px', fontWeight: 600, color: '#57606a', marginBottom: '6px' }}>
                            Suggested from Google:
                          </div>
                          {addressSuggestions[index].editing ? (
                            <input
                              type="text"
                              value={addressSuggestions[index].editedAddress}
                              onChange={(e) => setAddressSuggestions(prev => ({
                                ...prev,
                                [index]: { ...prev[index], editedAddress: e.target.value }
                              }))}
                              placeholder="Street address"
                              style={{ width: '100%', marginBottom: '8px' }}
                            />
                          ) : (
                            <div style={{ fontSize: '14px', color: '#1f2328', marginBottom: '8px' }}>
                              {addressSuggestions[index].formatted_address
                                || addressSuggestions[index].address
                                || '(no street address returned for this match)'}
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              type="button"
                              onClick={() => applyAddressSuggestion(index)}
                              className="btn btn-primary btn-small"
                              style={{ fontSize: '13px', padding: '6px 12px' }}
                              disabled={!addressSuggestions[index].address && !addressSuggestions[index].editedAddress}
                            >
                              <CheckCircle size={14} style={{ marginRight: '6px' }} />
                              Accept
                            </button>
                            <button
                              type="button"
                              onClick={() => setAddressSuggestions(prev => ({
                                ...prev,
                                [index]: { ...prev[index], editing: !prev[index].editing }
                              }))}
                              className="btn btn-secondary btn-small"
                              style={{ fontSize: '13px', padding: '6px 12px' }}
                            >
                              <Edit size={14} style={{ marginRight: '6px' }} />
                              {addressSuggestions[index].editing ? 'Done' : 'Edit'}
                            </button>
                            <button
                              type="button"
                              onClick={() => dismissAddressSuggestion(index)}
                              className="btn btn-secondary btn-small"
                              style={{ fontSize: '13px', padding: '6px 12px' }}
                            >
                              <X size={14} style={{ marginRight: '6px' }} />
                              Dismiss
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <label>Unit/Suite/Apt</label>
                      <input
                        type="text"
                        value={location.unit || ''}
                        onChange={(e) => updateLocation(index, 'unit', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>City</label>
                      <input
                        type="text"
                        value={location.city}
                        onChange={(e) => updateLocation(index, 'city', e.target.value)}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>State/Province/Region</label>
                      <input
                        type="text"
                        value={location.state}
                        onChange={(e) => updateLocation(index, 'state', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Postal/ZIP Code</label>
                      <input
                        type="text"
                        value={location.zip_code}
                        onChange={(e) => updateLocation(index, 'zip_code', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Country</label>
                      <select
                        value={location.country || 'United States'}
                        onChange={(e) => updateLocation(index, 'country', e.target.value)}
                      >
                        <option value="United States">United States</option>
                        <option value="Canada">Canada</option>
                        <option value="Bangladesh">Bangladesh</option>
                        <option value="United Kingdom">United Kingdom</option>
                        <option value="Australia">Australia</option>
                        <option value="India">India</option>
                        <option value="Mexico">Mexico</option>
                        <option value="France">France</option>
                        <option value="Germany">Germany</option>
                        <option value="Italy">Italy</option>
                        <option value="Spain">Spain</option>
                        <option value="Japan">Japan</option>
                        <option value="China">China</option>
                        <option value="South Korea">South Korea</option>
                        <option value="Thailand">Thailand</option>
                        <option value="Brazil">Brazil</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    {location.latitude && location.longitude && (
                      <div className="form-group">
                        <div className="geocoded-indicator" style={{
                          color: '#10b981',
                          fontSize: '13px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '8px 12px',
                          background: '#f0fdf4',
                          borderRadius: '4px',
                          border: '1px solid #bbf7d0'
                        }}>
                          <CheckCircle size={16} />
                          <span>
                            <strong>Geocoded:</strong> {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
                          </span>
                        </div>
                      </div>
                    )}
                    <div className="form-group">
                      <label>Phone</label>
                      <input
                        type="text"
                        value={location.phone}
                        onChange={(e) => updateLocation(index, 'phone', e.target.value)}
                      />
                    </div>

                    {/* Location Status Dropdown */}
                    <div className="form-group">
                      <label>Location Status</label>
                      <select
                        value={
                          location.is_closed
                            ? location.closure_reason || 'Temporarily Closed'
                            : location.closure_reason === 'Opening Soon'
                            ? 'Opening Soon'
                            : 'Open'
                        }
                        onChange={(e) => {
                          const status = e.target.value;

                          if (status === 'Open') {
                            // Location is open - clear all closure fields
                            updateLocationFields(index, {
                              is_closed: false,
                              closure_reason: null,
                              closed_date: null,
                              expected_opening_date: null
                            });
                          } else if (status === 'Opening Soon') {
                            // New location not yet open
                            updateLocationFields(index, {
                              is_closed: false,
                              closure_reason: 'Opening Soon',
                              closed_date: null
                              // Keep expected_opening_date if it exists
                            });
                          } else {
                            // Location is closed with specific reason
                            updateLocationFields(index, {
                              is_closed: true,
                              closure_reason: status
                              // Keep closed_date and expected_opening_date if they exist
                            });
                          }
                        }}
                      >
                        <option value="Open">Open</option>
                        <option value="Opening Soon">Opening Soon</option>
                        <option value="Temporarily Closed">Temporarily Closed</option>
                        <option value="Under Renovation">Under Renovation</option>
                        <option value="Relocated">Relocated</option>
                        <option value="Permanently Closed">Permanently Closed</option>
                      </select>
                    </div>

                    {/* Closed Date - shown for closed statuses */}
                    {location.is_closed && (
                      <div className="form-group">
                        <label>Closed Date</label>
                        <input
                          type="date"
                          value={location.closed_date || ''}
                          onChange={(e) => updateLocation(index, 'closed_date', e.target.value)}
                        />
                      </div>
                    )}

                    {/* Expected Opening/Reopening Date */}
                    {(location.closure_reason === 'Opening Soon' ||
                      location.closure_reason === 'Under Renovation' ||
                      location.closure_reason === 'Temporarily Closed') && (
                      <div className="form-group">
                        <label>
                          {location.closure_reason === 'Opening Soon'
                            ? 'Expected Opening Date'
                            : 'Expected Reopening Date (Optional)'}
                        </label>
                        <input
                          type="date"
                          value={location.expected_opening_date || ''}
                          onChange={(e) => updateLocation(index, 'expected_opening_date', e.target.value)}
                          placeholder={
                            location.closure_reason === 'Opening Soon'
                              ? 'When will it open?'
                              : 'When will it reopen?'
                          }
                        />
                      </div>
                    )}

                    {/* Operating Hours Section */}
                    <div className="form-section" style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e0e0e0' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                        <h4 style={{ margin: 0, color: '#333' }}>
                          <Clock size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                          Operating Hours
                        </h4>
                        <button
                          type="button"
                          onClick={() => fetchHoursFromGoogle(index)}
                          className="btn btn-secondary btn-small"
                          style={{ fontSize: '13px', padding: '6px 12px' }}
                          disabled={loadingHours[index] || !location.latitude || !location.longitude}
                          title={!location.latitude || !location.longitude ? 'Add an address with coordinates first' : 'Fetch operating hours from Google Maps'}
                        >
                          {loadingHours[index] ? (
                            <>
                              <RefreshCw size={14} style={{ marginRight: '6px', animation: 'spin 1s linear infinite' }} />
                              Loading...
                            </>
                          ) : (
                            <>
                              <Clock size={14} style={{ marginRight: '6px' }} />
                              Get Hours from Google
                            </>
                          )}
                        </button>
                      </div>
                      <div className="form-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }}>
                        {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                          <div className="form-group" key={day}>
                            <label style={{ textTransform: 'capitalize' }}>{day}</label>
                            <input
                              type="text"
                              value={location[`${day}_hours`] || ''}
                              onChange={(e) => updateLocation(index, `${day}_hours`, e.target.value)}
                              placeholder="e.g., 9:00 AM - 10:00 PM, Closed, 24 Hours"
                            />
                          </div>
                        ))}
                      </div>
                      <p style={{ fontSize: '12px', color: '#666', marginTop: '10px', fontStyle: 'italic' }}>
                        Examples: "9:00 AM - 10:00 PM", "Closed", "24 Hours", "11:00 AM - 2:00 PM, 5:00 PM - 10:00 PM"
                      </p>
                    </div>

                    <div className="form-group main-location-group">
                      <label className="checkbox-label">
                        <input
                          type="radio"
                          name="main_location"
                          checked={location.is_main_location}
                          onChange={(e) => updateLocation(index, 'is_main_location', e.target.checked)}
                        />
                        Main Location
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="form-actions">
              <button onClick={handleSubmit} className="btn btn-success">
                <Save size={16} />
                Save
              </button>
              <button onClick={resetForm} className="btn btn-secondary">
                <X size={16} />
                Cancel
              </button>
            </div>
          </div>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Categories</th>
                <th>Links</th>
                <th>Locations</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {restaurants.map((restaurant) => (
                <React.Fragment key={restaurant.id}>
                  <tr>
                    <td>
                      <div>
                        <div className="font-medium">
                          {restaurant.name}
                          <button
                            onClick={() => toggleRestaurantDetails(restaurant.id)}
                            className="btn-icon btn-expand"
                            style={{ marginLeft: '8px' }}
                          >
                            {expandedRestaurant === restaurant.id ? '▼' : '▶'}
                          </button>
                        </div>
                        <div className="text-secondary">{restaurant.description}</div>
                      </div>
                    </td>
                    <td>{restaurant.categories.map(cat => cat.name).join(', ') || 'No categories'}</td>
                    <td>
                      <div className="links-icons">
                        {restaurant.website_url && (
                          <a href={restaurant.website_url} target="_blank" rel="noopener noreferrer" className="link-icon" title="Website">
                            🌐
                          </a>
                        )}
                        {restaurant.menu_url && (
                          <a href={restaurant.menu_url} target="_blank" rel="noopener noreferrer" className="link-icon" title="Menu">
                            📄
                          </a>
                        )}
                        {restaurant.flickr_album_url && (
                          <a href={restaurant.flickr_album_url} target="_blank" rel="noopener noreferrer" className="link-icon" title="Photos">
                            📸
                          </a>
                        )}
                        {!restaurant.website_url && !restaurant.menu_url && !restaurant.flickr_album_url && (
                          <span className="text-secondary">-</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="locations-compact">
                        {restaurant.locations.length > 0 && (
                          <div className="location-summary">
                            {(() => {
                              // Sort to show main location first
                              const sortedLocations = [...restaurant.locations].sort((a, b) =>
                                b.is_main_location - a.is_main_location
                              );

                              // Show first 2 locations
                              const displayLocations = sortedLocations.slice(0, 2);
                              const remainingCount = restaurant.locations.length - displayLocations.length;

                              return (
                                <>
                                  {displayLocations.map((loc, index) => (
                                    <span key={loc.id}>
                                      {index > 0 && ', '}
                                      {loc.name || `${loc.city}${loc.state ? `, ${loc.state}` : ''}`}
                                    </span>
                                  ))}
                                  {remainingCount > 0 && (
                                    <span className="additional-count"> +{remainingCount}</span>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="actions">
                      <button
                        onClick={() => handleEdit(restaurant)}
                        className="btn-icon btn-edit"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(restaurant.id)}
                        className="btn-icon btn-delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                  {expandedRestaurant === restaurant.id && (
                    <tr>
                      <td colSpan="5" className="menu-details-cell">
                        <div className="menu-details">
                          {/* Restaurant Images Section */}
                          {restaurantImages[restaurant.id] && restaurantImages[restaurant.id].length > 0 && (
                            <div style={{ marginBottom: '24px' }}>
                              <h4>Restaurant Images</h4>
                              {loadingImages ? (
                                <div className="loading-menu">Loading images...</div>
                              ) : (
                                <div className="menu-items-grid">
                                  {restaurantImages[restaurant.id].map((image) => (
                                    <div key={image.id} className="menu-item-card">
                                      <div className="menu-item-image">
                                        <img src={image.flickr_url} alt={image.caption || 'Restaurant image'} />
                                      </div>
                                      {image.caption && (
                                        <div className="menu-item-name">
                                          {image.caption}
                                        </div>
                                      )}
                                      {image.image_type && (
                                        <div className="menu-item-categories">
                                          Type: {image.image_type}
                                        </div>
                                      )}
                                      {image.is_primary === 1 && (
                                        <div className="menu-item-status">
                                          <span className="status-badge available">Primary</span>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                          <h4>Menu Items</h4>
                          {loadingMenu ? (
                            <div className="loading-menu">Loading menu...</div>
                          ) : restaurantMenuItems[restaurant.id] ? (
                            restaurantMenuItems[restaurant.id].length > 0 ? (
                              <div className="menu-items-grid">
                                {restaurantMenuItems[restaurant.id].map((item) => {
                                  const primaryImage = item.images?.find(img => img.is_primary) || item.images?.[0];
                                  return (
                                    <div key={item.id} className="menu-item-card">
                                      {primaryImage && (
                                        <div className="menu-item-image">
                                          <img src={primaryImage.flickr_url} alt={item.custom_name || item.food.name} />
                                        </div>
                                      )}
                                      <div className="menu-item-name">
                                        {item.custom_name || item.food.name}
                                        {item.secondary_foods && item.secondary_foods.length > 0 && (
                                          <span style={{ color: '#6b7280', fontSize: '0.9em', fontWeight: 'normal' }}>
                                            {' + '}
                                            {item.secondary_foods.map(sf => sf.name).join(', ')}
                                          </span>
                                        )}
                                      </div>
                                      <div className="menu-item-price">
                                        {item.price != null
                                          ? `$${item.price}`
                                          : (item.food?.base_price
                                              ? `$${item.food.base_price} (base)`
                                              : 'No price set')
                                        }
                                      </div>
                                      <div className="menu-item-status">
                                        <span className={`status-badge ${item.is_available ? 'available' : 'unavailable'}`}>
                                          {item.is_available ? 'Available' : 'Unavailable'}
                                        </span>
                                      </div>
                                      {item.custom_description && (
                                        <div className="menu-item-description">
                                          {item.custom_description}
                                        </div>
                                      )}
                                      {(() => {
                                        // Combine categories from primary food and all secondary foods
                                        const allCategories = [...(item.food.categories || [])];
                                        if (item.secondary_foods && item.secondary_foods.length > 0) {
                                          item.secondary_foods.forEach(sf => {
                                            if (sf.categories) {
                                              sf.categories.forEach(cat => {
                                                // Add category if not already in the list
                                                if (!allCategories.find(c => c.id === cat.id)) {
                                                  allCategories.push(cat);
                                                }
                                              });
                                            }
                                          });
                                        }
                                        return allCategories.length > 0 && (
                                          <div className="menu-item-categories">
                                            {allCategories.map(cat => cat.name).join(', ')}
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="no-menu-items">No menu items found for this restaurant</div>
                            )
                          ) : (
                            <div className="menu-error">Failed to load menu items</div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Food Categories Management (similar structure to Restaurant Categories)
const FoodCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [similarCategories, setSimilarCategories] = useState([]);

  useEffect(() => {
    fetchCategories();
  }, []);

  // Check for similar categories when name changes
  useEffect(() => {
    if (formData.name && formData.name.trim().length >= 2) {
      const similar = findSimilarItems(formData.name, categories, editingCategory?.id);
      setSimilarCategories(similar);
    } else {
      setSimilarCategories([]);
    }
  }, [formData.name, categories, editingCategory]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const data = await api.get('/foods/categories/');
      // Filter out CLOSED category - now using is_closed field on locations
      const filteredData = data.filter(cat => cat.name.toUpperCase() !== 'CLOSED');
      // Sort categories alphabetically by name
      const sortedData = filteredData.sort((a, b) => a.name.localeCompare(b.name));
      setCategories(sortedData);
    } catch (err) {
      setError('Failed to fetch food categories');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingCategory) {
        await api.put(`/foods/categories/${editingCategory.id}`, formData);
        setSuccess('Food category updated successfully');
      } else {
        await api.post('/foods/categories/', formData);
        setSuccess('Food category created successfully');
      }
      setFormData({ name: '', description: '' });
      setEditingCategory(null);
      setShowForm(false);
      fetchCategories();
    } catch (err) {
      setError('Failed to save food category');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({ name: category.name, description: category.description || '' });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this food category?')) {
      try {
        await api.delete(`/foods/categories/${id}`);
        setSuccess('Food category deleted successfully');
        fetchCategories();
      } catch (err) {
        setError('Failed to delete food category');
      }
    }
  };

  return (
    <div className="section">
      <div className="section-header">
        <h2>Food Categories</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="btn btn-primary"
        >
          <Plus size={16} />
          Add Food Category
        </button>
      </div>

      {error && <ErrorMessage message={error} onDismiss={() => setError('')} />}
      {success && <SuccessMessage message={success} onDismiss={() => setSuccess('')} />}

      {showForm && (
        <div className="form-container">
          <h3>{editingCategory ? 'Edit Food Category' : 'Add New Food Category'}</h3>
          <div className="form">
            <div className="form-group">
              <label>Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
              <DuplicateWarning items={similarCategories} fieldName="food category" />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows="3"
              />
            </div>
            <div className="form-actions">
              <button onClick={handleSubmit} className="btn btn-success">
                <Save size={16} />
                Save
              </button>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditingCategory(null);
                  setFormData({ name: '', description: '' });
                }}
                className="btn btn-secondary"
              >
                <X size={16} />
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((category) => (
                <tr key={category.id}>
                  <td className="font-medium">{category.name}</td>
                  <td>{category.description || 'No description'}</td>
                  <td className="actions">
                    <button
                      onClick={() => handleEdit(category)}
                      className="btn-icon btn-edit"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(category.id)}
                      className="btn-icon btn-delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Menu Items Management
// #151: how a price unit renders next to an amount. Keys match the backend's
// PRICE_UNITS (schemas/food.py); `each` is the default and is stored as null, so
// it has no suffix and doesn't appear here.
const PRICE_UNIT_SUFFIX = {
  lb: '/lb',
  oz: '/oz',
  kg: '/kg',
  g: '/g',
  '100g': '/100g',
  ml: '/ml',
  l: '/L',
  '100ml': '/100ml',
  dozen: '/dozen',
  person: '/person',
};

// The pickable list, derived from the same map so the two can't drift. #208
// added ml/l/100ml to the backend and to PRICE_UNIT_SUFFIX above, but the Menu
// Items form had its own hand-written <option> list and silently didn't offer
// them — the exact drift #52 created the meal-types endpoint to stop. One
// source, used by both the Menu Item and Food (#209) forms.
//
// `each` is the default and is stored as null, so it is the empty option rather
// than a value.
const PRICE_UNIT_OPTIONS = Object.keys(PRICE_UNIT_SUFFIX).map((unit) => ({
  value: unit,
  label: `per ${unit === 'l' ? 'L' : unit}`,
}));

const MenuItems = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [foods, setFoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingMenuItem, setEditingMenuItem] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showImageManager, setShowImageManager] = useState(false);
  const [managingImagesFor, setManagingImagesFor] = useState(null);
  const [menuItemImages, setMenuItemImages] = useState([]);
  const [imageLoading, setImageLoading] = useState(false);
  const [newImage, setNewImage] = useState({
    flickr_url: '',
    caption: '',
    is_primary: false,
    display_order: 0
  });
  const [showSecondaryFoodsManager, setShowSecondaryFoodsManager] = useState(false);
  const [managingSecondaryFoodsFor, setManagingSecondaryFoodsFor] = useState(null);
  const [secondaryFoods, setSecondaryFoods] = useState([]);
  const [secondaryFoodsLoading, setSecondaryFoodsLoading] = useState(false);
  const [selectedSecondaryFood, setSelectedSecondaryFood] = useState('');
  const [formData, setFormData] = useState({
    restaurant_id: '',
    food_id: '',
    price: '',
    // #151: "priced per what". '' means each (sent as null).
    price_unit: '',
    is_available: true,
    custom_name: '',
    custom_description: ''
  });
  // #151: size variants for the item being edited, as [{ size_id, price }].
  // Kept OUT of formData because it isn't a scalar field and, unlike everything
  // in there, it can't ride the create request (see handleSubmit).
  const [priceVariants, setPriceVariants] = useState([]);
  const [menuItemSizes, setMenuItemSizes] = useState([]);
  const [restaurantFilter, setRestaurantFilter] = useState('');
  const [foodFilter, setFoodFilter] = useState('');

  useEffect(() => {
    fetchMenuItems();
    fetchRestaurants();
    fetchFoods();
    fetchMenuItemSizes();
  }, []);

  const fetchMenuItems = async () => {
    try {
      setLoading(true);
      const data = await api.get('/foods/menu/items/?include_pending=true&limit=10000');
      // Sort menu items alphabetically by food name (custom_name or food.name)
      const sortedData = data.sort((a, b) => {
        const nameA = a.custom_name || a.food?.name || '';
        const nameB = b.custom_name || b.food?.name || '';
        return nameA.localeCompare(nameB);
      });
      setMenuItems(sortedData);
    } catch (err) {
      setError('Failed to fetch menu items');
    } finally {
      setLoading(false);
    }
  };

  const fetchRestaurants = async () => {
    try {
      // #345: the operator read (§ 2.5). Without include_pending this was the
      // DINER's list, so a private restaurant's menu items appeared (their own
      // call is operator-scoped) while the restaurant itself was missing from
      // the filter and the edit form's Restaurant field rendered blank.
      const data = await api.get('/restaurants/?include_pending=true&limit=10000');
      // Sort restaurants alphabetically by name
      const sortedData = data.sort((a, b) => a.name.localeCompare(b.name));
      setRestaurants(sortedData);
    } catch (err) {
      console.error('Failed to fetch restaurants');
    }
  };

  const fetchFoods = async () => {
    try {
      const data = await api.get('/foods/?limit=10000');
      // Sort foods alphabetically by name
      const sortedData = data.sort((a, b) => a.name.localeCompare(b.name));
      setFoods(sortedData);
    } catch (err) {
      console.error('Failed to fetch foods');
    }
  };

  const fetchMenuItemSizes = async () => {
    try {
      // #151: the canonical size vocabulary, same role /meal-types plays for
      // visits — fetched rather than hardcoded, so adding a size server-side
      // reaches this form with no release.
      setMenuItemSizes(await api.get('/api/v1/sizes/'));
    } catch (err) {
      console.error('Failed to fetch menu item sizes');
    }
  };

  // #209: picking a food pre-seeds this item from the catalog's template — the
  // sizes that dish comes in, and what it's priced by. Prices are left blank:
  // those are the one thing the Food cannot know.
  //
  // Only ever ADDITIVE, and only into empty space. Anything the admin has
  // already typed wins, and an existing item being edited is never touched —
  // re-pointing a menu item at a different catalog food must not silently
  // rewrite prices someone entered by hand (#191 is what that looks like when
  // it goes wrong).
  const handleFoodSelected = (id) => {
    const food = foods.find((f) => f.id === id);
    const seedableUnit = !formData.price_unit && food?.default_price_unit;

    setFormData({
      ...formData,
      food_id: id,
      price_unit: seedableUnit ? food.default_price_unit : formData.price_unit,
    });

    if (editingMenuItem || !food?.sizes?.length) return;

    // "Untouched" means no row carries a size or a price yet — the blank
    // scaffolding row the form starts with counts as untouched.
    const untouched = priceVariants.every((v) => v.size_id === '' && v.price === '');
    if (!untouched) return;

    setPriceVariants(food.sizes.map((s) => ({ size_id: String(s.id), price: '' })));
  };

  // #209: split the vocabulary into "typical for this dish" and the rest, so the
  // food's template leads the picker instead of being invisible among 24 entries.
  // Returns one ungrouped block when there is no template, which keeps the
  // dropdown exactly as it was for a food that declares none.
  const sizeGroupsForSelectedFood = () => {
    const food = foods.find((f) => f.id === parseInt(formData.food_id));
    const templateIds = new Set((food?.sizes || []).map((s) => s.id));
    if (templateIds.size === 0) return [{ label: null, sizes: menuItemSizes }];

    const label = food?.name ? `Typical for ${food.name}` : 'Typical for this dish';
    return [
      { label, sizes: menuItemSizes.filter((s) => templateIds.has(s.id)) },
      { label: 'All sizes', sizes: menuItemSizes.filter((s) => !templateIds.has(s.id)) },
    ];
  };

  const renderSizeOption = (size, rowIndex) => (
    <option
      key={size.id}
      value={size.id}
      // Can't pick a size already used on this item — the backend rejects
      // duplicates, so don't offer them.
      disabled={priceVariants.some(
        (v, i) => i !== rowIndex && String(v.size_id) === String(size.id)
      )}
    >
      {size.display_name}
    </option>
  );

  // #151: rows the user has actually filled in. A blank row is scaffolding, not
  // data — sending it would be a validation error for something they never typed.
  const completeVariants = () =>
    priceVariants
      .filter((v) => v.size_id !== '' && v.price !== '')
      .map((v) => ({ size_id: parseInt(v.size_id), price: parseFloat(v.price) }));

  // #151: the client-side half of the backend's rules, so the admin gets a
  // sentence instead of a 400. The backend still enforces both.
  const variantError = () => {
    const rows = completeVariants();
    const ids = rows.map((v) => v.size_id);
    if (new Set(ids).size !== ids.length) {
      return 'Each size can only appear once — remove the duplicate before saving.';
    }
    if (rows.some((v) => Number.isNaN(v.price) || v.price < 0)) {
      return 'Variant prices must be zero or more.';
    }
    return null;
  };

  const handleSubmit = async () => {
    try {
      // Validation
      if (!formData.restaurant_id || !formData.food_id) {
        setError('Please fill in all required fields (Restaurant and Food)');
        return;
      }

      const variantProblem = variantError();
      if (variantProblem) {
        setError(variantProblem);
        return;
      }

      const variants = completeVariants();
      const submitData = {
        ...formData,
        price: formData.price ? parseFloat(formData.price) : null,
        // '' is the "each" default and the column is nullable — don't send an
        // empty string, which would fail the backend's unit literal.
        price_unit: formData.price_unit || null,
        restaurant_id: parseInt(formData.restaurant_id),
        food_id: parseInt(formData.food_id)
      };

      if (editingMenuItem) {
        // Always send `prices` on update, including [] — that's how a cleared
        // list is expressed. Omitting the key means "leave them alone", which
        // would make removing the last variant impossible from this form.
        await api.put(`/foods/menu/items/${editingMenuItem.id}`, { ...submitData, prices: variants });
        setSuccess('Menu item updated successfully');
      } else {
        // Two requests on purpose. `prices` exists on MenuItemUpdate but NOT on
        // MenuItemCreate, and Pydantic drops unknown fields silently — sending
        // it here would look like success and quietly lose every variant, the
        // same failure #191 documented. So create first, then set the variants
        // on the item we just got back.
        const created = await api.post('/foods/menu/items/', submitData);
        if (variants.length > 0) {
          try {
            await api.put(`/foods/menu/items/${created.id}`, { prices: variants });
          } catch (variantErr) {
            // The item exists and is fine; only the variants failed. Say exactly
            // that, so the fix is "edit it" rather than "create it again".
            setError(`Menu item created, but its size prices could not be saved: ${variantErr.message}. Edit the item to add them.`);
            resetForm();
            fetchMenuItems();
            return;
          }
        }
        setSuccess('Menu item created successfully');
      }
      resetForm();
      fetchMenuItems();
    } catch (err) {
      console.error('Submit error:', err);
      setError(`Failed to save menu item: ${err.message}`);
    }
  };

  const resetForm = () => {
    setFormData({
      restaurant_id: '',
      food_id: '',
      price: '',
      price_unit: '',
      is_available: true,
      custom_name: '',
      custom_description: ''
    });
    setPriceVariants([]);
    setEditingMenuItem(null);
    setShowForm(false);
  };

  const handleEdit = (menuItem) => {
    setEditingMenuItem(menuItem);
    setFormData({
      restaurant_id: menuItem.restaurant_id,
      food_id: menuItem.food_id,
      price: menuItem.price,
      price_unit: menuItem.price_unit || '',
      is_available: menuItem.is_available,
      custom_name: menuItem.custom_name || '',
      custom_description: menuItem.custom_description || ''
    });
    // #151: seed the variant rows so an unrelated edit re-saves them unchanged
    // rather than clearing them (this form always sends `prices` on update).
    setPriceVariants(
      (menuItem.prices || []).map((p) => ({ size_id: String(p.size_id), price: String(p.price) }))
    );
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to remove this menu item?')) {
      try {
        await api.delete(`/foods/menu/items/${id}`);
        setSuccess('Menu item removed successfully');
        fetchMenuItems();
      } catch (err) {
        setError('Failed to remove menu item');
      }
    }
  };

  const getRestaurantName = (menuItem) => {
    // First try to get from nested restaurant object
    if (menuItem.restaurant && menuItem.restaurant.name) {
      return menuItem.restaurant.name;
    }
    // Fallback to finding by ID
    const restaurant = restaurants.find(r => r.id === menuItem.restaurant_id);
    return restaurant ? restaurant.name : 'Unknown Restaurant';
  };

  const getFoodName = (menuItem) => {
    // First try to get from nested food object
    if (menuItem.food && menuItem.food.name) {
      return menuItem.food.name;
    }
    // Fallback to finding by ID
    const food = foods.find(f => f.id === menuItem.food_id);
    return food ? food.name : 'Unknown Food';
  };

  const fetchMenuItemImages = async (menuItemId) => {
    try {
      setImageLoading(true);
      const data = await api.get(`/foods/menu/items/${menuItemId}/images/`);
      setMenuItemImages(data);
    } catch (err) {
      console.error('Failed to fetch menu item images:', err);
      setError('Failed to load images');
    } finally {
      setImageLoading(false);
    }
  };

  const openImageManager = async (menuItem) => {
    setManagingImagesFor(menuItem);
    setShowImageManager(true);
    await fetchMenuItemImages(menuItem.id);
  };

  const closeImageManager = () => {
    setShowImageManager(false);
    setManagingImagesFor(null);
    setMenuItemImages([]);
    setNewImage({
      flickr_url: '',
      caption: '',
      is_primary: false,
      display_order: 0
    });
  };

  const addImage = async () => {
    if (!newImage.flickr_url.trim()) {
      setError('Please enter a Flickr URL');
      return;
    }

    try {
      await api.post(`/foods/menu/items/${managingImagesFor.id}/images/`, newImage);
      setSuccess('Image added successfully');
      setNewImage({
        flickr_url: '',
        caption: '',
        is_primary: false,
        display_order: 0
      });
      await fetchMenuItemImages(managingImagesFor.id);
    } catch (err) {
      console.error('Failed to add image:', err);
      setError('Failed to add image. Maximum 5 images per menu item.');
    }
  };

  const updateImage = async (imageId, updates) => {
    try {
      await api.put(`/foods/menu/images/${imageId}`, updates);
      setSuccess('Image updated successfully');
      await fetchMenuItemImages(managingImagesFor.id);
    } catch (err) {
      console.error('Failed to update image:', err);
      setError('Failed to update image');
    }
  };

  const deleteImage = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      await api.delete(`/foods/menu/images/${imageId}`);
      setSuccess('Image deleted successfully');
      await fetchMenuItemImages(managingImagesFor.id);
    } catch (err) {
      console.error('Failed to delete image:', err);
      setError('Failed to delete image');
    }
  };

  // Secondary Foods Management
  const fetchSecondaryFoods = async (menuItemId) => {
    try {
      setSecondaryFoodsLoading(true);
      const data = await api.get(`/foods/menu/items/${menuItemId}/secondary-foods/`);
      setSecondaryFoods(data);
    } catch (err) {
      console.error('Failed to fetch secondary foods:', err);
      setError('Failed to load secondary foods');
    } finally {
      setSecondaryFoodsLoading(false);
    }
  };

  const openSecondaryFoodsManager = async (menuItem) => {
    setManagingSecondaryFoodsFor(menuItem);
    setShowSecondaryFoodsManager(true);
    await fetchSecondaryFoods(menuItem.id);
  };

  const closeSecondaryFoodsManager = () => {
    setShowSecondaryFoodsManager(false);
    setManagingSecondaryFoodsFor(null);
    setSecondaryFoods([]);
    setSelectedSecondaryFood('');
  };

  const addSecondaryFood = async () => {
    if (!selectedSecondaryFood) {
      setError('Please select a food item to add');
      return;
    }

    try {
      await api.post(`/foods/menu/items/${managingSecondaryFoodsFor.id}/secondary-foods/`, {
        food_id: parseInt(selectedSecondaryFood),
        display_order: 0
      });
      setSuccess('Secondary food added successfully');
      setSelectedSecondaryFood('');
      await fetchSecondaryFoods(managingSecondaryFoodsFor.id);
      // Refresh menu items to show updated data
      await fetchMenuItems();
    } catch (err) {
      console.error('Failed to add secondary food:', err);
      setError('Failed to add secondary food. It may already be associated or is the primary food.');
    }
  };

  const removeSecondaryFood = async (foodId) => {
    if (!window.confirm('Are you sure you want to remove this secondary food?')) return;

    try {
      await api.delete(`/foods/menu/items/${managingSecondaryFoodsFor.id}/secondary-foods/${foodId}`);
      setSuccess('Secondary food removed successfully');
      await fetchSecondaryFoods(managingSecondaryFoodsFor.id);
      // Refresh menu items to show updated data
      await fetchMenuItems();
    } catch (err) {
      console.error('Failed to remove secondary food:', err);
      setError('Failed to remove secondary food');
    }
  };

  // Filter menu items based on selected filters
  const filteredMenuItems = menuItems.filter(menuItem => {
    const matchesRestaurant = !restaurantFilter || menuItem.restaurant_id === parseInt(restaurantFilter);
    const matchesFood = !foodFilter || menuItem.food_id === parseInt(foodFilter);
    return matchesRestaurant && matchesFood;
  });

  // #151: what the backend will store as the representative price once this form
  // is saved — the cheapest complete variant, or null when there are none (in
  // which case the Price field stays a normal editable input). Computed here so
  // the form shows the same number the server is about to derive, rather than
  // letting the admin discover it after saving.
  const completedForPreview = completeVariants();
  const derivedPrice = completedForPreview.length > 0
    ? Math.min(...completedForPreview.map((v) => v.price))
    : null;

  return (
    <div className="section">
      <div className="section-header">
        <h2>Menu Items</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="btn btn-primary"
        >
          <Plus size={16} />
          Add Menu Item
        </button>
      </div>

      {error && <ErrorMessage message={error} onDismiss={() => setError('')} />}
      {success && <SuccessMessage message={success} onDismiss={() => setSuccess('')} />}

      {showForm && (
        <div className="form-container">
          <h3>{editingMenuItem ? 'Edit Menu Item' : 'Add New Menu Item'}</h3>
          <div className="form">
            <div className="form-grid">
              <AutocompleteSelect
                items={restaurants}
                selectedId={formData.restaurant_id}
                onSelect={(id) => setFormData({ ...formData, restaurant_id: id })}
                placeholder="Type to search restaurants..."
                label="Restaurant"
                getDisplayText={withVisibilityTag}
                getSecondaryText={(restaurant) => {
                  const mainLocation = restaurant.locations?.find(loc => loc.is_main_location) || restaurant.locations?.[0];
                  return mainLocation ? `${mainLocation.city}${mainLocation.state ? `, ${mainLocation.state}` : ''}` : '';
                }}
                required={true}
              />
              <AutocompleteSelect
                items={foods}
                selectedId={formData.food_id}
                onSelect={(id) => handleFoodSelected(id)}
                placeholder="Type to search food items..."
                label="Food Item"
                getDisplayText={(food) => food.name}
                getSecondaryText={(food) => food.description || (food.categories?.length > 0 ? food.categories.map(c => c.name).join(', ') : '')}
                required={true}
              />
              <div className="form-group">
                <label>Price ($) - optional</label>
                <input
                  type="number"
                  step="0.01"
                  value={derivedPrice !== null ? derivedPrice : formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  placeholder="Leave empty to use food's base price"
                  // #151: once there are size variants this field is DERIVED —
                  // the backend overwrites it with the cheapest variant on save,
                  // so an editable box here would just be a lie the admin typed.
                  disabled={derivedPrice !== null}
                />
                {derivedPrice !== null && (
                  <small className="text-muted">
                    Set from the cheapest size below. Listings show &ldquo;from ${derivedPrice.toFixed(2)}&rdquo;.
                  </small>
                )}
              </div>
            </div>

            {/* #151: priced per what — separate from size. A latte is "each"
                with 12oz/16oz sizes; brisket is "lb" with no sizes at all. */}
            <div className="form-grid">
              <div className="form-group">
                <label>Price unit - optional</label>
                <select
                  value={formData.price_unit}
                  onChange={(e) => setFormData({ ...formData, price_unit: e.target.value })}
                >
                  <option value="">Each (default)</option>
                  {PRICE_UNIT_OPTIONS.map((u) => (
                    <option key={u.value} value={u.value}>{u.label}</option>
                  ))}
                </select>
                <small className="text-muted">
                  For items sold by weight or head — brisket at $18.99/lb, family style at $25/person.
                </small>
              </div>
            </div>

            {/* #151: size variants. Separate from the unit above: this answers
                WHICH SIZE, that one answers PRICED PER WHAT. */}
            <div className="form-group">
              <label>Sizes &amp; prices - optional</label>
              {priceVariants.map((variant, index) => (
                <div key={index} className="form-grid" style={{ marginBottom: '0.5rem' }}>
                  <select
                    value={variant.size_id}
                    onChange={(e) => {
                      const next = [...priceVariants];
                      next[index] = { ...next[index], size_id: e.target.value };
                      setPriceVariants(next);
                    }}
                  >
                    <option value="">Select a size…</option>
                    {/* #209: the food's template leads, everything else follows.
                        Grouped rather than filtered — the template is a
                        suggestion, not a constraint, so a restaurant selling a
                        24oz Drip must still be able to say so without anyone
                        editing the catalog first. Before this the template did
                        the work of pre-seeding a NEW item and then contributed
                        nothing when a row was added by hand. */}
                    {sizeGroupsForSelectedFood().map((group) => (
                      group.sizes.length === 0 ? null : group.label ? (
                        <optgroup key={group.label} label={group.label}>
                          {group.sizes.map((size) => renderSizeOption(size, index))}
                        </optgroup>
                      ) : (
                        <React.Fragment key="ungrouped">
                          {group.sizes.map((size) => renderSizeOption(size, index))}
                        </React.Fragment>
                      )
                    ))}
                  </select>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={variant.price}
                    onChange={(e) => {
                      const next = [...priceVariants];
                      next[index] = { ...next[index], price: e.target.value };
                      setPriceVariants(next);
                    }}
                    placeholder="Price for this size"
                  />
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setPriceVariants(priceVariants.filter((_, i) => i !== index))}
                  >
                    <X size={16} />
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setPriceVariants([...priceVariants, { size_id: '', price: '' }])}
                disabled={menuItemSizes.length > 0 && priceVariants.length >= menuItemSizes.length}
              >
                <Plus size={16} />
                Add a size
              </button>
              <small className="text-muted">
                Only for items that come in more than one size — a 12oz / 16oz / 20oz latte. Leave
                empty for a single-price dish and use the Price field above.
              </small>
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label>Custom Name (optional)</label>
                <input
                  type="text"
                  value={formData.custom_name}
                  onChange={(e) => setFormData({ ...formData, custom_name: e.target.value })}
                  placeholder="e.g., House Special Cortado"
                />
              </div>
              <div className="form-group main-location-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_available}
                    onChange={(e) => setFormData({ ...formData, is_available: e.target.checked })}
                  />
                  Available
                </label>
              </div>
            </div>

            <div className="form-group">
              <label>Custom Description (optional)</label>
              <textarea
                value={formData.custom_description}
                onChange={(e) => setFormData({ ...formData, custom_description: e.target.value })}
                rows="3"
                placeholder="Restaurant-specific description or notes"
              />
              <small className="text-muted">
                Supports links and <strong>bold</strong> — paste a URL, or use <code>[label](https://…)</code> and <code>**bold**</code>.
              </small>
            </div>

            <div className="form-actions">
              <button onClick={handleSubmit} className="btn btn-success">
                <Save size={16} />
                Save
              </button>
              <button onClick={resetForm} className="btn btn-secondary">
                <X size={16} />
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Management Modal */}
      {showImageManager && managingImagesFor && (
        <div className="form-container">
          <div className="modal-header">
            <h3>Manage Images - {getFoodName(managingImagesFor)} at {getRestaurantName(managingImagesFor)}</h3>
            <button onClick={closeImageManager} className="btn-icon btn-secondary">
              <X size={16} />
            </button>
          </div>

          {/* Add New Image */}
          <div className="image-add-section">
            <h4>Add New Image {menuItemImages.length >= 5 && <span className="text-secondary">(Maximum reached)</span>}</h4>
            {menuItemImages.length < 5 && (
              <div className="form">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Flickr URL *</label>
                    <input
                      type="url"
                      value={newImage.flickr_url}
                      onChange={(e) => setNewImage({ ...newImage, flickr_url: e.target.value })}
                      placeholder="https://www.flickr.com/photos/user/12345/"
                    />
                  </div>
                  <div className="form-group">
                    <label>Caption</label>
                    <input
                      type="text"
                      value={newImage.caption}
                      onChange={(e) => setNewImage({ ...newImage, caption: e.target.value })}
                      placeholder="Image description (optional)"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={newImage.is_primary}
                      onChange={(e) => setNewImage({ ...newImage, is_primary: e.target.checked })}
                    />
                    Set as primary image
                  </label>
                </div>
                <button onClick={addImage} className="btn btn-primary">
                  <Plus size={16} />
                  Add Image
                </button>
              </div>
            )}
          </div>

          {/* Existing Images */}
          <div className="existing-images-section">
            <h4>Existing Images ({menuItemImages.length}/5)</h4>
            {imageLoading ? (
              <div className="loading-menu">Loading images...</div>
            ) : menuItemImages.length > 0 ? (
              <div className="images-grid">
                {menuItemImages.map((image, index) => (
                  <div key={image.id} className="image-card">
                    <div className="image-info">
                      <div className="image-header">
                        <strong>Image {index + 1}</strong>
                        {image.is_primary && <span className="primary-badge">Primary</span>}
                      </div>
                      <div className="image-url">
                        <a href={image.flickr_url} target="_blank" rel="noopener noreferrer">
                          {image.flickr_url}
                        </a>
                      </div>
                      {image.caption && (
                        <div className="image-caption">"{image.caption}"</div>
                      )}
                    </div>
                    <div className="image-actions">
                      {!image.is_primary && (
                        <button
                          onClick={() => updateImage(image.id, { is_primary: true })}
                          className="btn btn-outline btn-small"
                        >
                          Set Primary
                        </button>
                      )}
                      <button
                        onClick={() => deleteImage(image.id)}
                        className="btn-icon btn-delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-images">No images added yet</div>
            )}
          </div>
        </div>
      )}

      {/* Secondary Foods Management Modal */}
      {showSecondaryFoodsManager && managingSecondaryFoodsFor && (
        <div className="form-container">
          <div className="modal-header">
            <h3>Manage Secondary Foods - {getFoodName(managingSecondaryFoodsFor)} at {getRestaurantName(managingSecondaryFoodsFor)}</h3>
            <button onClick={closeSecondaryFoodsManager} className="btn-icon btn-secondary">
              <X size={16} />
            </button>
          </div>

          <div className="info-box">
            <p><strong>Primary Food:</strong> {getFoodName(managingSecondaryFoodsFor)}</p>
            <p className="text-secondary">Add secondary foods to indicate that this menu item includes multiple food items (e.g., a combo or a photo showing multiple items together).</p>
          </div>

          {/* Add Secondary Food */}
          <div className="image-add-section">
            <h4>Add Secondary Food</h4>
            <div className="form">
              <div className="form-group">
                <label>Select Food Item *</label>
                <select
                  value={selectedSecondaryFood}
                  onChange={(e) => setSelectedSecondaryFood(e.target.value)}
                  style={{ width: '100%', padding: '0.5rem' }}
                >
                  <option value="">-- Select a food item --</option>
                  {foods
                    .filter(food => {
                      // Exclude primary food and already added secondary foods
                      if (food.id === managingSecondaryFoodsFor.food_id) return false;
                      if (secondaryFoods.some(sf => sf.id === food.id)) return false;
                      return true;
                    })
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(food => (
                      <option key={food.id} value={food.id}>
                        {food.name}
                        {food.categories && food.categories.length > 0 ? ` (${food.categories.map(c => c.name).join(', ')})` : ''}
                      </option>
                    ))}
                </select>
              </div>
              <button onClick={addSecondaryFood} className="btn btn-primary">
                <Plus size={16} />
                Add Secondary Food
              </button>
            </div>
          </div>

          {/* Existing Secondary Foods */}
          <div className="existing-images-section">
            <h4>Current Secondary Foods ({secondaryFoods.length})</h4>
            {secondaryFoodsLoading ? (
              <div className="loading-menu">Loading secondary foods...</div>
            ) : secondaryFoods.length > 0 ? (
              <div className="images-grid">
                {secondaryFoods.map((food) => (
                  <div key={food.id} className="image-card">
                    <div className="image-info">
                      <div className="image-header">
                        <strong>{food.name}</strong>
                      </div>
                      {food.description && (
                        <div className="image-caption">{food.description}</div>
                      )}
                      {food.categories && food.categories.length > 0 && (
                        <div className="text-secondary small">
                          Categories: {food.categories.map(c => c.name).join(', ')}
                        </div>
                      )}
                    </div>
                    <div className="image-actions">
                      <button
                        onClick={() => removeSecondaryFood(food.id)}
                        className="btn-icon btn-delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-images">No secondary foods added yet. The primary food is {getFoodName(managingSecondaryFoodsFor)}.</div>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>
                  <select
                    value={restaurantFilter}
                    onChange={(e) => setRestaurantFilter(e.target.value)}
                    style={{ width: '100%', padding: '0.25rem', fontSize: '0.9rem' }}
                  >
                    <option value="">All Restaurants</option>
                    {[...restaurants].sort((a, b) => a.name.localeCompare(b.name)).map(restaurant => (
                      <option key={restaurant.id} value={restaurant.id}>
                        {withVisibilityTag(restaurant)}
                      </option>
                    ))}
                  </select>
                </th>
                <th>
                  <select
                    value={foodFilter}
                    onChange={(e) => setFoodFilter(e.target.value)}
                    style={{ width: '100%', padding: '0.25rem', fontSize: '0.9rem' }}
                  >
                    <option value="">All Food Items</option>
                    {[...foods].sort((a, b) => a.name.localeCompare(b.name)).map(food => (
                      <option key={food.id} value={food.id}>
                        {food.name}
                      </option>
                    ))}
                  </select>
                </th>
                <th>Display Name</th>
                <th>Price</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredMenuItems.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                    {(restaurantFilter || foodFilter)
                      ? 'No menu items match the selected filters'
                      : 'No menu items found'}
                  </td>
                </tr>
              ) : (
                filteredMenuItems.map((menuItem) => (
                  <tr key={menuItem.id}>
                    <td className="font-medium">
                      {getRestaurantName(menuItem)}
                      {(() => {
                        // #345: a private restaurant's items are never shown unmarked.
                        const tag = restaurantVisibilityTag(restaurants.find(r => r.id === menuItem.restaurant_id));
                        return tag && (
                          <span title={`This restaurant is ${tag} — not visible to diners`}
                                style={{ marginLeft: '6px', fontSize: '12px', fontWeight: 'normal', color: '#6e7781', whiteSpace: 'nowrap' }}>
                            <Lock size={12} style={{ verticalAlign: '-1px', marginRight: '3px' }} />
                            {tag}
                          </span>
                        );
                      })()}
                    </td>
                    <td>{getFoodName(menuItem)}</td>
                    <td>
                      {menuItem.custom_name || getFoodName(menuItem)}
                      {menuItem.secondary_foods && menuItem.secondary_foods.length > 0 && (
                        <div className="text-secondary small">
                          + {menuItem.secondary_foods.map(sf => sf.name).join(', ')}
                        </div>
                      )}
                      {menuItem.custom_description && (
                        <div className="text-secondary">{menuItem.custom_description}</div>
                      )}
                    </td>
                    <td>
                      {/* #151: a variant item reads "from $X", matching what the
                          customer-facing surfaces show, since price is derived
                          as the cheapest size.
                          #211: the threshold is > 1, not > 0. ONE size is a
                          price, not a floor — "from $4.99" over a single 12 oz
                          claims pricier options that don't exist. Backend, web
                          and iOS all use > 1; this line was the odd one out.
                          The size list below deliberately still shows at > 0,
                          so a single size is still named rather than lost. */}
                      {menuItem.price != null
                        ? `${menuItem.prices?.length > 1 ? 'from ' : ''}$${menuItem.price}${PRICE_UNIT_SUFFIX[menuItem.price_unit] || ''}`
                        : (menuItem.food?.base_price
                            ? `$${menuItem.food.base_price} (base)`
                            : 'No price set')
                      }
                      {menuItem.prices?.length > 0 && (
                        <div className="text-secondary small">
                          {menuItem.prices
                            .map((p) => `${p.size_display_name} $${p.price}`)
                            .join(' · ')}
                        </div>
                      )}
                    </td>
                    <td>
                      <span className={`status-badge ${menuItem.is_available ? 'available' : 'unavailable'}`}>
                        {menuItem.is_available ? 'Available' : 'Unavailable'}
                      </span>
                    </td>
                    <td className="actions">
                      <button
                        onClick={() => openImageManager(menuItem)}
                        className="btn-icon btn-secondary"
                        title="Manage Images"
                      >
                        📷
                      </button>
                      <button
                        onClick={() => openSecondaryFoodsManager(menuItem)}
                        className="btn-icon btn-secondary"
                        title="Manage Secondary Foods"
                      >
                        🍽️
                      </button>
                      <button
                        onClick={() => handleEdit(menuItem)}
                        className="btn-icon btn-edit"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(menuItem.id)}
                        className="btn-icon btn-delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Restaurant Images Management
const RestaurantImages = () => {
  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [locations, setLocations] = useState([]);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imageLoading, setImageLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingImage, setEditingImage] = useState(null);
  const [newImage, setNewImage] = useState({
    flickr_url: '',
    caption: '',
    image_type: 'other',
    location_id: null,
    is_primary: false,
    display_order: 0
  });

  useEffect(() => {
    fetchRestaurants();
  }, []);

  const fetchRestaurants = async () => {
    try {
      setLoading(true);
      // #345: operator read — private restaurants have images to manage too.
      const data = await api.get('/restaurants/?include_pending=true&limit=10000');
      // Sort restaurants alphabetically by name
      const sortedData = data.sort((a, b) => a.name.localeCompare(b.name));
      setRestaurants(sortedData);
    } catch (err) {
      setError('Failed to fetch restaurants');
    } finally {
      setLoading(false);
    }
  };

  const fetchRestaurantImages = async (restaurantId) => {
    try {
      setImageLoading(true);
      const data = await api.get(`/restaurants/${restaurantId}/images`);
      setImages(data.images || []);
    } catch (err) {
      console.error('Failed to fetch restaurant images:', err);
      setError('Failed to load images');
      setImages([]);
    } finally {
      setImageLoading(false);
    }
  };

  const fetchRestaurantLocations = async (restaurantId) => {
    try {
      const data = await api.get(`/restaurants/${restaurantId}/locations/`);
      setLocations(data || []);
    } catch (err) {
      console.error('Failed to fetch restaurant locations:', err);
      setError('Failed to load locations');
      setLocations([]);
    }
  };

  const handleSelectRestaurant = async (restaurant) => {
    setSelectedRestaurant(restaurant);
    await Promise.all([
      fetchRestaurantImages(restaurant.id),
      fetchRestaurantLocations(restaurant.id)
    ]);
  };

  const handleAddImage = async () => {
    if (!newImage.flickr_url.trim()) {
      setError('Please enter a Flickr URL');
      return;
    }

    try {
      // Convert location_id to null if empty string or "global"
      const imageData = {
        ...newImage,
        location_id: newImage.location_id === '' || newImage.location_id === 'global' ? null : parseInt(newImage.location_id)
      };
      await api.post(`/restaurants/${selectedRestaurant.id}/images`, imageData);
      setSuccess('Image added successfully');
      setNewImage({
        flickr_url: '',
        caption: '',
        image_type: 'other',
        location_id: null,
        is_primary: false,
        display_order: 0
      });
      await fetchRestaurantImages(selectedRestaurant.id);
    } catch (err) {
      console.error('Failed to add image:', err);
      setError('Failed to add image');
    }
  };

  const handleUpdateImage = async (imageId, updates) => {
    try {
      await api.put(`/restaurants/images/${imageId}`, updates);
      setSuccess('Image updated successfully');
      setEditingImage(null);
      await fetchRestaurantImages(selectedRestaurant.id);
    } catch (err) {
      console.error('Failed to update image:', err);
      setError('Failed to update image');
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      await api.delete(`/restaurants/images/${imageId}`);
      setSuccess('Image deleted successfully');
      await fetchRestaurantImages(selectedRestaurant.id);
    } catch (err) {
      console.error('Failed to delete image:', err);
      setError('Failed to delete image');
    }
  };

  return (
    <div className="section">
      <div className="section-header">
        <h2>Restaurant Images</h2>
        <button onClick={fetchRestaurants} className="btn btn-outline">
          <RefreshCw size={16} />
          Refresh List
        </button>
      </div>

      {error && <ErrorMessage message={error} onDismiss={() => setError('')} />}
      {success && <SuccessMessage message={success} onDismiss={() => setSuccess('')} />}

      {loading ? (
        <LoadingSpinner />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '20px' }}>
          {/* Restaurant List */}
          <div>
            <h3 style={{ marginBottom: '10px' }}>Select Restaurant</h3>
            <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
              {restaurants.map((restaurant) => (
                <div
                  key={restaurant.id}
                  onClick={() => handleSelectRestaurant(restaurant)}
                  style={{
                    padding: '10px',
                    marginBottom: '5px',
                    background: selectedRestaurant?.id === restaurant.id ? '#e3f2fd' : '#f5f5f5',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    border: selectedRestaurant?.id === restaurant.id ? '2px solid #2196f3' : '1px solid #ddd'
                  }}
                >
                  <strong>{restaurant.name}</strong>
                  {restaurantVisibilityTag(restaurant) && (
                    <span style={{ marginLeft: '6px', fontSize: '12px', color: '#6e7781' }}>
                      <Lock size={12} style={{ verticalAlign: '-1px', marginRight: '3px' }} />
                      {restaurantVisibilityTag(restaurant)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Images Panel */}
          <div>
            {selectedRestaurant ? (
              <>
                <h3>{selectedRestaurant.name} - Images ({images.length})</h3>

                {/* Add New Image Form */}
                <div className="form-container" style={{ marginTop: '15px', marginBottom: '20px' }}>
                  <h4>Add New Image</h4>
                  <div className="form-grid">
                    <div>
                      <label>Flickr URL *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={newImage.flickr_url}
                        onChange={(e) => setNewImage({ ...newImage, flickr_url: e.target.value })}
                        placeholder="https://www.flickr.com/photos/user/12345/"
                      />
                    </div>
                    <div>
                      <label>Caption</label>
                      <input
                        type="text"
                        className="form-control"
                        value={newImage.caption}
                        onChange={(e) => setNewImage({ ...newImage, caption: e.target.value })}
                      />
                    </div>
                    <div>
                      <label>Image Type</label>
                      <select
                        className="form-control"
                        value={newImage.image_type}
                        onChange={(e) => setNewImage({ ...newImage, image_type: e.target.value })}
                      >
                        <option value="exterior">Exterior</option>
                        <option value="interior">Interior</option>
                        <option value="ambiance">Ambiance</option>
                        <option value="logo">Logo</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label>Location (Optional)</label>
                      <select
                        className="form-control"
                        value={newImage.location_id || 'global'}
                        onChange={(e) => setNewImage({ ...newImage, location_id: e.target.value === 'global' ? null : e.target.value })}
                      >
                        <option value="global">Global (All Locations)</option>
                        {locations.map((location) => (
                          <option key={location.id} value={location.id}>
                            {location.name ? `${location.name} - ${location.address}, ${location.city}` : `${location.address}, ${location.city}`}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label>
                        <input
                          type="checkbox"
                          checked={newImage.is_primary}
                          onChange={(e) => setNewImage({ ...newImage, is_primary: e.target.checked })}
                        />
                        {' '}Primary Image
                      </label>
                    </div>
                  </div>
                  <button onClick={handleAddImage} className="btn btn-primary" style={{ marginTop: '10px' }}>
                    Add Image
                  </button>
                </div>

                {/* Images List */}
                {imageLoading ? (
                  <LoadingSpinner />
                ) : images.length === 0 ? (
                  <p>No images for this restaurant yet.</p>
                ) : (
                  <div style={{ display: 'grid', gap: '15px' }}>
                    {images.map((image) => (
                      <div key={image.id} style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '15px', background: '#f9f9f9' }}>
                        {editingImage?.id === image.id ? (
                          // Edit Mode
                          <div>
                            <div className="form-grid">
                              <div>
                                <label>Caption</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={editingImage.caption}
                                  onChange={(e) => setEditingImage({ ...editingImage, caption: e.target.value })}
                                />
                              </div>
                              <div>
                                <label>Image Type</label>
                                <select
                                  className="form-control"
                                  value={editingImage.image_type}
                                  onChange={(e) => setEditingImage({ ...editingImage, image_type: e.target.value })}
                                >
                                  <option value="exterior">Exterior</option>
                                  <option value="interior">Interior</option>
                                  <option value="ambiance">Ambiance</option>
                                  <option value="logo">Logo</option>
                                  <option value="other">Other</option>
                                </select>
                              </div>
                              <div>
                                <label>Location (Optional)</label>
                                <select
                                  className="form-control"
                                  value={editingImage.location_id || 'global'}
                                  onChange={(e) => setEditingImage({ ...editingImage, location_id: e.target.value === 'global' ? null : parseInt(e.target.value) })}
                                >
                                  <option value="global">Global (All Locations)</option>
                                  {locations.map((location) => (
                                    <option key={location.id} value={location.id}>
                                      {location.name ? `${location.name} - ${location.address}, ${location.city}` : `${location.address}, ${location.city}`}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label>Display Order</label>
                                <input
                                  type="number"
                                  className="form-control"
                                  value={editingImage.display_order}
                                  onChange={(e) => setEditingImage({ ...editingImage, display_order: parseInt(e.target.value) })}
                                />
                              </div>
                              <div>
                                <label>
                                  <input
                                    type="checkbox"
                                    checked={editingImage.is_primary}
                                    onChange={(e) => setEditingImage({ ...editingImage, is_primary: e.target.checked })}
                                  />
                                  {' '}Primary Image
                                </label>
                              </div>
                            </div>
                            <div style={{ marginTop: '10px' }}>
                              <button
                                onClick={() => handleUpdateImage(image.id, editingImage)}
                                className="btn btn-success btn-sm"
                                style={{ marginRight: '5px' }}
                              >
                                <Save size={14} /> Save
                              </button>
                              <button
                                onClick={() => setEditingImage(null)}
                                className="btn btn-secondary btn-sm"
                              >
                                <X size={14} /> Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          // View Mode
                          <div style={{ display: 'flex', gap: '15px' }}>
                            <div style={{ flex: '0 0 150px' }}>
                              <img
                                src={image.flickr_url}
                                alt={image.caption || 'Restaurant image'}
                                style={{ width: '100%', height: 'auto', borderRadius: '4px' }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            </div>
                            <div style={{ flex: 1 }}>
                              <div><strong>Caption:</strong> {image.caption || '(none)'}</div>
                              <div><strong>Type:</strong> {image.image_type}</div>
                              <div><strong>Location:</strong> {image.location_id ?
                                (() => {
                                  const loc = locations.find(l => l.id === image.location_id);
                                  return loc ? (loc.name || `${loc.address}, ${loc.city}`) : 'Unknown';
                                })()
                                : 'Global (All Locations)'}
                              </div>
                              <div><strong>Primary:</strong> {image.is_primary ? 'Yes' : 'No'}</div>
                              <div><strong>Display Order:</strong> {image.display_order}</div>
                              <div style={{ marginTop: '10px' }}>
                                <a href={image.flickr_url} target="_blank" rel="noopener noreferrer" className="btn btn-outline btn-sm" style={{ marginRight: '5px' }}>
                                  <ExternalLink size={14} /> View on Flickr
                                </a>
                                <button
                                  onClick={() => setEditingImage(image)}
                                  className="btn btn-outline btn-sm"
                                  style={{ marginRight: '5px' }}
                                >
                                  <Edit size={14} /> Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteImage(image.id)}
                                  className="btn btn-danger btn-sm"
                                >
                                  <Trash2 size={14} /> Delete
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                <p>Select a restaurant from the list to manage its images</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Foods Management
const Foods = () => {
  const [foods, setFoods] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cuisines, setCuisines] = useState([]);
  // #209: the size vocabulary, for the "typical sizes" template picker.
  const [sizes, setSizes] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingFood, setEditingFood] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [similarFoods, setSimilarFoods] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    base_price: '',
    preparation_time_minutes: '',
    calories: '',
    is_vegetarian: false,
    is_vegan: false,
    is_gluten_free: false,
    category_ids: [],
    cuisine_ids: [],
    // #209: which sizes this dish typically comes in, and what it's priced by.
    default_price_unit: '',
    size_ids: []
  });

  useEffect(() => {
    fetchFoods();
    fetchCategories();
    fetchCuisines();
    fetchMenuItems();
    fetchSizes();
  }, []);

  // Check for similar foods when name changes
  useEffect(() => {
    if (formData.name && formData.name.trim().length >= 2) {
      const similar = findSimilarItems(formData.name, foods, editingFood?.id);
      setSimilarFoods(similar);
    } else {
      setSimilarFoods([]);
    }
  }, [formData.name, foods, editingFood]);

  const fetchFoods = async () => {
    try {
      setLoading(true);
      const data = await api.get('/foods/?limit=10000');
      // Sort foods alphabetically by name
      const sortedData = data.sort((a, b) => a.name.localeCompare(b.name));
      setFoods(sortedData);
    } catch (err) {
      setError('Failed to fetch foods');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const data = await api.get('/foods/categories/');
      // Filter out CLOSED category - now using is_closed field on locations
      const filteredData = data.filter(cat => cat.name.toUpperCase() !== 'CLOSED');
      // Sort categories alphabetically by name
      const sortedData = filteredData.sort((a, b) => a.name.localeCompare(b.name));
      setCategories(sortedData);
    } catch (err) {
      console.error('Failed to fetch food categories');
    }
  };

  // #209: already in vocabulary order from the server, and active-only.
  const fetchSizes = async () => {
    try {
      setSizes(await api.get('/api/v1/sizes/'));
    } catch (err) {
      console.error('Failed to fetch sizes');
    }
  };

  const fetchCuisines = async () => {
    try {
      const data = await api.get('/cuisines/');
      // Sort cuisines alphabetically by name
      const sortedData = data.sort((a, b) => a.name.localeCompare(b.name));
      setCuisines(sortedData);
    } catch (err) {
      console.error('Failed to fetch cuisines');
    }
  };

  const fetchMenuItems = async () => {
    try {
      const data = await api.get('/foods/menu/items/?include_pending=true&limit=10000');
      // Sort menu items alphabetically by food name (custom_name or food.name)
      const sortedData = data.sort((a, b) => {
        const nameA = a.custom_name || a.food?.name || '';
        const nameB = b.custom_name || b.food?.name || '';
        return nameA.localeCompare(nameB);
      });
      setMenuItems(sortedData);
    } catch (err) {
      console.error('Failed to fetch menu items');
    }
  };

  const isOrphaned = (foodId) => {
    return !menuItems.some(menuItem => menuItem.food_id === foodId);
  };

  const handleSubmit = async () => {
    try {
      const submitData = {
        ...formData,
        description: formData.description?.trim() || null,
        base_price: formData.base_price ? parseFloat(formData.base_price) : null,
        preparation_time_minutes: formData.preparation_time_minutes ? parseInt(formData.preparation_time_minutes) : null,
        calories: formData.calories ? parseInt(formData.calories) : null,
        // #209: 'each' is the default and is stored as null, not as a string.
        default_price_unit: formData.default_price_unit || null
      };

      if (editingFood) {
        await api.put(`/foods/${editingFood.id}`, submitData);
        setSuccess('Food updated successfully');
      } else {
        await api.post('/foods/', submitData);
        setSuccess('Food created successfully');
      }
      resetForm();
      fetchFoods();
    } catch (err) {
      // Show specific error message from backend, or generic fallback
      if (err.message.includes('already exists')) {
        setError(`Cannot add "${formData.name}" - this food item already exists in the database. Please use a different name or update the existing item.`);
      } else {
        setError(err.message || 'Failed to save food');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      base_price: '',
      preparation_time_minutes: '',
      calories: '',
      is_vegetarian: false,
      is_vegan: false,
      is_gluten_free: false,
      category_ids: [],
      cuisine_ids: [],
      default_price_unit: '',
      size_ids: []
    });
    setEditingFood(null);
    setShowForm(false);
  };

  const handleEdit = (food) => {
    setEditingFood(food);
    setFormData({
      name: food.name,
      description: food.description || '',
      base_price: food.base_price || '',
      preparation_time_minutes: food.preparation_time_minutes || '',
      calories: food.calories || '',
      is_vegetarian: food.is_vegetarian,
      is_vegan: food.is_vegan,
      is_gluten_free: food.is_gluten_free,
      category_ids: food.categories.map(c => c.id),
      cuisine_ids: food.cuisines ? food.cuisines.map(c => c.id) : [],
      default_price_unit: food.default_price_unit || '',
      // #209: MUST be seeded. handleSubmit spreads formData, so size_ids is
      // always sent — and the backend treats a present [] as "clear it". Leaving
      // this unseeded would wipe the template on any unrelated edit.
      size_ids: food.sizes ? food.sizes.map(s => s.id) : []
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this food?')) {
      try {
        await api.delete(`/foods/${id}`);
        setSuccess('Food deleted successfully');
        fetchFoods();
      } catch (err) {
        setError('Failed to delete food');
      }
    }
  };

  return (
    <div className="section">
      <div className="section-header">
        <h2>Foods</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="btn btn-primary"
        >
          <Plus size={16} />
          Add Food
        </button>
      </div>

      {error && <ErrorMessage message={error} onDismiss={() => setError('')} />}
      {success && <SuccessMessage message={success} onDismiss={() => setSuccess('')} />}

      {showForm && (
        <div className="form-container">
          <h3>{editingFood ? 'Edit Food' : 'Add New Food'}</h3>
          <div className="form">
            <div className="form-grid">
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
                <DuplicateWarning items={similarFoods} fieldName="food" />
              </div>
              <div className="form-group">
                <label>Categories</label>
                <select
                  multiple
                  value={formData.category_ids}
                  onChange={(e) => setFormData({ ...formData, category_ids: Array.from(e.target.selectedOptions, option => parseInt(option.value)) })}
                >
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>{category.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Cuisines</label>
                <select
                  multiple
                  value={formData.cuisine_ids}
                  onChange={(e) => setFormData({ ...formData, cuisine_ids: Array.from(e.target.selectedOptions, option => parseInt(option.value)) })}
                >
                  {cuisines.map(cuisine => (
                    <option key={cuisine.id} value={cuisine.id}>{cuisine.name}</option>
                  ))}
                </select>
              </div>
              {/* #209: the size TEMPLATE — which sizes this dish comes in,
                  independent of what any restaurant charges. Prices never live
                  on a Food; a catalog dish has no universal price. */}
              <div className="form-group">
                <label>Typical sizes - optional</label>
                <select
                  multiple
                  value={formData.size_ids}
                  onChange={(e) => setFormData({ ...formData, size_ids: Array.from(e.target.selectedOptions, option => parseInt(option.value)) })}
                >
                  {sizes.map(size => (
                    <option key={size.id} value={size.id}>{size.display_name}</option>
                  ))}
                </select>
                <small className="text-muted">
                  Pre-fills the size rows when this dish is added to a restaurant's menu —
                  a Drip that comes in 12/16/20oz only needs saying once. A restaurant can
                  still price a size that isn't listed here. Manage the list under Foods &rarr; Sizes.
                </small>
              </div>
              <div className="form-group">
                <label>Priced per - optional</label>
                <select
                  value={formData.default_price_unit}
                  onChange={(e) => setFormData({ ...formData, default_price_unit: e.target.value })}
                >
                  <option value="">Each (default)</option>
                  {PRICE_UNIT_OPTIONS.map((u) => (
                    <option key={u.value} value={u.value}>{u.label}</option>
                  ))}
                </select>
                <small className="text-muted">
                  For dishes sold by a continuous measure wherever you buy them — brisket at
                  $18.99/lb. A discrete choice like "1 lb" is a size, not a unit.
                </small>
              </div>
              <div className="form-group">
                <label>Base Price ($)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.base_price}
                  onChange={(e) => setFormData({ ...formData, base_price: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Prep Time (minutes)</label>
                <input
                  type="number"
                  value={formData.preparation_time_minutes}
                  onChange={(e) => setFormData({ ...formData, preparation_time_minutes: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Calories</label>
                <input
                  type="number"
                  value={formData.calories}
                  onChange={(e) => setFormData({ ...formData, calories: e.target.value })}
                />
              </div>
            </div>
            
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows="3"
              />
              <small className="text-muted">
                Supports links and <strong>bold</strong> — paste a URL, or use <code>[label](https://…)</code> and <code>**bold**</code>.
              </small>
            </div>

            <div className="checkbox-group">
              <label>Dietary Preferences</label>
              <div className="checkbox-row">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_vegetarian}
                    onChange={(e) => setFormData({ ...formData, is_vegetarian: e.target.checked })}
                  />
                  Vegetarian
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_vegan}
                    onChange={(e) => setFormData({ ...formData, is_vegan: e.target.checked })}
                  />
                  Vegan
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_gluten_free}
                    onChange={(e) => setFormData({ ...formData, is_gluten_free: e.target.checked })}
                  />
                  Gluten Free
                </label>
              </div>
            </div>

            <div className="form-actions">
              <button onClick={handleSubmit} className="btn btn-success">
                <Save size={16} />
                Save
              </button>
              <button onClick={resetForm} className="btn btn-secondary">
                <X size={16} />
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Categories</th>
                <th>Price</th>
                <th>Details</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {foods.map((food) => (
                <tr key={food.id}>
                  <td>
                    <div>
                      <div className="font-medium">{food.name}</div>
                      <div className="text-secondary">{food.description || <em style={{opacity: 0.5}}>No description</em>}</div>
                    </div>
                  </td>
                  <td>{food.categories.map(cat => cat.name).join(', ') || 'No categories'}</td>
                  <td>{food.base_price ? `$${food.base_price}` : 'N/A'}</td>
                  <td>
                    <div className="food-details">
                      {food.preparation_time_minutes && (
                        <div className="detail-item">
                          <Clock size={12} />
                          {food.preparation_time_minutes} min
                        </div>
                      )}
                      {food.calories && (
                        <div>{food.calories} cal</div>
                      )}
                      <div className="dietary-tags">
                        {food.is_vegetarian && <span className="tag tag-vegetarian">V</span>}
                        {food.is_vegan && <span className="tag tag-vegan">VG</span>}
                        {food.is_gluten_free && <span className="tag tag-gluten-free">GF</span>}
                      </div>
                    </div>
                  </td>
                  <td>
                    {isOrphaned(food.id) ? (
                      <span className="status-badge unavailable" title="Not linked to any restaurant">
                        Orphaned
                      </span>
                    ) : (
                      <span className="status-badge available" title="Linked to at least one restaurant">
                        Active
                      </span>
                    )}
                  </td>
                  <td className="actions">
                    <button
                      onClick={() => handleEdit(food)}
                      className="btn-icon btn-edit"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(food.id)}
                      className="btn-icon btn-delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Users Management Component
const PAGE_SIZE = 25;

const UsersManagement = ({ currentUser }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  // Tri-state, not a boolean (#328). 'all' | 'real' | 'dormant' — a checkbox
  // could express two of the three, and the missing one ("show me the real
  // userbase without bot-shaped noise") is the one wanted daily.
  const [dormantFilter, setDormantFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [resetUserId, setResetUserId] = useState(null);
  const [resetUserEmail, setResetUserEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    first_name: '',
    last_name: '',
    password: '',
    status: 'pending',
    is_superuser: false,
    is_verified: false
  });

  // Refetch whenever the filter or page changes. Both are server-side now, so a
  // stale fetch would silently show the wrong page rather than erroring.
  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dormantFilter, page]);

  useEffect(() => {
    // Log current user for debugging
    if (currentUser) {
      console.log('UsersManagement: Current user is', currentUser);
    }
  }, [currentUser]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      // Sorting is the server's job now (#324). It used to sort here, which is
      // fine while every row is fetched — but under pagination a client-side sort
      // orders only the current page, giving "the first N rows the database
      // happened to return, then alphabetised". That is not the first N
      // alphabetically, and it looks entirely correct.
      const params = new URLSearchParams({
        sort: 'email',
        direction: 'asc',
        limit: String(PAGE_SIZE),
        offset: String(page * PAGE_SIZE)
      });
      // Omitted for 'all'. The parameter is tri-state server-side, so sending
      // 'false' is a real request for the complement, not a no-op.
      if (dormantFilter === 'dormant') params.set('dormant_unverified', 'true');
      if (dormantFilter === 'real') params.set('dormant_unverified', 'false');

      const { data, total: matched } = await api.getWithTotal(`/users/?${params}`);
      setUsers(data);
      setTotal(matched);
    } catch (err) {
      setError('Failed to fetch users: ' + err.message);
    } finally {
      setLoading(false);
    }
  };


  const resetForm = () => {
    setFormData({
      email: '',
      username: '',
      first_name: '',
      last_name: '',
      password: '',
      status: 'pending',
      is_superuser: false,
      is_verified: false
    });
    setEditingUser(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const { password, ...updateData } = formData;
        // Only include password if it's not empty
        const payload = password ? { ...updateData, password } : updateData;
        await api.put(`/users/${editingUser.id}`, payload);
        setSuccess('User updated successfully!');
      } else {
        await api.post('/users/register', formData);
        setSuccess('User created successfully!');
      }
      fetchUsers();
      resetForm();
    } catch (err) {
      setError('Failed to save user: ' + err.message);
    }
  };

  const handleEdit = (user) => {
    setFormData({
      email: user.email,
      username: user.username || '',
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      password: '', // Don't pre-fill password for security
      status: user.status,
      is_superuser: user.is_superuser,
      is_verified: user.is_verified
    });
    setEditingUser(user);
    setShowForm(true);
  };

  const handleDelete = async (userId) => {
    // Additional safety check
    if (currentUser && (currentUser.id === userId || users.find(u => u.id === userId)?.email === currentUser.email)) {
      setError('You cannot delete your own account!');
      return;
    }

    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await api.delete(`/users/${userId}`);
        setSuccess('User deleted successfully!');
        fetchUsers();
      } catch (err) {
        setError('Failed to delete user: ' + err.message);
      }
    }
  };

  const handleVerify = async (userId) => {
    if (window.confirm('Are you sure you want to verify this user?')) {
      try {
        await api.post(`/users/${userId}/verify`);
        setSuccess('User verified successfully!');
        fetchUsers();
      } catch (err) {
        setError('Failed to verify user: ' + err.message);
      }
    }
  };

  const handlePasswordResetClick = (user) => {
    setResetUserId(user.id);
    setResetUserEmail(user.email);
    setNewPassword('');
    setShowPasswordReset(true);
  };

  const handlePasswordResetSubmit = async () => {
    if (!newPassword || newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    try {
      await api.put(`/users/${resetUserId}`, { password: newPassword });
      setSuccess(`Password reset successfully for ${resetUserEmail}`);
      setShowPasswordReset(false);
      setResetUserId(null);
      setResetUserEmail('');
      setNewPassword('');
    } catch (err) {
      setError('Failed to reset password: ' + err.message);
    }
  };

  const handlePasswordResetCancel = () => {
    setShowPasswordReset(false);
    setResetUserId(null);
    setResetUserEmail('');
    setNewPassword('');
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="content">
      <div className="content-header">
        <h1>Users Management</h1>
        <button
          onClick={() => setShowForm(true)}
          className="btn btn-primary"
        >
          <Plus size={16} />
          Add User
        </button>
      </div>

      {error && <ErrorMessage message={error} onDismiss={() => setError('')} />}
      {success && (
        <div className="success-message">
          <div className="message-content">
            <span>{success}</span>
            <button onClick={() => setSuccess('')} className="dismiss-btn">
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {showPasswordReset && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>Reset Password</h2>
              <button onClick={handlePasswordResetCancel} className="close-btn">
                <X size={20} />
              </button>
            </div>
            <div className="modal-content">
              <p style={{ marginBottom: '1rem', color: '#666' }}>
                Reset password for: <strong>{resetUserEmail}</strong>
              </p>
              <div className="form-group">
                <label>New Password (minimum 8 characters)</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  autoFocus
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handlePasswordResetSubmit();
                    }
                  }}
                />
              </div>
              <div className="button-group" style={{ marginTop: '1.5rem' }}>
                <button
                  onClick={handlePasswordResetSubmit}
                  className="btn btn-primary"
                  disabled={!newPassword || newPassword.length < 8}
                >
                  <Key size={16} />
                  Reset Password
                </button>
                <button
                  onClick={handlePasswordResetCancel}
                  className="btn btn-secondary"
                >
                  <X size={16} />
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{editingUser ? 'Edit User' : 'Add New User'}</h2>
              <button onClick={resetForm} className="close-btn">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Username</label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label>First Name</label>
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Last Name</label>
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label>{editingUser ? 'New Password (leave blank to keep current)' : 'Password *'}</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required={!editingUser}
                  />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="pending">Pending</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_superuser}
                    onChange={(e) => setFormData({ ...formData, is_superuser: e.target.checked })}
                  />
                  Super User
                </label>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_verified}
                    onChange={(e) => setFormData({ ...formData, is_verified: e.target.checked })}
                  />
                  Mark as Verified (Skip email verification)
                </label>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  <Save size={16} />
                  {editingUser ? 'Update User' : 'Create User'}
                </button>
                <button type="button" onClick={resetForm} className="btn btn-secondary">
                  <X size={16} />
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="filter-bar" style={{ display: 'flex', alignItems: 'center', gap: '16px', margin: '12px 0', flexWrap: 'wrap' }}>
        {/* "Real users" rather than "Non-dormant" — a double negative describes
            users by what they are not. "Active accounts" was the other candidate
            and collides with status: active, which means something else. */}
        {[
          ['all', 'All users'],
          ['real', 'Real users only'],
          ['dormant', 'Dormant unverified only'],
        ].map(([value, label]) => (
          <label key={value} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
            <input
              type="radio"
              name="dormant_filter"
              value={value}
              checked={dormantFilter === value}
              onChange={() => { setPage(0); setDormantFilter(value); }}
            />
            {label}
          </label>
        ))}
        {/* The count stays visible in every mode. #324's point was that the flag
            is a heuristic an operator can disagree with, and they can only do
            that if the basis is on screen — hiding it in a "real users" view
            would undo exactly that. */}
        <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>
          {dormantFilter === 'dormant' && `${total} account${total === 1 ? '' : 's'} never verified, no content, never returned`}
          {dormantFilter === 'real' && `${total} user${total === 1 ? '' : 's'} verified, or with content, or who came back`}
          {dormantFilter === 'all' && `${total} user${total === 1 ? '' : 's'}`}
        </span>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Username</th>
              <th>Status</th>
              <th>Super User</th>
              <th>Verified</th>
              {/* Shown alongside the flag, never instead of it: the flag is a
                  heuristic, and an operator needs to see why a row was marked
                  in order to disagree with it. */}
              <th>Activity</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id} style={(currentUser?.id === user.id || currentUser?.email === user.email) ? { backgroundColor: '#fff3cd' } : {}}>
                <td>
                  {user.email}
                  {user.is_dormant_unverified && (
                    <span
                      title="Never verified, produced no content, and never returned after signup"
                      style={{
                        marginLeft: '8px', padding: '2px 6px', borderRadius: '4px',
                        background: '#fef3c7', color: '#92400e',
                        fontSize: '0.75rem', whiteSpace: 'nowrap'
                      }}
                    >
                      dormant
                    </span>
                  )}
                </td>
                <td>{`${user.first_name || ''} ${user.last_name || ''}`.trim() || '-'}</td>
                <td>{user.username || '-'}</td>
                <td>
                  <span className={`status-badge status-${user.status}`}>
                    {user.status}
                  </span>
                </td>
                <td>{user.is_superuser ? '✓' : '-'}</td>
                <td>{user.is_verified ? '✓' : '-'}</td>
                <td style={{ whiteSpace: 'nowrap', fontSize: '0.875rem' }}>
                  {user.content_counts
                    ? `${user.content_counts.reviews}r / ${user.content_counts.visits}v / ${user.content_counts.restaurants}p`
                    : '-'}
                </td>
                <td>{new Date(user.created_at).toLocaleDateString()}</td>
                <td>
                  <div className="action-buttons">
                    <button
                      onClick={() => handleEdit(user)}
                      className="btn btn-outline btn-small"
                      title="Edit User"
                    >
                      <Edit size={14} />
                    </button>
                    {!user.is_verified && (
                      <button
                        onClick={() => handleVerify(user.id)}
                        className="btn btn-outline btn-small btn-success"
                        title="Verify User"
                      >
                        <CheckCircle size={14} />
                      </button>
                    )}
                    <button
                      onClick={() => handlePasswordResetClick(user)}
                      className="btn btn-outline btn-small btn-warning"
                      title="Reset Password"
                    >
                      <Key size={14} />
                    </button>
                    <button
                      onClick={() => handleDelete(user.id)}
                      className="btn btn-outline btn-small btn-danger"
                      title={(currentUser?.id === user.id || currentUser?.email === user.email) ? "Cannot delete yourself" : "Delete User"}
                      disabled={currentUser?.id === user.id || currentUser?.email === user.email}
                      style={(currentUser?.id === user.id || currentUser?.email === user.email) ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {total > PAGE_SIZE && (
        <div
          className="pagination"
          style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '12px 0' }}
        >
          <button
            className="btn btn-outline btn-small"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0 || loading}
          >
            Previous
          </button>
          <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
            {/* Positions within the FILTERED total, which the server counts before
                cutting the page — a page number against a page-sized count would
                always read "1 of 1". */}
            Page {page + 1} of {Math.max(1, Math.ceil(total / PAGE_SIZE))}
          </span>
          <button
            className="btn btn-outline btn-small"
            onClick={() => setPage((p) => p + 1)}
            disabled={(page + 1) * PAGE_SIZE >= total || loading}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

// Recently Edited Component (#71) — restaurants a creator edited after approval.
// The edits are already live; this is the admin review queue (not an approval gate).
const RecentlyEdited = () => {
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchRecentlyEdited();
  }, []);

  const fetchRecentlyEdited = async () => {
    setLoading(true);
    try {
      const data = await api.get('/restaurants/admin/recently-edited');
      setRestaurants(data);
    } catch (err) {
      setError('Failed to fetch recently edited restaurants');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkReviewed = async (id) => {
    try {
      await api.post(`/restaurants/${id}/mark-reviewed`, {});
      setSuccess('Marked as reviewed');
      setRestaurants((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      setError('Failed to mark as reviewed');
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="section">
      <div className="section-header">
        <h2>Recently Edited</h2>
        <span className="badge">
          {restaurants.length} awaiting review
        </span>
      </div>
      <p style={{ marginTop: '-0.5rem', marginBottom: '1rem', color: '#57606a', fontSize: '13px' }}>
        Restaurants a creator edited after approval. The changes are already live — review them and mark reviewed when you've checked them.
      </p>

      {error && <ErrorMessage message={error} onDismiss={() => setError('')} />}
      {success && <SuccessMessage message={success} onDismiss={() => setSuccess('')} />}

      {restaurants.length === 0 ? (
        <div className="empty-state">
          <CheckCircle size={48} />
          <p>Nothing awaiting review</p>
        </div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>City</th>
                <th>Edited by</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {restaurants.map((restaurant) => (
                <tr key={restaurant.id}>
                  <td className="font-medium">{restaurant.name}</td>
                  <td>
                    {restaurant.locations && restaurant.locations.length > 0
                      ? `${restaurant.locations[0].city}${restaurant.locations[0].state ? `, ${restaurant.locations[0].state}` : ''}`
                      : 'N/A'}
                  </td>
                  <td>
                    {restaurant.created_by_display_name || restaurant.created_by_email || 'Unknown'}
                    {restaurant.created_by_is_verified && (
                      <span title="Verified" style={{ color: '#1a7f37', marginLeft: '4px' }}>✓</span>
                    )}
                  </td>
                  <td className="actions">
                    <a
                      href={`https://dynerz.net/restaurant/${restaurant.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-icon"
                      title="View on site"
                    >
                      <ExternalLink size={16} />
                    </a>
                    <button
                      onClick={() => handleMarkReviewed(restaurant.id)}
                      className="btn-icon btn-success"
                      title="Mark reviewed"
                    >
                      <CheckCircle size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Photo Contributions Component (#94) — community-submitted photos awaiting
// approval before they join the public restaurant / menu-item galleries.
// Pending contributions live in a staging table (never public), so approving is
// what publishes them; rejecting deletes the staged image.
const PhotoContributions = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    fetchPending();
  }, []);

  const fetchPending = async () => {
    setLoading(true);
    try {
      const data = await api.get('/contributions/pending');
      setItems(data);
    } catch (err) {
      setError('Failed to fetch photo contributions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const act = async (id, action) => {
    setBusyId(id);
    setError('');
    setSuccess('');
    try {
      await api.post(`/contributions/${id}/${action}`, {});
      setSuccess(action === 'approve' ? 'Published to the public gallery' : 'Rejected and removed');
      setItems((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      setError(err.detail || `Failed to ${action} contribution`);
    } finally {
      setBusyId(null);
    }
  };

  const targetLabel = (c) => {
    if (c.target_name) return c.target_name;
    if (c.target_type === 'restaurant') return `Restaurant #${c.target_id}`;
    // new_menu_item's target_id is the parent restaurant, not a menu item (#103).
    if (c.target_type === 'new_menu_item') {
      return `${c.proposed_name || 'New dish'} @ Restaurant #${c.target_id}`;
    }
    return `Menu item #${c.target_id}`;
  };
  // new_menu_item also carries a restaurant target_id, so it links to the restaurant.
  const linksToRestaurant = (c) =>
    c.target_type === 'restaurant' || c.target_type === 'new_menu_item';

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="section">
      <div className="section-header">
        <h2>Photo Contributions</h2>
        <span className="badge">{items.length} pending</span>
      </div>
      <p style={{ marginTop: '-0.5rem', marginBottom: '1rem', color: '#57606a', fontSize: '13px' }}>
        Community-submitted photos (#94) awaiting review. Approving publishes the photo to the
        public restaurant / menu-item gallery; rejecting deletes it.
      </p>

      {error && <ErrorMessage message={error} onDismiss={() => setError('')} />}
      {success && <SuccessMessage message={success} onDismiss={() => setSuccess('')} />}

      {items.length === 0 ? (
        <div className="empty-state">
          <CheckCircle size={48} />
          <p>No photo contributions awaiting review</p>
        </div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Photo</th>
                <th>For</th>
                <th>Caption</th>
                <th>Contributor</th>
                <th>Submitted</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id}>
                  <td>
                    {c.preview_url ? (
                      <a href={c.preview_url} target="_blank" rel="noopener noreferrer" title="Open full size">
                        <img
                          src={c.preview_url}
                          alt="contribution"
                          style={{ width: '72px', height: '72px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #d0d7de' }}
                        />
                      </a>
                    ) : (
                      <span style={{ color: '#8c959f' }}><Image size={20} /></span>
                    )}
                  </td>
                  <td className="font-medium">
                    {linksToRestaurant(c) ? (
                      <a href={`https://dynerz.net/restaurant/${c.target_id}`} target="_blank" rel="noopener noreferrer">
                        {targetLabel(c)}
                      </a>
                    ) : (
                      targetLabel(c)
                    )}
                  </td>
                  <td>{c.caption || <span style={{ color: '#8c959f' }}>—</span>}</td>
                  <td>{c.contributor_name || `#${c.contributor_user_id}`}</td>
                  <td>{c.created_at ? new Date(c.created_at).toLocaleDateString() : '—'}</td>
                  <td className="actions">
                    <button
                      onClick={() => act(c.id, 'approve')}
                      disabled={busyId === c.id}
                      className="btn-icon btn-success"
                      title="Approve & publish"
                    >
                      <CheckCircle size={16} />
                    </button>
                    <button
                      onClick={() => act(c.id, 'reject')}
                      disabled={busyId === c.id}
                      className="btn-icon btn-delete"
                      title="Reject & delete"
                    >
                      <X size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Category Suggestions Component (#127b) — community-suggested Food categories
// awaiting approval. A pending suggestion never touches the public
// food_categories association; approving links the category to the dish (minting
// a new FoodCategory when it's a proposed name), rejecting drops it. Built plain
// (approve/reject) — reject-with-reason is deferred to #104 across all surfaces.
const CategorySuggestions = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    fetchPending();
  }, []);

  const fetchPending = async () => {
    setLoading(true);
    try {
      const data = await api.get('/category-suggestions/pending');
      setItems(data);
    } catch (err) {
      setError('Failed to fetch category suggestions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const act = async (id, action) => {
    setBusyId(id);
    setError('');
    setSuccess('');
    try {
      await api.post(`/category-suggestions/${id}/${action}`, {});
      setSuccess(action === 'approve' ? 'Category linked to the dish' : 'Suggestion rejected');
      setItems((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      setError(err.detail || `Failed to ${action} suggestion`);
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="section">
      <div className="section-header">
        <h2>Category Suggestions</h2>
        <span className="badge">{items.length} pending</span>
      </div>
      <p style={{ marginTop: '-0.5rem', marginBottom: '1rem', color: '#57606a', fontSize: '13px' }}>
        Community-suggested dish categories (#127) awaiting review. Approving links the category to
        the dish (creating a new category when it's flagged <em>new</em>); rejecting drops it.
      </p>

      {error && <ErrorMessage message={error} onDismiss={() => setError('')} />}
      {success && <SuccessMessage message={success} onDismiss={() => setSuccess('')} />}

      {items.length === 0 ? (
        <div className="empty-state">
          <CheckCircle size={48} />
          <p>No category suggestions awaiting review</p>
        </div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Dish</th>
                <th>Category</th>
                <th>Suggested by</th>
                <th>Submitted</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id}>
                  <td className="font-medium">
                    <a href={`https://dynerz.net/food/${c.food_id}`} target="_blank" rel="noopener noreferrer">
                      {c.food_name || `Food #${c.food_id}`}
                    </a>
                  </td>
                  <td>
                    {c.category_name || '—'}
                    {c.proposed_name ? (
                      <span
                        title="A brand-new category will be created on approval"
                        style={{ marginLeft: '6px', fontSize: '11px', color: '#9a6700', background: '#fff8c5', border: '1px solid #eac54f', borderRadius: '10px', padding: '1px 6px' }}
                      >
                        new
                      </span>
                    ) : null}
                  </td>
                  <td>{c.suggester_name || `#${c.suggester_user_id}`}</td>
                  <td>{c.created_at ? new Date(c.created_at).toLocaleDateString() : '—'}</td>
                  <td className="actions">
                    <button
                      onClick={() => act(c.id, 'approve')}
                      disabled={busyId === c.id}
                      className="btn-icon btn-success"
                      title="Approve & link category"
                    >
                      <CheckCircle size={16} />
                    </button>
                    <button
                      onClick={() => act(c.id, 'reject')}
                      disabled={busyId === c.id}
                      className="btn-icon btn-delete"
                      title="Reject suggestion"
                    >
                      <X size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Pending Approvals Component
// #349: other restaurants with a location at the same Google place — an
// inspection aid, never an action. The backend sends possible_duplicates to
// superusers only; keyed on place id, so two branches of a chain never match.
// ownerById resolves the other row's owner from rows already loaded; a row that
// is on screen gets a link that scrolls to it.
const PossibleDuplicates = ({ restaurant, ownerById }) => {
  const dups = restaurant.possible_duplicates;
  if (!dups || dups.length === 0) return null;
  const jumpTo = (id) => {
    const row = document.getElementById(`restaurant-row-${id}`);
    if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };
  return (
    <div
      style={{ color: '#bf8700', fontSize: '12px', marginTop: '4px', fontWeight: 'normal' }}
      title="Shares a Google place with another restaurant. Not necessarily wrong — two users can each keep their own private copy."
    >
      <AlertCircle size={12} style={{ verticalAlign: '-2px', marginRight: '4px' }} />
      Same place as{' '}
      {dups.map((d, i) => {
        const owner = ownerById[d.created_by_user_id] || (d.created_by_user_id ? `user ${d.created_by_user_id}` : 'no owner');
        const label = `#${d.id} ${d.name} (${d.visibility} · ${owner})`;
        const onScreen = !!ownerById.__ids && ownerById.__ids.has(d.id);
        return (
          <span key={d.id}>
            {i > 0 && ', '}
            {onScreen ? (
              <button
                type="button"
                onClick={() => jumpTo(d.id)}
                style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }}
              >
                {label}
              </button>
            ) : label}
          </span>
        );
      })}
    </div>
  );
};

const PendingApprovals = () => {
  const [pendingRestaurants, setPendingRestaurants] = useState([]);
  const [pendingMenuItems, setPendingMenuItems] = useState([]);
  // #282: the settled private states. "Make Private" moves a row OUT of the
  // queue above (review_state 'pending' -> 'rejected'), and until now it landed
  // on no admin surface at all — the admin created a state it could not then see
  // or undo. Kept separate from pendingRestaurants so the queue keeps meaning
  // "awaiting a decision".
  const [privateRestaurants, setPrivateRestaurants] = useState([]);
  // #349: owner names for the duplicate hints, from rows already on screen.
  const ownerById = (() => {
    const map = { __ids: new Set() };
    [...pendingRestaurants, ...privateRestaurants].forEach((r) => {
      map.__ids.add(r.id);
      if (r.created_by_user_id) {
        map[r.created_by_user_id] = r.created_by_display_name || r.created_by_email || map[r.created_by_user_id];
      }
    });
    return map;
  })();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // #45 — suggest a street address from Google for pending submissions that lack one
  // (post-#44, users can submit name + city only). Keyed by restaurant id.
  const [loadingSuggest, setLoadingSuggest] = useState({});
  const [addressSuggestions, setAddressSuggestions] = useState({});

  useEffect(() => {
    fetchPendingItems();
  }, []);

  const fetchPendingItems = async () => {
    setLoading(true);
    try {
      // #137: server-side moderation queue = review_state='pending' only. This
      // EXCLUDES private "just for me" adds (review_state='none') and rejected
      // ones (review_state='rejected'), which the old is_approved!==1 client
      // filter would have swept in (private/rejected are also is_approved=0).
      const pending = await api.get('/restaurants/?only_pending=true');
      setPendingRestaurants(pending);

      // Fetch only pending menu items from backend (no limit needed - backend filters and returns only pending)
      // This is scalable regardless of total number of menu items in the database
      const pendingMenu = await api.get('/foods/menu/items/?only_pending=true');
      setPendingMenuItems(pendingMenu);

      // #282 / § 2.5: the OPERATOR read — every restaurant whatever its
      // visibility, superuser-gated server-side (#286). visibility=private names
      // the set exactly in one request; review_state alone could not, because
      // 'none' is shared by an approved PUBLIC row and a "just for me" one.
      // Pending rows are excluded here on purpose — they are the queue above, and
      // listing them twice would make "awaiting a decision" ambiguous.
      const priv = await api.get('/restaurants/?include_pending=true&visibility=private&limit=10000');
      setPrivateRestaurants(priv.filter(r => r.review_state !== 'pending'));
    } catch (err) {
      setError('Failed to fetch pending items');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveRestaurant = async (id) => {
    try {
      await api.put(`/restaurants/${id}`, { is_approved: 1 });
      setSuccess('Restaurant approved successfully');
      fetchPendingItems();
    } catch (err) {
      setError('Failed to approve restaurant');
    }
  };

  // #45 — true when a pending restaurant's main location has no street address.
  const mainLocation = (restaurant) => restaurant.locations && restaurant.locations[0];
  const isAddressless = (restaurant) => {
    const loc = mainLocation(restaurant);
    return loc && (!loc.address || !loc.address.trim());
  };

  const suggestAddressForPending = async (restaurant) => {
    const loc = mainLocation(restaurant);
    if (!loc) { setError('This restaurant has no location to attach an address to'); return; }
    if (!loc.city) { setError('A city is required to suggest an address'); return; }
    try {
      setLoadingSuggest(prev => ({ ...prev, [restaurant.id]: true }));
      const response = await api.post('/api/google-maps/suggest-address', {
        business_name: restaurant.name,
        city: loc.city,
        state: loc.state,
        ...locationBias(loc)
      });
      if (!response.found) { setError(response.error || 'Business not found in Google Maps'); return; }
      setAddressSuggestions(prev => ({
        ...prev,
        [restaurant.id]: { ...response, editing: false, editedAddress: response.address || '' }
      }));
      if (response.error) setError(response.error);
    } catch (err) {
      setError(err.detail || 'Failed to suggest address from Google Maps');
    } finally {
      setLoadingSuggest(prev => ({ ...prev, [restaurant.id]: false }));
    }
  };

  const applyAddressForPending = async (restaurant) => {
    const s = addressSuggestions[restaurant.id];
    const loc = mainLocation(restaurant);
    if (!s || !loc) return;
    try {
      // Partial PUT — backend update is exclude_unset, so only these fields change.
      await api.put(`/restaurants/locations/${loc.id}`, {
        address: (s.editing ? s.editedAddress : s.address) || '',
        city: s.city || loc.city || '',
        state: s.state || loc.state || '',
        zip_code: s.zip_code || loc.zip_code || '',
        country: s.country || loc.country || 'United States',
        latitude: s.latitude,
        longitude: s.longitude
      });
      setSuccess(`Address saved for ${restaurant.name}${s.formatted_address ? ` (${s.formatted_address})` : ''}`);
      dismissAddressForPending(restaurant.id);
      fetchPendingItems();
    } catch (err) {
      setError('Failed to save address');
    }
  };

  const dismissAddressForPending = (restaurantId) => {
    setAddressSuggestions(prev => {
      const next = { ...prev };
      delete next[restaurantId];
      return next;
    });
  };

  // #137: downgrade a pending public submission to the submitter's PRIVATE
  // restaurant instead of deleting it — for a legit spot that just doesn't meet
  // the public/curation bar (e.g. a chain). is_approved=-1 → the backend sets
  // visibility=private/review_state=rejected; the submitter keeps it, and no
  // other DINER sees it. Distinct from Reject & Delete, which stays for spam/junk.
  //
  // #282: the confirm used to say "only they will see it". Honest about
  // publication, wrong about the system — admins can see it (§ 2.5), and it
  // lands in Private Restaurants below, where Publish can undo this. Saying so
  // is also what makes a misclick here (it sits beside Reject & Delete)
  // obviously recoverable.
  const handleMakePrivateRestaurant = async (id) => {
    if (window.confirm("Make this the submitter's private restaurant? It won't be published or deleted — diners won't see it, but the submitter keeps it. It moves to Private Restaurants below, where you can publish it later.")) {
      try {
        await api.put(`/restaurants/${id}`, { is_approved: -1 });
        setSuccess('Kept as a private restaurant for the submitter');
        fetchPendingItems();
      } catch (err) {
        setError('Failed to make restaurant private');
      }
    }
  };

  const handleRejectRestaurant = async (id) => {
    if (window.confirm('Are you sure you want to reject and DELETE this restaurant? This cannot be undone.')) {
      try {
        await api.delete(`/restaurants/${id}`);
        setSuccess('Restaurant rejected and deleted');
        fetchPendingItems();
      } catch (err) {
        setError('Failed to reject restaurant');
      }
    }
  };

  const handleApproveMenuItem = async (id) => {
    try {
      await api.put(`/foods/menu/items/${id}`, { is_approved: 1 });
      setSuccess('Menu item approved successfully');
      fetchPendingItems();
    } catch (err) {
      setError('Failed to approve menu item');
    }
  };

  const handleRejectMenuItem = async (id) => {
    if (window.confirm('Are you sure you want to reject and delete this menu item?')) {
      try {
        await api.delete(`/foods/menu/items/${id}`);
        setSuccess('Menu item rejected and deleted');
        fetchPendingItems();
      } catch (err) {
        setError('Failed to reject menu item');
      }
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  const totalPending = pendingRestaurants.length + pendingMenuItems.length;

  return (
    <div className="section">
      <div className="section-header">
        <h2>Pending Approvals</h2>
        <span className="badge">
          {totalPending} pending item{totalPending !== 1 ? 's' : ''}
        </span>
      </div>

      {error && <ErrorMessage message={error} onDismiss={() => setError('')} />}
      {success && <SuccessMessage message={success} onDismiss={() => setSuccess('')} />}

      {totalPending === 0 && (
        <div className="empty-state">
          <CheckCircle size={48} />
          <p>No pending approvals at this time</p>
        </div>
      )}

      {/* Pending Restaurants */}
      {pendingRestaurants.length > 0 && (
        <>
          <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>Pending Restaurants ({pendingRestaurants.length})</h3>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>City</th>
                  <th>Description</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingRestaurants.map((restaurant) => (
                  <React.Fragment key={restaurant.id}>
                    <tr id={`restaurant-row-${restaurant.id}`}>
                    <td className="font-medium">
                      {restaurant.name}
                      <PossibleDuplicates restaurant={restaurant} ownerById={ownerById} />
                    </td>
                    <td>
                      {restaurant.locations && restaurant.locations.length > 0
                        ? `${restaurant.locations[0].city}${restaurant.locations[0].state ? `, ${restaurant.locations[0].state}` : ''}`
                        : 'N/A'}
                      {isAddressless(restaurant) && (
                        <span style={{ color: '#bf8700', fontSize: '12px', marginLeft: '6px' }} title="No street address">· no address</span>
                      )}
                    </td>
                    <td>{restaurant.description || 'No description'}</td>
                    <td className="actions">
                      {isAddressless(restaurant) && (
                        <button
                          onClick={() => suggestAddressForPending(restaurant)}
                          className="btn-icon"
                          title="Suggest address from Google"
                          disabled={loadingSuggest[restaurant.id]}
                        >
                          {loadingSuggest[restaurant.id]
                            ? <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                            : <MapPin size={16} />}
                        </button>
                      )}
                      <button
                        onClick={() => handleApproveRestaurant(restaurant.id)}
                        className="btn-icon btn-success"
                        title="Approve — publish to the public directory"
                      >
                        <CheckCircle size={16} />
                      </button>
                      <button
                        onClick={() => handleMakePrivateRestaurant(restaurant.id)}
                        className="btn-icon"
                        title="Make Private — keep it for the submitter (not public, not deleted)"
                      >
                        <Lock size={16} />
                      </button>
                      <button
                        onClick={() => handleRejectRestaurant(restaurant.id)}
                        className="btn-icon btn-delete"
                        title="Reject & Delete — remove entirely (spam/junk)"
                      >
                        <X size={16} />
                      </button>
                    </td>
                    </tr>
                    {addressSuggestions[restaurant.id] && (
                      <tr>
                        <td colSpan={4} style={{ background: '#f6f8fa' }}>
                          <div style={{ padding: '4px 2px' }}>
                            <div style={{ fontSize: '12px', fontWeight: 600, color: '#57606a', marginBottom: '6px' }}>
                              Suggested address for {restaurant.name}:
                            </div>
                            {addressSuggestions[restaurant.id].editing ? (
                              <input
                                type="text"
                                value={addressSuggestions[restaurant.id].editedAddress}
                                onChange={(e) => setAddressSuggestions(prev => ({
                                  ...prev,
                                  [restaurant.id]: { ...prev[restaurant.id], editedAddress: e.target.value }
                                }))}
                                placeholder="Street address"
                                style={{ width: '100%', maxWidth: '420px', marginBottom: '8px' }}
                              />
                            ) : (
                              <div style={{ fontSize: '14px', color: '#1f2328', marginBottom: '8px' }}>
                                {addressSuggestions[restaurant.id].formatted_address
                                  || addressSuggestions[restaurant.id].address
                                  || '(no street address returned for this match)'}
                              </div>
                            )}
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                type="button"
                                onClick={() => applyAddressForPending(restaurant)}
                                className="btn btn-primary btn-small"
                                style={{ fontSize: '13px', padding: '6px 12px' }}
                                disabled={!addressSuggestions[restaurant.id].address && !addressSuggestions[restaurant.id].editedAddress}
                              >
                                <CheckCircle size={14} style={{ marginRight: '6px' }} />
                                Accept
                              </button>
                              <button
                                type="button"
                                onClick={() => setAddressSuggestions(prev => ({
                                  ...prev,
                                  [restaurant.id]: { ...prev[restaurant.id], editing: !prev[restaurant.id].editing }
                                }))}
                                className="btn btn-secondary btn-small"
                                style={{ fontSize: '13px', padding: '6px 12px' }}
                              >
                                <Edit size={14} style={{ marginRight: '6px' }} />
                                {addressSuggestions[restaurant.id].editing ? 'Done' : 'Edit'}
                              </button>
                              <button
                                type="button"
                                onClick={() => dismissAddressForPending(restaurant.id)}
                                className="btn btn-secondary btn-small"
                                style={{ fontSize: '13px', padding: '6px 12px' }}
                              >
                                <X size={14} style={{ marginRight: '6px' }} />
                                Dismiss
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Pending Menu Items */}
      {pendingMenuItems.length > 0 && (
        <>
          <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>Pending Menu Items ({pendingMenuItems.length})</h3>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Food</th>
                  <th>Restaurant</th>
                  <th>Price</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingMenuItems.map((item) => (
                  <tr key={item.id}>
                    <td className="font-medium">{item.food?.name || 'N/A'}</td>
                    <td>{item.restaurant?.name || 'N/A'}</td>
                    <td>{item.price ? `$${item.price.toFixed(2)}` : 'N/A'}</td>
                    <td className="actions">
                      <button
                        onClick={() => handleApproveMenuItem(item.id)}
                        className="btn-icon btn-success"
                        title="Approve"
                      >
                        <CheckCircle size={16} />
                      </button>
                      <button
                        onClick={() => handleRejectMenuItem(item.id)}
                        className="btn-icon btn-delete"
                        title="Reject & Delete"
                      >
                        <X size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* #282: Private restaurants — the operator view (§ 2.5).
          Two SETTLED states, deliberately shown apart rather than merged: the
          surface exists to answer "which are meant to be public and which
          private", so collapsing them would defeat it.

          🔑 VISIBILITY IS NOT ACTIONABILITY. An admin may SEE a "just for me"
          row; an admin must NEVER be able to publish it — publishing a place its
          owner deliberately kept private is the violation the model exists to
          prevent. So Approve renders ONLY for rows an admin itself downgraded.
          That condition is the load-bearing line in this block, not the table. */}
      {privateRestaurants.length > 0 && (
        <>
          <h3 style={{ marginTop: '2rem', marginBottom: '0.25rem' }}>
            Private Restaurants ({privateRestaurants.length})
          </h3>
          <p style={{ margin: '0 0 1rem', fontSize: '13px', color: '#57606a' }}>
            Not in the public directory. Nothing here is awaiting a decision — the
            queue above is. Places kept private by their owner are shown for
            visibility only and cannot be published from here.
          </p>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>City</th>
                  <th>Why it's private</th>
                  <th>Submitter</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {privateRestaurants.map((restaurant) => {
                  const madePrivateByAdmin = restaurant.review_state === 'rejected';
                  return (
                    <tr key={restaurant.id} id={`restaurant-row-${restaurant.id}`}>
                      <td className="font-medium">
                        {restaurant.name}
                        <PossibleDuplicates restaurant={restaurant} ownerById={ownerById} />
                      </td>
                      <td>
                        {restaurant.locations && restaurant.locations.length > 0
                          ? `${restaurant.locations[0].city}${restaurant.locations[0].state ? `, ${restaurant.locations[0].state}` : ''}`
                          : 'N/A'}
                      </td>
                      <td>
                        {madePrivateByAdmin ? (
                          <span title="An admin declined it for the public directory. Not deleted — the submitter keeps it.">
                            <EyeOff size={14} style={{ verticalAlign: '-2px', marginRight: '6px' }} />
                            Made private by an admin
                          </span>
                        ) : (
                          <span title="The owner chose to keep this just for themselves. It never entered the queue.">
                            <Lock size={14} style={{ verticalAlign: '-2px', marginRight: '6px' }} />
                            Kept private by its owner
                          </span>
                        )}
                      </td>
                      <td>{restaurant.created_by_display_name || restaurant.created_by_email || '—'}</td>
                      <td className="actions">
                        {madePrivateByAdmin ? (
                          <button
                            onClick={() => handleApproveRestaurant(restaurant.id)}
                            className="btn-icon btn-success"
                            title="Publish after all — reverses the earlier Make Private"
                          >
                            <CheckCircle size={16} />
                          </button>
                        ) : (
                          <span style={{ fontSize: '12px', color: '#8c959f' }}>
                            Owner's choice
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

const Moderation = () => {
  const [reportedReviews, setReportedReviews] = useState([]);
  const [reportedPhotos, setReportedPhotos] = useState([]);  // #91 public visit photos
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchReported();
    fetchReportedPhotos();
  }, []);

  const fetchReported = async () => {
    setLoading(true);
    try {
      const data = await api.get('/reviews/admin/reported');
      setReportedReviews(data);
    } catch (err) {
      setError('Failed to fetch reported reviews');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // #91: reported (auto-hidden) public visit photos. Restore = make public again.
  const fetchReportedPhotos = async () => {
    try {
      const data = await api.get('/photos/reported');
      setReportedPhotos(data);
    } catch (err) {
      console.error('Failed to fetch reported photos', err);
    }
  };

  const handleRestorePhoto = async (photoId) => {
    try {
      await api.post(`/photos/${photoId}/restore`);
      setSuccess('Photo restored and made public again');
      fetchReportedPhotos();
    } catch (err) {
      setError('Failed to restore photo');
    }
  };

  const handleRestore = async (reviewId) => {
    try {
      await api.post(`/reviews/${reviewId}/restore`);
      setSuccess('Review restored and made public again');
      fetchReported();
    } catch (err) {
      setError('Failed to restore review');
    }
  };

  const handleDelete = async (reviewId) => {
    if (window.confirm('Permanently delete this review? This cannot be undone.')) {
      try {
        await api.delete(`/reviews/${reviewId}`);
        setSuccess('Review deleted');
        fetchReported();
      } catch (err) {
        setError('Failed to delete review');
      }
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="section">
      <div className="section-header">
        <h2>Reports</h2>
        <span className="badge">
          {reportedReviews.length} reported review{reportedReviews.length !== 1 ? 's' : ''}
          {reportedPhotos.length > 0 && `, ${reportedPhotos.length} reported photo${reportedPhotos.length !== 1 ? 's' : ''}`}
        </span>
      </div>

      {error && <ErrorMessage message={error} onDismiss={() => setError('')} />}
      {success && <SuccessMessage message={success} onDismiss={() => setSuccess('')} />}

      {reportedReviews.length === 0 ? (
        <div className="empty-state">
          <CheckCircle size={48} />
          <p>No reported reviews — the moderation queue is empty</p>
        </div>
      ) : (
        <>
          <p style={{ color: '#666', marginBottom: '1rem' }}>
            Reported reviews are hidden from public view immediately. Restore false positives or delete violations.
          </p>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Review</th>
                  <th>Author</th>
                  <th>Target</th>
                  <th>Reports</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {reportedReviews.map(({ review, reports }) => (
                  <tr key={review.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                        <Star size={14} fill="#f59e0b" color="#f59e0b" />
                        <span className="font-medium">{review.rating}/5</span>
                        {review.title && <span className="font-medium"> — {review.title}</span>}
                      </div>
                      <div style={{ fontSize: '0.85rem', color: '#666', maxWidth: '300px' }}>
                        {review.review_text || 'No text'}
                      </div>
                    </td>
                    <td>
                      {review.user_display_name || review.user_email || 'Deleted user'}
                    </td>
                    <td>
                      {review.restaurant?.name || review.food?.name ||
                        review.menu_item?.custom_name || review.menu_item?.food?.name || 'N/A'}
                    </td>
                    <td>
                      {reports.map((report) => (
                        <div key={report.id} style={{ marginBottom: '6px' }}>
                          <span className="badge" style={{ background: '#fee2e2', color: '#b91c1c' }}>
                            {report.reason}
                          </span>
                          {report.details && (
                            <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '2px' }}>
                              "{report.details}"
                            </div>
                          )}
                          <div style={{ fontSize: '0.75rem', color: '#999' }}>
                            {new Date(report.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      ))}
                    </td>
                    <td className="actions">
                      <button
                        onClick={() => handleRestore(review.id)}
                        className="btn-icon btn-success"
                        title="Restore (false positive — make public again)"
                      >
                        <CheckCircle size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(review.id)}
                        className="btn-icon btn-delete"
                        title="Delete review permanently"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* #91: reported public visit photos (post-moderated — a report auto-hides
          the photo; restore makes it public again). A genuine violation just stays
          hidden. */}
      <div className="section-header" style={{ marginTop: '2rem' }}>
        <h3>Reported Photos</h3>
        <span className="badge">
          {reportedPhotos.length} reported photo{reportedPhotos.length !== 1 ? 's' : ''}
        </span>
      </div>
      {reportedPhotos.length === 0 ? (
        <div className="empty-state">
          <CheckCircle size={48} />
          <p>No reported photos — nothing hidden</p>
        </div>
      ) : (
        <>
          <p style={{ color: '#666', marginBottom: '1rem' }}>
            Reported photos are hidden from public view immediately. Restore false positives;
            leave genuine violations hidden.
          </p>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Photo</th>
                  <th>Uploaded by</th>
                  <th>Visit</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {reportedPhotos.map((photo) => (
                  <tr key={photo.id}>
                    <td>
                      <a href={photo.url} target="_blank" rel="noopener noreferrer">
                        <img
                          src={photo.url}
                          alt="Reported"
                          style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #dee2e6' }}
                        />
                      </a>
                    </td>
                    <td>User #{photo.uploaded_by_user_id}</td>
                    <td>{photo.visit_id ? `Visit #${photo.visit_id}` : 'N/A'}</td>
                    <td className="actions">
                      <button
                        onClick={() => handleRestorePhoto(photo.id)}
                        className="btn-icon btn-success"
                        title="Restore (false positive — make public again)"
                      >
                        <CheckCircle size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

// Login Component
// Unified moderation hub (#132) — one nav destination, three sub-tabs:
//   Approvals       — gate new community content in (restaurants + photos #94 + category suggestions #127b)
//   Reports         — reported live content (reviews + photos #91)
//   Recently Edited — creator-changes review queue
const ModerationHub = () => {
  const [subTab, setSubTab] = useState('approvals');
  const subTabs = [
    { id: 'approvals', label: 'Approvals', icon: AlertCircle },
    { id: 'reports', label: 'Reports', icon: ShieldAlert },
    { id: 'recently-edited', label: 'Recently Edited', icon: Edit },
  ];

  return (
    <div>
      <div className="tab-navigation" style={{ marginBottom: '1.25rem' }}>
        {subTabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            className={`tab ${subTab === id ? 'tab-active' : ''}`}
            onClick={() => setSubTab(id)}
          >
            <Icon size={16} />
            {label}
          </button>
        ))}
      </div>

      {subTab === 'approvals' && (
        <>
          <PendingApprovals />
          <PhotoContributions />
          <CategorySuggestions />
        </>
      )}
      {subTab === 'reports' && <Moderation />}
      {subTab === 'recently-edited' && <RecentlyEdited />}
    </div>
  );
};

const Login = ({ onLogin, sessionExpiredMessage, onDismissSessionExpired }) => {
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await api.login(credentials.email, credentials.password);
      auth.setToken(response.access_token);
      onLogin();
    } catch (err) {
      // Display specific error message based on status code
      if (err.status === 403) {
        setError('Access denied. Admin privileges required.');
      } else if (err.detail) {
        setError(err.detail);
      } else {
        setError('Invalid email or password');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-form">
        <div className="login-header">
          <h1>dyner.z Admin</h1>
          <p>Sign in to manage restaurants and content</p>
        </div>

        {sessionExpiredMessage && (
          <div className="info-message">
            <div className="message-content">
              <span>{sessionExpiredMessage}</span>
              <button onClick={onDismissSessionExpired} className="close-btn">
                <X size={16} />
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="error-message">
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={credentials.email}
              onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
              required
              autoFocus
            />
          </div>
          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              value={credentials.password}
              onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
              required
            />
          </div>
          <button
            type="submit"
            className="btn btn-primary login-btn"
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="spinner"></div>
                Signing in...
              </>
            ) : (
              <>
                <LogIn size={16} />
                Sign In
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

// CSV Import Component
const CSVImport = () => {
  const [file, setFile] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [options, setOptions] = useState({
    skipDuplicates: true,
    createMissingCategories: true,
    createMissingCuisines: true,
    autoApprove: false
  });

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.name.endsWith('.csv')) {
      setFile(selectedFile);
      setValidationResult(null);
      setImportResult(null);
    } else {
      alert('Please select a CSV file');
    }
  };

  const handleValidate = async () => {
    if (!file) {
      alert('Please select a file first');
      return;
    }

    setLoading(true);
    setValidationResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_BASE_URL}/admin/import/csv/validate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${auth.getToken()}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error(`Validation failed: ${response.statusText}`);
      }

      const result = await response.json();
      setValidationResult(result);
    } catch (err) {
      alert(`Validation error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!file || !validationResult || !validationResult.valid) {
      alert('Please validate the CSV file first');
      return;
    }

    if (!window.confirm(`Import ${validationResult.restaurants_found} restaurants and ${validationResult.foods_found} foods?`)) {
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const queryParams = new URLSearchParams({
        skip_duplicates: options.skipDuplicates,
        create_missing_categories: options.createMissingCategories,
        create_missing_cuisines: options.createMissingCuisines,
        auto_approve: options.autoApprove
      });

      const response = await fetch(`${API_BASE_URL}/admin/import/csv/execute?${queryParams}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${auth.getToken()}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error(`Import failed: ${response.statusText}`);
      }

      const result = await response.json();
      setImportResult(result);

      if (result.success) {
        setFile(null);
        setValidationResult(null);
      }
    } catch (err) {
      alert(`Import error: ${err.message}`);
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    window.open(`${API_BASE_URL}/admin/import/csv/template`, '_blank');
  };

  return (
    <div className="content-section">
      <div className="section-header">
        <h2>CSV Bulk Import</h2>
        <button onClick={downloadTemplate} className="btn btn-outline">
          <Download size={16} />
          Download Template
        </button>
      </div>

      <div className="import-container">
        {/* Step 1: Upload */}
        <div className="import-step">
          <h3>Step 1: Select CSV File</h3>
          <div className="file-upload-area">
            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="file-input"
              id="csv-file-input"
            />
            <label htmlFor="csv-file-input" className="file-upload-label">
              <FileUp size={32} />
              <span>{file ? file.name : 'Choose CSV file or drag and drop'}</span>
              <small>CSV format with restaurant and food data</small>
            </label>
          </div>

          {file && (
            <div className="file-info">
              <CheckCircle size={16} style={{ color: 'green' }} />
              <span>Selected: {file.name} ({(file.size / 1024).toFixed(2)} KB)</span>
            </div>
          )}

          <button
            onClick={handleValidate}
            disabled={!file || loading}
            className="btn btn-primary"
            style={{ marginTop: '1rem' }}
          >
            {loading ? 'Validating...' : 'Validate CSV'}
          </button>
        </div>

        {/* Step 2: Validation Results */}
        {validationResult && (
          <div className="import-step">
            <h3>Step 2: Validation Results</h3>

            <div className={`validation-summary ${validationResult.valid ? 'success' : 'error'}`}>
              <div className="summary-stats">
                <div className="stat">
                  <span className="stat-label">Total Rows:</span>
                  <span className="stat-value">{validationResult.total_rows}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Restaurants:</span>
                  <span className="stat-value">{validationResult.restaurants_found}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Foods:</span>
                  <span className="stat-value">{validationResult.foods_found}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Images:</span>
                  <span className="stat-value">{validationResult.images_found}</span>
                </div>
              </div>

              {validationResult.errors && validationResult.errors.length > 0 && (
                <div className="validation-errors">
                  <h4 style={{ color: 'red' }}>❌ Errors ({validationResult.errors.length})</h4>
                  <ul>
                    {validationResult.errors.slice(0, 10).map((error, idx) => (
                      <li key={idx}>
                        Row {error.row}: {error.message}
                        {error.field && ` (${error.field})`}
                      </li>
                    ))}
                  </ul>
                  {validationResult.errors.length > 10 && (
                    <p>... and {validationResult.errors.length - 10} more errors</p>
                  )}
                </div>
              )}

              {validationResult.warnings && validationResult.warnings.length > 0 && (
                <div className="validation-warnings">
                  <h4 style={{ color: 'orange' }}>⚠️  Warnings ({validationResult.warnings.length})</h4>
                  <ul>
                    {validationResult.warnings.slice(0, 5).map((warning, idx) => (
                      <li key={idx}>
                        Row {warning.row}: {warning.message}
                      </li>
                    ))}
                  </ul>
                  {validationResult.warnings.length > 5 && (
                    <p>... and {validationResult.warnings.length - 5} more warnings</p>
                  )}
                </div>
              )}

              {validationResult.valid && validationResult.preview_data && validationResult.preview_data.length > 0 && (
                <div className="preview-data">
                  <h4>Preview (First 10 rows)</h4>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Row</th>
                        <th>Restaurant</th>
                        <th>Food</th>
                        <th>Cuisine</th>
                        <th>City</th>
                        <th>Image</th>
                      </tr>
                    </thead>
                    <tbody>
                      {validationResult.preview_data.map((row, idx) => (
                        <tr key={idx}>
                          <td>{row.row}</td>
                          <td>{row.restaurant_name}</td>
                          <td>{row.food_name}</td>
                          <td>{row.cuisine || '-'}</td>
                          <td>{row.city || '-'}</td>
                          <td>{row.has_image ? '✓' : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Import Options */}
        {validationResult && validationResult.valid && (
          <div className="import-step">
            <h3>Step 3: Import Options</h3>
            <div className="import-options">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={options.skipDuplicates}
                  onChange={(e) => setOptions({ ...options, skipDuplicates: e.target.checked })}
                />
                Skip duplicate restaurants/foods
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={options.createMissingCategories}
                  onChange={(e) => setOptions({ ...options, createMissingCategories: e.target.checked })}
                />
                Auto-create missing categories
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={options.createMissingCuisines}
                  onChange={(e) => setOptions({ ...options, createMissingCuisines: e.target.checked })}
                />
                Auto-create missing cuisines
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={options.autoApprove}
                  onChange={(e) => setOptions({ ...options, autoApprove: e.target.checked })}
                />
                Auto-approve restaurants (skip pending review)
              </label>
            </div>

            <button
              onClick={handleImport}
              disabled={importing}
              className="btn btn-success"
              style={{ marginTop: '1rem' }}
            >
              {importing ? 'Importing...' : `Import ${validationResult.restaurants_found} Restaurants`}
            </button>
          </div>
        )}

        {/* Step 4: Import Results */}
        {importResult && (
          <div className="import-step">
            <h3>Step 4: Import Results</h3>
            <div className={`import-result ${importResult.success ? 'success' : 'error'}`}>
              {importResult.success ? (
                <>
                  <h4 style={{ color: 'green' }}>✅ Import Successful!</h4>
                  <div className="result-stats">
                    <div className="stat">
                      <span className="stat-label">Restaurants Created:</span>
                      <span className="stat-value">{importResult.restaurants_created}</span>
                    </div>
                    <div className="stat">
                      <span className="stat-label">Foods Created:</span>
                      <span className="stat-value">{importResult.foods_created}</span>
                    </div>
                    <div className="stat">
                      <span className="stat-label">Menu Items Created:</span>
                      <span className="stat-value">{importResult.menu_items_created}</span>
                    </div>
                    <div className="stat">
                      <span className="stat-label">Images Imported:</span>
                      <span className="stat-value">{importResult.images_imported}</span>
                    </div>
                    {importResult.duplicates_skipped > 0 && (
                      <div className="stat">
                        <span className="stat-label">Duplicates Skipped:</span>
                        <span className="stat-value">{importResult.duplicates_skipped}</span>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <h4 style={{ color: 'red' }}>❌ Import Failed</h4>
                  {importResult.errors && importResult.errors.length > 0 && (
                    <ul>
                      {importResult.errors.map((error, idx) => (
                        <li key={idx} style={{ color: 'red' }}>{error}</li>
                      ))}
                    </ul>
                  )}
                </>
              )}

              {importResult.warnings && importResult.warnings.length > 0 && (
                <div className="result-warnings">
                  <h5>⚠️  Warnings:</h5>
                  <ul>
                    {importResult.warnings.map((warning, idx) => (
                      <li key={idx} style={{ color: 'orange' }}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Flickr Import Component
const FlickrImport = () => {
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importedRestaurantId, setImportedRestaurantId] = useState(null);
  const [autoApprove, setAutoApprove] = useState(true);
  const [imageTypes, setImageTypes] = useState({}); // Maps photo_id to "food" or "restaurant"
  const [searchTerm, setSearchTerm] = useState(''); // For filtering albums by title
  const [showRestaurantOnly, setShowRestaurantOnly] = useState(false); // Filter for restaurant category prefixes
  const [locationData, setLocationData] = useState({
    address: '',
    unit: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'United States',
    phone: ''
  });

  // Restaurant category prefixes to filter by
  // These match the actual prefixes used in Flickr album titles
  const restaurantPrefixes = [
    'Restaurant',
    'Coffeehouse',
    'Cafe',
    'Bakery',
    'Food Truck'
  ];

  const fetchAlbums = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get('/import/flickr/albums');
      setAlbums(data.albums || []);
      setSuccess(`Loaded ${data.total} albums from Flickr`);
    } catch (err) {
      console.error('Error fetching albums:', err);
      setError(err.message || 'Failed to fetch Flickr albums. Check that Flickr API credentials are configured.');
    } finally {
      setLoading(false);
    }
  };

  const fetchPreview = async (photosetId) => {
    setLoadingPreview(true);
    setError(null);
    try {
      const data = await api.get(`/import/flickr/albums/${photosetId}/preview`);
      setPreviewData(data.preview);

      // Initialize image types
      const initialTypes = {};

      // Photos with titles default to "food"
      if (data.preview.food_items) {
        data.preview.food_items.forEach(item => {
          if (item.photo_id) {
            initialTypes[String(item.photo_id)] = 'food';
          }
        });
      }

      // Photos without titles default to "restaurant" (from backend)
      if (data.preview.restaurant_images) {
        data.preview.restaurant_images.forEach(item => {
          if (item.photo_id) {
            initialTypes[String(item.photo_id)] = 'restaurant';
          }
        });
      }

      setImageTypes(initialTypes);
    } catch (err) {
      console.error('Error fetching preview:', err);
      setError(err.message || 'Failed to fetch album preview');
      setPreviewData(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSelectAlbum = async (album) => {
    setSelectedAlbum(album);
    setPreviewData(null);
    await fetchPreview(album.id);
  };

  const closePreview = () => {
    setSelectedAlbum(null);
    setPreviewData(null);
    setImportedRestaurantId(null);
    setImageTypes({});
    setLocationData({
      address: '',
      unit: '',
      city: '',
      state: '',
      zip_code: '',
      country: 'United States',
      phone: ''
    });
  };

  const handleImport = async () => {
    if (!selectedAlbum || !previewData) return;

    setImporting(true);
    setError(null);

    try {
      // Split items into food vs restaurant images based on imageTypes
      const food_items = [];
      const restaurant_images = [];

      // Process food_items (photos with titles)
      if (previewData.food_items) {
        previewData.food_items.forEach(item => {
          const photoId = String(item.photo_id);
          const itemType = imageTypes[photoId] || 'food';
          if (itemType === 'food') {
            food_items.push(item);
          } else {
            restaurant_images.push({
              ...item,
              image_type: 'other'
            });
          }
        });
      }

      // Process restaurant_images (photos without titles)
      if (previewData.restaurant_images) {
        previewData.restaurant_images.forEach(item => {
          const photoId = String(item.photo_id);
          const itemType = imageTypes[photoId] || 'restaurant';
          if (itemType === 'food') {
            // User toggled this to Menu Item - add to food_items
            food_items.push(item);
          } else {
            // Keep as restaurant image
            restaurant_images.push({
              ...item,
              image_type: item.image_type || 'other'
            });
          }
        });
      }

      // Prepare import data
      const importData = {
        restaurant_name: previewData.restaurant_name,
        description: previewData.album_description || '',
        website_url: '',
        flickr_album_url: previewData.album_url,
        cuisines: previewData.cuisine ? [previewData.cuisine] : [],
        location: locationData.address && locationData.city ? locationData : null,
        food_items: food_items,
        restaurant_images: restaurant_images.length > 0 ? restaurant_images : null,
        skip_duplicates: true,
        auto_approve: autoApprove
      };

      const response = await api.post(`/import/flickr/albums/${selectedAlbum.id}/import`, importData);

      setImportedRestaurantId(response.restaurant_id);
      setSuccess(response.message || `Successfully imported ${response.foods_created} food items!`);
    } catch (err) {
      console.error('Error importing album:', err);
      setError(err.message || 'Failed to import album. Please try again.');
    } finally {
      setImporting(false);
    }
  };

  useEffect(() => {
    fetchAlbums();
  }, []);

  // Filter albums based on search term and restaurant filter
  const filteredAlbums = albums.filter(album => {
    const matchesSearch = searchTerm === '' ||
      album.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (album.description && album.description.toLowerCase().includes(searchTerm.toLowerCase()));

    // Check if album title starts with any of the restaurant category prefixes
    const matchesRestaurantFilter = !showRestaurantOnly ||
      restaurantPrefixes.some(prefix =>
        album.title.toLowerCase().startsWith(prefix.toLowerCase() + ':')
      );

    return matchesSearch && matchesRestaurantFilter;
  });

  return (
    <div className="section">
      <div className="section-header">
        <h2>Flickr Import</h2>
        <button
          className="btn btn-primary"
          onClick={fetchAlbums}
          disabled={loading}
        >
          <RefreshCw size={16} />
          {loading ? 'Loading...' : 'Refresh Albums'}
        </button>
      </div>

      {/* Filter Controls */}
      {!loading && albums.length > 0 && !selectedAlbum && (
        <div className="filter-controls" style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
          <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ flex: '1', minWidth: '250px' }}>
              <input
                type="text"
                className="form-control"
                placeholder="Search albums by title or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ width: '100%' }}
              />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <input
                type="checkbox"
                checked={showRestaurantOnly}
                onChange={(e) => setShowRestaurantOnly(e.target.checked)}
              />
              <span>Show only food venue albums</span>
            </label>
            <div style={{ color: '#6c757d', fontSize: '14px', whiteSpace: 'nowrap' }}>
              Showing {filteredAlbums.length} of {albums.length} albums
            </div>
          </div>
        </div>
      )}

      {error && <ErrorMessage message={error} onDismiss={() => setError(null)} />}
      {success && <SuccessMessage message={success} onDismiss={() => setSuccess(null)} />}

      {loading && <LoadingSpinner />}

      {!loading && albums.length === 0 && !error && (
        <div className="empty-state">
          <p>No Flickr albums found. Make sure your Flickr API credentials are configured in the backend.</p>
        </div>
      )}

      {!loading && albums.length > 0 && !selectedAlbum && filteredAlbums.length === 0 && (
        <div className="empty-state">
          <p>No albums match your search criteria. Try adjusting your filters.</p>
        </div>
      )}

      {!loading && filteredAlbums.length > 0 && !selectedAlbum && (
        <div className="albums-grid">
          {filteredAlbums.map((album) => (
            <div key={album.id} className="album-card">
              {album.primary_photo_url && (
                <div className="album-image">
                  <img src={album.primary_photo_url} alt={album.title} />
                </div>
              )}
              <div className="album-info">
                <h3>{album.title}</h3>
                {album.description && (
                  <p className="album-description">{album.description.substring(0, 100)}{album.description.length > 100 ? '...' : ''}</p>
                )}
                <div className="album-meta">
                  <span className="photo-count">{album.photo_count} photos</span>
                  {!album.is_public && <span className="private-badge">Private</span>}
                </div>
              </div>
              <div className="album-actions">
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => handleSelectAlbum(album)}
                >
                  <Eye size={14} />
                  Preview
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedAlbum && (
        <div className="preview-modal">
          <div className="preview-content">
            <div className="preview-header">
              <h2>Preview: {selectedAlbum.title}</h2>
              <button className="btn btn-outline" onClick={closePreview}>
                <X size={16} />
                Close
              </button>
            </div>

            {loadingPreview && <LoadingSpinner />}

            {!loadingPreview && previewData && (
              <div className="preview-body">
                <div className="preview-section">
                  <h3>Restaurant Information</h3>
                  <div className="form-group">
                    <label>Restaurant Name</label>
                    <input
                      type="text"
                      value={previewData.restaurant_name || ''}
                      readOnly
                      className="form-control"
                    />
                  </div>
                  <div className="form-group">
                    <label>Location Hint</label>
                    <input
                      type="text"
                      value={previewData.location_hint || 'Not detected'}
                      readOnly
                      className="form-control"
                    />
                  </div>
                  <div className="form-group">
                    <label>Detected Cuisine</label>
                    <input
                      type="text"
                      value={previewData.cuisine || 'Not detected'}
                      readOnly
                      className="form-control"
                    />
                  </div>
                  {previewData.album_description && (
                    <div className="form-group">
                      <label>Album Description</label>
                      <textarea
                        value={previewData.album_description}
                        readOnly
                        className="form-control"
                        rows="3"
                      />
                    </div>
                  )}
                  <div className="form-group">
                    <label>Album URL</label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input
                        type="text"
                        value={previewData.album_url || ''}
                        readOnly
                        className="form-control"
                      />
                      {previewData.album_url && (
                        <a
                          href={previewData.album_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-outline btn-sm"
                        >
                          <ExternalLink size={14} />
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                <div className="preview-section">
                  <h3>
                    Album Photos
                    ({(previewData.food_items?.length || 0) + (previewData.restaurant_images?.length || 0)})
                    {previewData.food_count !== undefined && (
                      <span style={{ fontSize: '14px', fontWeight: 'normal', marginLeft: '8px', color: '#666' }}>
                        ({previewData.food_count} with titles, {previewData.restaurant_image_count} without)
                      </span>
                    )}
                  </h3>
                  {(() => {
                    // Combine food_items and restaurant_images for display
                    const allItems = [
                      ...(previewData.food_items || []),
                      ...(previewData.restaurant_images || [])
                    ];
                    return allItems && allItems.length > 0 ? (
                      <div className="food-items-preview">
                        {allItems.slice(0, 10).map((food, index) => (
                        <div key={food.photo_id || index} className="food-item-preview">
                          <div className="food-item-info">
                            <strong>{food.name}</strong>
                            {food.description && <p>{food.description.substring(0, 100)}{food.description.length > 100 ? '...' : ''}</p>}
                            {food.tags && food.tags.length > 0 && (
                              <div className="tags-preview">
                                {food.tags.slice(0, 5).map((tag, i) => (
                                  <span key={i} className="tag-badge">{tag}</span>
                                ))}
                              </div>
                            )}
                            {!importedRestaurantId && food.photo_id && (
                              <div className="image-type-selector" style={{ marginTop: '8px' }}>
                                <label style={{ fontSize: '12px', marginRight: '4px' }}>Type:</label>
                                <select
                                  value={imageTypes[String(food.photo_id)] || 'food'}
                                  onChange={(e) => {
                                    const newValue = e.target.value;
                                    const photoId = String(food.photo_id); // Ensure string type
                                    // Use functional setState to avoid stale closure issues
                                    setImageTypes(prev => ({ ...prev, [photoId]: newValue }));
                                  }}
                                  className="form-control"
                                  style={{ width: 'auto', display: 'inline-block', fontSize: '12px', padding: '2px 6px' }}
                                >
                                  <option value="food">Menu Item</option>
                                  <option value="restaurant">Restaurant Image</option>
                                </select>
                                <span style={{ fontSize: '10px', marginLeft: '4px', color: '#666' }}>
                                  (ID: {food.photo_id})
                                </span>
                              </div>
                            )}
                          </div>
                          {food.image_url && (
                            <div className="food-item-thumb">
                              <img src={food.image_url} alt={food.name} />
                            </div>
                          )}
                        </div>
                      ))}
                      {allItems.length > 10 && (
                        <p className="more-items">... and {allItems.length - 10} more photos</p>
                      )}
                    </div>
                  ) : (
                    <p className="empty-state">No photos found in this album</p>
                  );
                  })()}
                </div>

                {!importedRestaurantId && (
                  <>
                    <div className="preview-section">
                      <h3>Import Settings</h3>
                      <div className="form-group">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={autoApprove}
                            onChange={(e) => setAutoApprove(e.target.checked)}
                          />
                          <span>Auto-approve imported items (Restaurant and Menu Items will be immediately published)</span>
                        </label>
                      </div>
                    </div>

                    <div className="preview-section">
                      <h3>Location Information (Optional)</h3>
                    <div className="form-group">
                      <label>Address</label>
                      <input
                        type="text"
                        value={locationData.address}
                        onChange={(e) => setLocationData({ ...locationData, address: e.target.value })}
                        className="form-control"
                        placeholder="123 Main St"
                      />
                    </div>
                    <div className="form-group">
                      <label>Unit/Suite/Apt</label>
                      <input
                        type="text"
                        value={locationData.unit || ''}
                        onChange={(e) => setLocationData({ ...locationData, unit: e.target.value })}
                        className="form-control"
                        placeholder="Suite 100"
                      />
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>City</label>
                        <input
                          type="text"
                          value={locationData.city}
                          onChange={(e) => setLocationData({ ...locationData, city: e.target.value })}
                          className="form-control"
                          placeholder="New York"
                        />
                      </div>
                      <div className="form-group">
                        <label>State</label>
                        <input
                          type="text"
                          value={locationData.state}
                          onChange={(e) => setLocationData({ ...locationData, state: e.target.value })}
                          className="form-control"
                          placeholder="NY"
                        />
                      </div>
                      <div className="form-group">
                        <label>ZIP Code</label>
                        <input
                          type="text"
                          value={locationData.zip_code}
                          onChange={(e) => setLocationData({ ...locationData, zip_code: e.target.value })}
                          className="form-control"
                          placeholder="10001"
                        />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Country</label>
                        <input
                          type="text"
                          value={locationData.country}
                          onChange={(e) => setLocationData({ ...locationData, country: e.target.value })}
                          className="form-control"
                          placeholder="United States"
                        />
                      </div>
                      <div className="form-group">
                        <label>Phone</label>
                        <input
                          type="text"
                          value={locationData.phone}
                          onChange={(e) => setLocationData({ ...locationData, phone: e.target.value })}
                          className="form-control"
                          placeholder="(555) 123-4567"
                        />
                      </div>
                    </div>
                  </div>
                  </>
                )}

                <div className="preview-footer">
                  {importedRestaurantId ? (
                    <>
                      <p className="success-text">
                        <CheckCircle size={20} />
                        <strong>Import Successful!</strong> The restaurant has been created.
                      </p>
                      <div className="button-group">
                        <button className="btn btn-primary" onClick={closePreview}>
                          Done
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="info-text">
                        {importing ? (
                          'Importing restaurant and food items...'
                        ) : (
                          'Review the information above and add location details (optional) before importing.'
                        )}
                      </p>
                      <div className="button-group">
                        <button className="btn btn-outline" onClick={closePreview} disabled={importing}>
                          Cancel
                        </button>
                        <button
                          className="btn btn-primary"
                          onClick={handleImport}
                          disabled={importing}
                        >
                          <Download size={16} />
                          {importing ? 'Importing...' : 'Import Restaurant'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Maintenance Component
const Maintenance = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({ flickr: false, geocoding: false });
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const fetchStatus = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await api.get('/maintenance/status');
      setStatus(data);
    } catch (err) {
      setError(err.message || 'Failed to fetch maintenance status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const convertFlickrUrls = async (dryRun = false) => {
    try {
      setProcessing({ ...processing, flickr: true });
      setError('');
      setResult(null);
      const data = await api.post('/maintenance/convert-flickr-urls', {
        dry_run: dryRun,
        menu_only: false,
        restaurants_only: false
      });
      setResult({ type: 'flickr', dryRun, ...data });
      if (!dryRun) {
        await fetchStatus(); // Refresh status after conversion
      }
    } catch (err) {
      setError(err.message || 'Failed to convert Flickr URLs');
    } finally {
      setProcessing({ ...processing, flickr: false });
    }
  };

  const geocodeLocations = async (dryRun = false) => {
    try {
      setProcessing({ ...processing, geocoding: true });
      setError('');
      setResult(null);
      const data = await api.post('/maintenance/geocode-locations', {
        dry_run: dryRun
      });
      setResult({ type: 'geocoding', dryRun, ...data });
      if (!dryRun) {
        await fetchStatus(); // Refresh status after geocoding
      }
    } catch (err) {
      setError(err.message || 'Failed to geocode locations');
    } finally {
      setProcessing({ ...processing, geocoding: false });
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading-spinner">Loading maintenance status...</div>
      </div>
    );
  }

  return (
    <div className="container">
      <h2 className="mb-4">
        <Settings size={24} className="me-2" />
        System Maintenance
      </h2>
      <p className="mb-4 text-muted">
        Administrative maintenance operations for database cleanup and optimization.
      </p>

      {error && (
        <div className="error-message mb-3">
          <strong>Error:</strong> {error}
        </div>
      )}

      {result && (
        <div className={`mb-3 ${result.success ? 'success-message' : 'error-message'}`}>
          <strong>{result.success ? 'Success:' : 'Failed:'}</strong> {result.message}
          {result.results && (
            <div className="mt-2" style={{ fontSize: '0.9em' }}>
              {result.type === 'flickr' && (
                <>
                  <div>Menu Item Images: {result.results.menu_items.converted} converted, {result.results.menu_items.failed} failed</div>
                  <div>Restaurant Images: {result.results.restaurant_images.converted} converted, {result.results.restaurant_images.failed} failed</div>
                  <div>Restaurant Albums: {result.results.restaurant_albums.converted} converted, {result.results.restaurant_albums.failed} failed</div>
                  <div className="mt-1"><strong>Total: {result.results.total_converted} converted, {result.results.total_failed} failed</strong></div>
                </>
              )}
              {result.type === 'geocoding' && (
                <>
                  <div>Total Locations: {result.results.total_locations}</div>
                  <div>Already Geocoded: {result.results.already_geocoded}</div>
                  <div>Successfully Geocoded: {result.results.success}</div>
                  <div>Failed: {result.results.failed}</div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      <div className="row">
        {/* Flickr URL Conversion Card */}
        <div className="col-md-6 mb-4">
          <div className="card">
            <div className="card-header">
              <h5>
                <Image size={18} className="me-2" />
                Flickr URL Conversion
              </h5>
            </div>
            <div className="card-body">
              <p className="text-muted mb-3" style={{ fontSize: '0.9em' }}>
                Convert Flickr page URLs to direct image URLs for better performance.
              </p>
              {status && (
                <>
                  <div className="stats-grid mb-3">
                    <div className="stat-item">
                      <div className="stat-label">Menu Images</div>
                      <div className="stat-value text-warning">{status.flickr_conversion.menu_item_images}</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-label">Restaurant Images</div>
                      <div className="stat-value text-warning">{status.flickr_conversion.restaurant_images}</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-label">Albums</div>
                      <div className="stat-value text-warning">{status.flickr_conversion.restaurant_albums}</div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <strong>Total URLs to convert: </strong>
                    <span className={status.flickr_conversion.total > 0 ? 'text-warning' : 'text-success'}>
                      {status.flickr_conversion.total}
                    </span>
                  </div>

                  <div className="d-flex gap-2 flex-wrap">
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => convertFlickrUrls(true)}
                      disabled={processing.flickr}
                    >
                      {processing.flickr ? (
                        <>
                          <RefreshCw size={14} className="spin" />
                          <span className="ms-1">Processing...</span>
                        </>
                      ) : (
                        <>
                          <Eye size={14} />
                          <span className="ms-1">Preview (Dry Run)</span>
                        </>
                      )}
                    </button>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => convertFlickrUrls(false)}
                      disabled={processing.flickr || status.flickr_conversion.total === 0}
                    >
                      {processing.flickr ? (
                        <>
                          <RefreshCw size={14} className="spin" />
                          <span className="ms-1">Converting...</span>
                        </>
                      ) : (
                        <>
                          <RefreshCw size={14} />
                          <span className="ms-1">Convert All URLs</span>
                        </>
                      )}
                    </button>
                  </div>

                  {status.flickr_conversion.total === 0 && (
                    <div className="mt-2 text-success">
                      <small>✓ All Flickr URLs already converted</small>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Location Geocoding Card */}
        <div className="col-md-6 mb-4">
          <div className="card">
            <div className="card-header">
              <h5>
                <MapPin size={18} className="me-2" />
                Location Geocoding
              </h5>
            </div>
            <div className="card-body">
              <p className="text-muted mb-3" style={{ fontSize: '0.9em' }}>
                Add latitude/longitude coordinates to restaurant locations for maps and distance features.
              </p>
              {status && (
                <>
                  <div className="stats-grid mb-3">
                    <div className="stat-item">
                      <div className="stat-label">Total Locations</div>
                      <div className="stat-value">{status.geocoding.total_locations}</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-label">Geocoded</div>
                      <div className="stat-value text-success">{status.geocoding.with_coordinates}</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-label">Missing</div>
                      <div className="stat-value text-warning">{status.geocoding.without_coordinates}</div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <small className="text-muted">
                      ⚠️ Geocoding respects rate limits (1 request/second). This may take time for many locations.
                    </small>
                  </div>

                  <div className="d-flex gap-2 flex-wrap">
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => geocodeLocations(true)}
                      disabled={processing.geocoding}
                    >
                      {processing.geocoding ? (
                        <>
                          <RefreshCw size={14} className="spin" />
                          <span className="ms-1">Processing...</span>
                        </>
                      ) : (
                        <>
                          <Eye size={14} />
                          <span className="ms-1">Preview (Dry Run)</span>
                        </>
                      )}
                    </button>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => geocodeLocations(false)}
                      disabled={processing.geocoding || status.geocoding.without_coordinates === 0}
                    >
                      {processing.geocoding ? (
                        <>
                          <RefreshCw size={14} className="spin" />
                          <span className="ms-1">Geocoding...</span>
                        </>
                      ) : (
                        <>
                          <MapPin size={14} />
                          <span className="ms-1">Geocode Locations</span>
                        </>
                      )}
                    </button>
                  </div>

                  {status.geocoding.without_coordinates === 0 && (
                    <div className="mt-2 text-success">
                      <small>✓ All locations have coordinates</small>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h5>About These Tools</h5>
        </div>
        <div className="card-body">
          <div className="mb-3">
            <strong>Flickr URL Conversion:</strong>
            <p className="mb-0 text-muted" style={{ fontSize: '0.9em' }}>
              Converts Flickr page URLs (flickr.com/photos/...) to direct image URLs (live.staticflickr.com/...)
              for faster loading and better reliability. This process is safe and reversible.
            </p>
          </div>
          <div>
            <strong>Location Geocoding:</strong>
            <p className="mb-0 text-muted" style={{ fontSize: '0.9em' }}>
              Uses the Nominatim geocoding service to look up latitude and longitude coordinates for restaurant
              addresses. These coordinates enable map displays and distance calculations. The service is free but
              rate-limited to 1 request per second.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Data Enrichment Component
const DataEnrichment = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enriching, setEnriching] = useState({ cuisines: false, foods: false });
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const fetchStatus = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await api.get('/admin/enrich/status');
      // Returned as well as stored: the enrich-all loop needs the fresh count
      // immediately, and React state updates are not synchronous.
      setStatus(data);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to fetch enrichment status');
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const enrichCuisines = async (limit = 5) => {
    try {
      setEnriching({ ...enriching, cuisines: true });
      setError('');
      setResult(null);
      const data = await api.post(`/admin/enrich/cuisines?limit=${limit}`);
      setResult({ type: 'cuisines', ...data });
      await fetchStatus(); // Refresh status after enrichment
    } catch (err) {
      setError(err.message || 'Failed to enrich cuisines');
    } finally {
      setEnriching({ ...enriching, cuisines: false });
    }
  };

  const enrichFoods = async (limit = 5, source = 'themealdb') => {
    try {
      setEnriching({ ...enriching, foods: true });
      setError('');
      setResult(null);
      const data = await api.post(`/admin/enrich/foods?limit=${limit}&source=${source}`);
      setResult({ type: 'foods', ...data });
      await fetchStatus(); // Refresh status after enrichment
    } catch (err) {
      setError(err.message || 'Failed to enrich foods');
    } finally {
      setEnriching({ ...enriching, foods: false });
    }
  };

  // Walk the whole pending cuisine queue in small server-side batches (#175).
  //
  // Deliberately a client-side loop rather than one big request: a cuisine
  // costs a Wikipedia fetch plus a model call (~20-30s), so asking the server
  // for all 40 in one go would run for ~20 minutes and die on a proxy timeout.
  // Five at a time keeps each request short, and the backend commits per row,
  // so progress survives even if a round fails.
  //
  // Stops when the queue empties, when a round makes no progress (a cuisine
  // that fails stays pending and would otherwise be retried forever — the
  // stall that bit the food enrichment), or on error.
  const enrichCuisinesAll = async () => {
    const ROUND = 5;
    // Accumulate in the API's own field names (items_processed / items_succeeded
    // / items_failed). The result panel reads those directly, so inventing
    // shorter local names here renders a blank panel titled "No matches:" —
    // a completed run reported as a failure.
    let totals = { items_processed: 0, items_succeeded: 0, items_failed: 0, details: [] };
    try {
      setEnriching({ ...enriching, cuisines: true });
      setError('');
      setResult(null);

      let remaining = status.cuisines_pending;
      let rounds = 0;
      while (remaining > 0) {
        const data = await api.post(`/admin/enrich/cuisines?limit=${ROUND}`);
        rounds += 1;
        totals = {
          items_processed: totals.items_processed + (data.items_processed || 0),
          items_succeeded: totals.items_succeeded + (data.items_succeeded || 0),
          items_failed: totals.items_failed + (data.items_failed || 0),
          details: [...totals.details, ...(data.details || [])],
        };
        // Show progress as it accrues rather than only at the end.
        setResult({
          ...totals,
          success: true,
          message: `Enriched ${totals.items_succeeded} cuisine(s) over ${rounds} round(s).`,
        });

        const fresh = await fetchStatus();
        const nowPending = fresh ? fresh.cuisines_pending : 0;
        if (nowPending >= remaining) {
          // No progress this round — every remaining cuisine is failing.
          setError(
            `Stopped after ${totals.items_succeeded} enriched: the last round made ` +
            `no progress, so the ${nowPending} still pending are failing rather ` +
            `than waiting. Check the details below.`
          );
          break;
        }
        remaining = nowPending;
      }
    } catch (err) {
      setError(err.message || 'Failed to enrich cuisines');
    } finally {
      await fetchStatus();
      setEnriching({ ...enriching, cuisines: false });
    }
  };

  // Enrich ALL pending foods in one pass. Repeatedly clicking the small
  // buttons stalls (un-matched foods stay pending and keep getting re-tried),
  // so the batch endpoint is the clean way to walk the whole catalog. It can
  // take up to a minute (one external call per pending food) — refresh status
  // in finally so the count updates even if the response is slow.
  const enrichFoodsBatch = async (source = 'themealdb') => {
    try {
      setEnriching({ ...enriching, foods: true });
      setError('');
      setResult(null);
      const data = await api.post(`/admin/enrich/foods/batch?source=${source}`);
      setResult({ type: 'foods', ...data });
    } catch (err) {
      setError(err.message || 'Failed to enrich foods');
    } finally {
      await fetchStatus();
      setEnriching({ ...enriching, foods: false });
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading-spinner">Loading enrichment status...</div>
      </div>
    );
  }

  return (
    <div className="container">
      <h2 className="mb-4">Data Enrichment</h2>
      <p className="mb-4 text-muted">
        Enrich cuisines and foods with contextual information from external APIs.
      </p>

      {error && (
        <div className="error-message mb-3">
          <strong>Error:</strong> {error}
        </div>
      )}

      {result && (
        <div className={`mb-3 ${result.items_succeeded > 0 || result.items_failed === 0 ? 'success-message' : 'error-message'}`}>
          <strong>{result.items_succeeded > 0 ? 'Done:' : 'No matches:'}</strong> {result.message}
          <div className="mt-2">
            <small>
              Processed: {result.items_processed} |
              Enriched: {result.items_succeeded} |
              No match: {result.items_failed}
            </small>
          </div>
          {result.items_failed > 0 && (
            <div className="mt-1">
              <small className="text-muted">
                "No match" = the source had no entry for that item (not an error). Those stay pending and can be retried with another source.
              </small>
            </div>
          )}
        </div>
      )}

      <div className="row">
        {/* Cuisines Card */}
        <div className="col-md-6 mb-4">
          <div className="card">
            <div className="card-header">
              <h5>Cuisines</h5>
            </div>
            <div className="card-body">
              {status && (
                <>
                  <div className="stats-grid mb-3">
                    <div className="stat-item">
                      <div className="stat-label">Total</div>
                      <div className="stat-value">{status.cuisines_total}</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-label">Enriched</div>
                      <div className="stat-value text-success">{status.cuisines_enriched}</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-label">Pending</div>
                      <div className="stat-value text-warning">{status.cuisines_pending}</div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <small className="text-muted">
                      Estimated API requests: ~{status.estimated_requests_cuisines}
                    </small>
                  </div>

                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => enrichCuisines(5)}
                      disabled={enriching.cuisines || status.cuisines_pending === 0}
                    >
                      {enriching.cuisines ? (
                        <>
                          <RefreshCw size={14} className="spin" />
                          <span className="ms-1">Enriching...</span>
                        </>
                      ) : (
                        <>
                          <RefreshCw size={14} />
                          <span className="ms-1">Enrich 5</span>
                        </>
                      )}
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => enrichCuisines(1)}
                      disabled={enriching.cuisines || status.cuisines_pending === 0}
                    >
                      Enrich 1
                    </button>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => enrichCuisinesAll()}
                      disabled={enriching.cuisines || status.cuisines_pending === 0}
                    >
                      Enrich ALL pending ({status.cuisines_pending})
                    </button>
                  </div>
                  <small className="text-muted d-block mt-1">
                    Cuisine text comes from the Wikipedia article, restructured into the
                    structured fields by Claude — needs ANTHROPIC_API_KEY set in the backend
                    environment. Each cuisine takes ~20-30 seconds (one article fetch plus one
                    model call), so "Enrich ALL" works through the queue five at a time and can
                    run for several minutes; progress is saved as it goes, so it is safe to
                    leave and come back.
                  </small>

                  {status.cuisines_pending === 0 && (
                    <div className="mt-2 text-success">
                      <small>✓ All cuisines enriched</small>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Foods Card */}
        <div className="col-md-6 mb-4">
          <div className="card">
            <div className="card-header">
              <h5>Foods</h5>
            </div>
            <div className="card-body">
              {status && (
                <>
                  <div className="stats-grid mb-3">
                    <div className="stat-item">
                      <div className="stat-label">Total</div>
                      <div className="stat-value">{status.foods_total}</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-label">Enriched</div>
                      <div className="stat-value text-success">{status.foods_enriched}</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-label">Pending</div>
                      <div className="stat-value text-warning">{status.foods_pending}</div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <small className="text-muted">
                      Estimated API requests: ~{status.estimated_requests_foods}
                      <br />
                      Source: {status.food_source}
                    </small>
                  </div>

                  <div className="mb-3">
                    {/* TheMealDB (free, no API key) is the default path for foods (#56). */}
                    <div className="d-flex gap-2 mb-2">
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => enrichFoods(10, 'themealdb')}
                        disabled={enriching.foods || status.foods_pending === 0}
                      >
                        {enriching.foods ? (
                          <>
                            <RefreshCw size={14} className="spin" />
                            <span className="ms-1">Enriching...</span>
                          </>
                        ) : (
                          <>
                            <RefreshCw size={14} />
                            <span className="ms-1">Enrich 10 (TheMealDB - Free)</span>
                          </>
                        )}
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => enrichFoods(5, 'themealdb')}
                        disabled={enriching.foods || status.foods_pending === 0}
                      >
                        Enrich 5
                      </button>
                    </div>

                    {/* One-click full pass over every pending food (free). */}
                    <div className="d-flex gap-2 mb-2">
                      <button
                        className="btn btn-success btn-sm"
                        onClick={() => enrichFoodsBatch('themealdb')}
                        disabled={enriching.foods || status.foods_pending === 0}
                      >
                        Enrich ALL pending ({status.foods_pending}) via TheMealDB
                      </button>
                    </div>

                    {/* Spoonacular is opt-in — richer data, but needs SPOONACULAR_API_KEY. */}
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => enrichFoods(5, 'spoonacular')}
                        disabled={enriching.foods || status.foods_pending === 0}
                      >
                        Enrich 5 (Spoonacular)
                      </button>
                    </div>
                    <small className="text-muted d-block mt-1">
                      TheMealDB is free (no key) but only covers common dishes (~a third of the catalog); un-matched foods stay pending, not an error. Spoonacular needs SPOONACULAR_API_KEY set in the backend environment. "Enrich ALL" may take up to a minute.
                    </small>
                  </div>

                  {status.foods_pending === 0 && (
                    <div className="mt-2 text-success">
                      <small>✓ All foods enriched</small>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h5>API Information</h5>
        </div>
        <div className="card-body">
          <div className="mb-2">
            <strong>Spoonacular:</strong> 150 requests/day free tier (~12 per cuisine, ~4 per food)
          </div>
          <div className="mb-2">
            <strong>TheMealDB:</strong> Unlimited free (alternative source for foods)
          </div>
          <div className="mt-3">
            <small className="text-muted">
              Enrichment adds contextual information like origin, ingredients, preparation methods,
              and dietary notes to improve the user experience.
            </small>
          </div>
        </div>
      </div>
    </div>
  );
};

const IntegrationSettings = () => {
  const [status, setStatus] = useState(null);  // { configured, masked_key, updated_at }
  const [loading, setLoading] = useState(true);
  const [rotating, setRotating] = useState(false);
  const [error, setError] = useState('');
  const [newKey, setNewKey] = useState(null);  // shown once after rotation
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const data = await api.get('/admin/config/api-key');
      setStatus(data);
    } catch (err) {
      setError('Failed to load API key status');
    } finally {
      setLoading(false);
    }
  };

  const handleRotate = async () => {
    if (!window.confirm('Generate a new API key? The current key will stop working immediately.')) return;
    try {
      setRotating(true);
      setNewKey(null);
      const data = await api.post('/admin/config/api-key/rotate', {});
      setNewKey(data.key);
      await fetchStatus();
    } catch (err) {
      setError('Failed to rotate API key');
    } finally {
      setRotating(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(newKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="section">
      <div className="section-header">
        <h2>Integrations</h2>
      </div>

      {error && <ErrorMessage message={error} onDismiss={() => setError('')} />}

      {loading ? <LoadingSpinner /> : (
        <div className="form-container">
          <h3>Machine-to-Machine API Key</h3>
          <p style={{ color: '#6b7280', marginBottom: '1.5rem', fontSize: '0.875rem' }}>
            Used by external services to authenticate with the dynerz API.
            Pass as <code style={{ background: '#f3f4f6', padding: '0.125rem 0.375rem', borderRadius: '0.25rem' }}>Authorization: Bearer &lt;key&gt;</code>.
          </p>

          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem', padding: '1rem', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '0.5rem' }}>
            <Key size={18} style={{ color: '#6b7280', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              {status?.configured ? (
                <>
                  <div style={{ fontFamily: 'monospace', letterSpacing: '0.05em', color: '#111827' }}>{status.masked_key}</div>
                  {status.updated_at && (
                    <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem' }}>
                      Last rotated: {new Date(status.updated_at).toLocaleString()}
                    </div>
                  )}
                </>
              ) : (
                <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>No key configured</span>
              )}
            </div>
            <button onClick={handleRotate} disabled={rotating} className="btn btn-primary" style={{ flexShrink: 0 }}>
              <RefreshCw size={15} />
              {rotating ? 'Generating…' : status?.configured ? 'Rotate Key' : 'Generate Key'}
            </button>
          </div>

          {newKey && (
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '0.5rem', padding: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                <strong style={{ color: '#166534', fontSize: '0.875rem' }}>New API Key — copy it now, it won't be shown again</strong>
                <button onClick={() => setNewKey(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
                  <X size={16} />
                </button>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <code style={{ flex: 1, background: 'white', border: '1px solid #bbf7d0', borderRadius: '0.375rem', padding: '0.5rem 0.75rem', fontFamily: 'monospace', fontSize: '0.875rem', wordBreak: 'break-all' }}>
                  {newKey}
                </code>
                <button onClick={handleCopy} className="btn btn-secondary" style={{ flexShrink: 0 }}>
                  <CheckCircle size={15} style={{ color: copied ? '#059669' : 'inherit' }} />
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Endpoint stats table (shared by the "slowest" and "most errors" lists)
const EndpointTable = ({ rows }) => {
  if (!rows || rows.length === 0) {
    return <div className="text-muted">No data yet.</div>;
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="table">
        <thead>
          <tr>
            <th>Method</th><th>Path</th><th>Requests</th>
            <th>Avg ms</th><th>Max ms</th><th>Errors</th><th>Error rate</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td><code>{r.method}</code></td>
              <td><code>{r.path}</code></td>
              <td>{(r.requests || 0).toLocaleString()}</td>
              <td>{Math.round(r.avg_ms || 0).toLocaleString()}</td>
              <td>{(r.max_ms || 0).toLocaleString()}</td>
              <td style={{ color: r.errors > 0 ? '#dc2626' : 'inherit' }}>{r.errors || 0}</td>
              <td>{((r.error_rate || 0) * 100).toFixed(1)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// System Health — privacy-preserving request & error telemetry (issue #79).
// Read-only view over /admin/telemetry/*; no personal data is displayed or stored.
const SystemHealth = () => {
  const WINDOWS = [
    { label: 'Last 24h', hours: 24 },
    { label: 'Last 7 days', hours: 168 },
    { label: 'Last 30 days', hours: 720 },
  ];
  const [hours, setHours] = useState(24);
  const [summary, setSummary] = useState(null);
  const [clientErrors, setClientErrors] = useState(null);
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = async (h) => {
    try {
      setLoading(true);
      setError('');
      const [s, e, ev] = await Promise.all([
        api.get(`/admin/telemetry/summary?hours=${h}`),
        api.get(`/admin/telemetry/client-errors?hours=${h}&limit=50`),
        api.get(`/admin/telemetry/events?hours=${h}`),
      ]);
      setSummary(s);
      setClientErrors(e);
      setEvents(ev);
    } catch (err) {
      setError(err.message || 'Failed to load telemetry');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(hours); }, [hours]);

  const pct = (frac) => `${((frac || 0) * 100).toFixed(2)}%`;
  const ms = (n) => `${Math.round(n || 0).toLocaleString()} ms`;
  const statusColor = (code) =>
    code >= 500 ? '#dc2626' : code >= 400 ? '#d97706' : code >= 300 ? '#2563eb' : '#16a34a';

  const cards = summary ? [
    { label: 'Requests', value: (summary.total_requests || 0).toLocaleString() },
    { label: 'Error rate (5xx)', value: pct(summary.error_rate), danger: (summary.error_rate || 0) > 0.01 },
    { label: 'Avg latency', value: ms(summary.avg_ms) },
    { label: 'Client errors', value: (clientErrors?.total || 0).toLocaleString() },
  ] : [];

  return (
    <div className="container">
      <h2 className="mb-4">
        <Activity size={24} className="me-2" />
        System Health
      </h2>
      <p className="mb-4 text-muted">
        Aggregate request &amp; error telemetry to keep the app stable (issue #79). No personal
        data — counts, status codes and latency only, with resource IDs and query strings stripped.
      </p>

      <div className="mb-4" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        {WINDOWS.map((w) => (
          <button
            key={w.hours}
            className={`btn ${hours === w.hours ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setHours(w.hours)}
          >
            {w.label}
          </button>
        ))}
        <button className="btn btn-outline" onClick={() => fetchData(hours)} title="Refresh">
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      {error && <div className="error-message mb-3"><strong>Error:</strong> {error}</div>}

      {loading ? (
        <div className="loading-spinner">Loading telemetry…</div>
      ) : summary && (
        <>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
            {cards.map((s) => (
              <div key={s.label} className="card" style={{ flex: '1 1 180px', padding: '1rem' }}>
                <div className="text-muted" style={{ fontSize: '0.85rem' }}>{s.label}</div>
                <div style={{ fontSize: '1.6rem', fontWeight: 700, color: s.danger ? '#dc2626' : 'inherit' }}>
                  {s.value}
                </div>
              </div>
            ))}
          </div>

          <div className="card mb-4" style={{ padding: '1rem' }}>
            <h5 className="mb-3">Product signals — guest bet (issue #13)</h5>
            {(() => {
              const counts = events?.counts || {};
              const created = counts.guest_content_created || 0;
              const converted = counts.guest_conversion || 0;
              const rate = events?.guest_conversion_rate;
              if (created === 0 && converted === 0) {
                return <div className="text-muted">No guest activity recorded in this window yet.</div>;
              }
              const tiles = [
                { label: 'Guests who saved content', value: created.toLocaleString() },
                { label: 'Guests who converted (signed in)', value: converted.toLocaleString() },
                { label: 'Conversion rate', value: rate == null ? '—' : `${(rate * 100).toFixed(1)}%` },
              ];
              return (
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  {tiles.map((t) => (
                    <div key={t.label} style={{ flex: '1 1 180px' }}>
                      <div className="text-muted" style={{ fontSize: '0.85rem' }}>{t.label}</div>
                      <div style={{ fontSize: '1.4rem', fontWeight: 700 }}>{t.value}</div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>

          <div className="card mb-4" style={{ padding: '1rem' }}>
            <h5 className="mb-3">Status codes</h5>
            {summary.status_breakdown.length === 0 ? (
              <div className="text-muted">No requests recorded in this window yet.</div>
            ) : (
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {summary.status_breakdown.map((s) => (
                  <span key={s.status_code} style={{
                    padding: '0.25rem 0.6rem', borderRadius: '999px', color: '#fff',
                    background: statusColor(s.status_code), fontSize: '0.85rem', fontWeight: 600,
                  }}>
                    {s.status_code}: {s.count.toLocaleString()}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="card mb-4" style={{ padding: '1rem' }}>
            <h5 className="mb-3">Slowest endpoints (by avg latency)</h5>
            <EndpointTable rows={summary.slowest_endpoints} />
          </div>

          <div className="card mb-4" style={{ padding: '1rem' }}>
            <h5 className="mb-3">Endpoints with the most 5xx errors</h5>
            {summary.most_errors.length === 0 ? (
              <div className="text-muted">No server errors in this window. 🎉</div>
            ) : (
              <EndpointTable rows={summary.most_errors} />
            )}
          </div>

          <div className="card mb-4" style={{ padding: '1rem' }}>
            <h5 className="mb-3">Recent client errors (scrubbed)</h5>
            {(!clientErrors || clientErrors.errors.length === 0) ? (
              <div className="text-muted">No client errors reported in this window.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Time</th><th>Source</th><th>Type</th><th>Network</th><th>Message</th><th>Path</th><th>App</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientErrors.errors.map((e, i) => (
                      <tr key={i}>
                        <td style={{ whiteSpace: 'nowrap' }}>{e.created_at ? new Date(e.created_at).toLocaleString() : '—'}</td>
                        <td>{e.source}</td>
                        <td>{e.name || '—'}</td>
                        {/* #364: queued network failures — class, network, repeat count and the
                            client-side window; Time is when it was finally delivered. */}
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {e.failure_class ? (
                            <>
                              <code>{e.failure_class}</code>
                              {e.network_type ? ` · ${e.network_type}` : ''}
                              {e.occurrences > 1 ? ` · ×${e.occurrences}` : ''}
                              {e.occurred_at && (
                                <div className="text-muted" style={{ fontSize: '0.8em' }}>
                                  {new Date(e.occurred_at).toLocaleTimeString()}
                                  {e.last_occurred_at && e.last_occurred_at !== e.occurred_at
                                    ? `–${new Date(e.last_occurred_at).toLocaleTimeString()}` : ''}
                                </div>
                              )}
                            </>
                          ) : '—'}
                        </td>
                        <td style={{ maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.message || '—'}</td>
                        <td><code>{e.url_path || '—'}</code></td>
                        <td>{e.app_version || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// Main App Component
const RestaurantAdminApp = () => {
  const [activeTab, setActiveTab] = useState('restaurants');
  const [isAuthenticated, setIsAuthenticated] = useState(auth.isAuthenticated());
  const [currentUser, setCurrentUser] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [openDropdown, setOpenDropdown] = useState(null);
  // Set only when logout is triggered by a 401 (expired/invalid token), so
  // the login screen can explain why instead of silently bouncing the user.
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState(null);
  const dropdownRef = useRef(null);

  // Handle logout - defined early so it can be used in useEffect
  const handleLogout = useCallback(() => {
    auth.removeToken();
    setIsAuthenticated(false);
    setCurrentUser(null);
    setActiveTab('restaurants');
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Setup JWT expiration handler
  useEffect(() => {
    // Set callback for handling expired tokens
    api.onUnauthorized = () => {
      setSessionExpiredMessage('Your session has expired. Please sign in again.');
      handleLogout();
    };

    // Cleanup on unmount
    return () => {
      api.onUnauthorized = null;
    };
  }, [handleLogout]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchCurrentUser();
      fetchPendingCount();
      // Refresh pending count every 30 seconds
      const interval = setInterval(fetchPendingCount, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const fetchCurrentUser = async () => {
    try {
      const token = auth.getToken();
      console.log('Fetching current user with token:', token ? token.substring(0, 20) + '...' : 'NO TOKEN');

      if (!token) {
        console.error('No auth token found in localStorage');
        handleLogout();
        return;
      }

      const user = await api.get('/users/me');
      console.log('API returned current user:', user);
      setCurrentUser(user);
    } catch (err) {
      console.error('Failed to fetch current user:', err);
      console.error('Error details:', err.message);
      // Don't logout on /users/me failure - might be temporary
      // handleLogout();
    }
  };

  const fetchPendingCount = async () => {
    try {
      // #137: count only the moderation queue (review_state='pending'), excluding
      // private "just for me" adds and rejected ones (both also is_approved=0).
      const pendingRestaurants = await api.get('/restaurants/?only_pending=true');

      const pendingMenuItems = await api.get('/foods/menu/items/?only_pending=true');

      // Photo contributions (#94) share the Pending Approvals tab, so the nav
      // badge must count them too — otherwise a queue of only photos looks empty
      // from the nav (#106).
      let pendingContributions = [];
      try {
        pendingContributions = await api.get('/contributions/pending');
      } catch (err) {
        // Non-fatal: keep the approvals count even if contributions fail to load
        console.error('Failed to fetch pending contributions for badge:', err);
      }

      // Category suggestions (#127b) also share the Pending Approvals tab, so the
      // nav badge counts them too (same reason as photo contributions, #106).
      let pendingSuggestions = [];
      try {
        pendingSuggestions = await api.get('/category-suggestions/pending');
      } catch (err) {
        console.error('Failed to fetch pending category suggestions for badge:', err);
      }

      setPendingCount(pendingRestaurants.length + pendingMenuItems.length + pendingContributions.length + pendingSuggestions.length);
    } catch (err) {
      console.error('Failed to fetch pending count:', err);
    }
  };

  const handleLogin = () => {
    setSessionExpiredMessage(null);
    setIsAuthenticated(true);
  };

  // Show login form if not authenticated
  if (!isAuthenticated) {
    return (
      <Login
        onLogin={handleLogin}
        sessionExpiredMessage={sessionExpiredMessage}
        onDismissSessionExpired={() => setSessionExpiredMessage(null)}
      />
    );
  }

  const navItems = [
    { id: 'moderation', label: 'Moderation', icon: ShieldAlert },
    {
      id: 'restaurants-group',
      label: 'Restaurants',
      icon: MapPin,
      children: [
        { id: 'restaurants', label: 'Restaurants', icon: MapPin },
        { id: 'restaurant-images', label: 'Restaurant Images', icon: Image },
        { id: 'restaurant-categories', label: 'Restaurant Categories', icon: Users },
      ]
    },
    {
      id: 'foods-group',
      label: 'Foods',
      icon: DollarSign,
      children: [
        { id: 'foods', label: 'Foods', icon: DollarSign },
        { id: 'cuisines', label: 'Cuisines', icon: Users },
        { id: 'food-categories', label: 'Food Categories', icon: Users },
        { id: 'menu-items', label: 'Menu Items', icon: DollarSign },
        { id: 'sizes', label: 'Sizes', icon: Settings },
      ]
    },
    { id: 'users', label: 'Users', icon: UserCheck },
    { id: 'csv-import', label: 'CSV Import', icon: FileUp },
    { id: 'flickr-import', label: 'Flickr Import', icon: Download },
    { id: 'data-enrichment', label: 'Data Enrichment', icon: RefreshCw },
    { id: 'integrations', label: 'Integrations', icon: Key },
    { id: 'maintenance', label: 'Maintenance', icon: Settings },
    { id: 'system-health', label: 'System Health', icon: Activity }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'moderation':
        return <ModerationHub />;
      case 'restaurants':
        return <Restaurants />;
      case 'restaurant-categories':
        return <RestaurantCategories />;
      case 'cuisines':
        return <Cuisines />;
      case 'foods':
        return <Foods />;
      case 'food-categories':
        return <FoodCategories />;
      case 'menu-items':
        return <MenuItems />;
      case 'sizes':
        return <SizeManager api={api} />;
      case 'restaurant-images':
        return <RestaurantImages />;
      case 'users':
        return <UsersManagement currentUser={currentUser} />;
      case 'csv-import':
        return <CSVImport />;
      case 'flickr-import':
        return <FlickrImport />;
      case 'data-enrichment':
        return <DataEnrichment />;
      case 'integrations':
        return <IntegrationSettings />;
      case 'maintenance':
        return <Maintenance />;
      case 'system-health':
        return <SystemHealth />;
      default:
        return <Restaurants />;
    }
  };

  return (
    <div className="app">
      <div className="header">
        <div className="header-content">
          <div className="header-title">
            <h1>Restaurant Admin</h1>
            <div className="header-subtitle">
              dyner.z Management System
            </div>
          </div>
          <div className="header-actions">
            {currentUser && (
              <div className="user-info">
                <span className="welcome-text">
                  Welcome, {currentUser.first_name || currentUser.username || currentUser.email}
                </span>
                {currentUser.is_superuser && (
                  <span className="superuser-badge">Admin</span>
                )}
              </div>
            )}
            <button
              onClick={handleLogout}
              className="btn btn-outline logout-btn"
            >
              <LogOut size={16} />
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="main-content">
        <div className="tab-navigation" ref={dropdownRef}>
          {navItems.map((item) => {
            const Icon = item.icon;
            const showBadge = item.id === 'moderation' && pendingCount > 0;

            if (item.children) {
              const isGroupActive = item.children.some(c => c.id === activeTab);
              const isOpen = openDropdown === item.id;
              return (
                <div key={item.id} className="tab-dropdown-wrapper">
                  <button
                    className={`tab tab-dropdown-trigger ${isGroupActive ? 'tab-active' : ''}`}
                    onClick={() => setOpenDropdown(isOpen ? null : item.id)}
                  >
                    <Icon size={16} />
                    {item.label}
                    <ChevronDown size={14} className={`dropdown-chevron ${isOpen ? 'dropdown-chevron-open' : ''}`} />
                  </button>
                  {isOpen && (
                    <div className="tab-dropdown-menu">
                      {item.children.map((child) => {
                        const ChildIcon = child.icon;
                        return (
                          <button
                            key={child.id}
                            className={`tab-dropdown-item ${activeTab === child.id ? 'tab-dropdown-item-active' : ''}`}
                            onClick={() => { setActiveTab(child.id); setOpenDropdown(null); }}
                          >
                            <ChildIcon size={14} />
                            {child.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <button
                key={item.id}
                onClick={() => { setActiveTab(item.id); setOpenDropdown(null); }}
                className={`tab ${activeTab === item.id ? 'tab-active' : ''}`}
              >
                <Icon size={16} />
                {item.label}
                {showBadge && (
                  <span className="pending-badge">{pendingCount}</span>
                )}
              </button>
            );
          })}
        </div>

        {renderTabContent()}
      </div>
    </div>
  );
};

export default RestaurantAdminApp;
